"use client";

import {
  Fragment,
  use,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { GoogleSignIn } from "./google-sign-in";
import { HomePage } from "./home-page";
import {
  buildShareableHref,
  parseShareableLocation,
  type ShareableLearningSection,
  type ShareableLocation,
} from "./shareable-navigation";
import {
  readStoredDevicePreferences,
  type ReadingMeasure,
  type ReadingSize,
} from "./device-preferences";
import {
  FREE_ENTITLEMENT,
  type EntitlementSnapshot,
} from "./entitlement-contract";
import { buildLexicalQuickFacts } from "./lexical-quick-facts";
import { meaningSwipeStep, moveMeaningIndex } from "./meaning-navigation";
import { VerbaloreMark } from "./verbalore-mark";
import { PronunciationGuide } from "./pronunciation-guide";
import { WordDiscovery } from "./word-discovery";
import {
  FREE_COMMAND_VOCABULARY,
  INITIAL_COMMAND_VOCABULARY_INDEX,
  loadCommandVocabularyEntry,
  loadCommandVocabularyIndex,
  type CommandVocabularyCatalogEntry,
} from "./command-vocabulary-catalog";
import {
  ALL_EXPRESSION_STUDIES,
  BOUNDARY_STUDY,
  EXPRESSION_STUDY_BY_ID,
  type ExpressionStudy,
} from "../content/expression-studies";
import {
  AVAILABLE_READING_FORMS,
  READINGS,
  READING_ARTICULATIONS,
  READING_BY_ID,
  READING_APPETITES,
  READING_COMMITMENTS,
  READING_DIFFICULTIES,
  READING_FORM_LABELS,
  READING_INTENTS,
  INITIAL_LESSON_LEXICON,
  computeReadingMetrics,
  findDictionaryEntry,
  findWordInsight,
  loadLessonLexicon,
  readingTokenIds,
  recommendReadings,
} from "../content/catalog";
import {
  type CommandVocabularyArticulation,
  type CommandVocabularyEntry,
  type CommandVocabularyHandle,
  type CommandVocabularyRegister,
} from "../content/command-vocabulary";
import type {
  GlanceContextPack,
  GlanceContextTreatment,
  LessonLexicon,
  LessonLexiconEntry,
  Reading,
  ReadingAppetite,
  ReadingArticulation,
  ReadingCommitment,
  ReadingForm,
  ReadingIntent,
  ReadingLexicalDifficulty,
  ReadingSegment as StorySegment,
  WordInsight,
} from "../content/types";

type Language = "English" | "Hindi" | "Punjabi";
type PanelMode = "empty" | "word" | "library" | "profile";
type ExperienceMode = "read" | "practice" | "learning";
type PracticeView = "overview" | "discover" | "words" | "expression";
type LearningSection = ShareableLearningSection;
type AccountAction = "idle" | "exporting" | "deleting";

const ACCOUNT_DELETE_CONFIRMATION = "delete-my-verbalore-data-v1";
type SelectionState = {
  word: string;
  key: string;
  tokenId: string;
  paragraphId: string;
  sentence: string;
  curated: boolean;
};

type ReadingViewportAnchor = {
  readingId: string;
  paragraphId: string;
  paragraphTop: number;
};

type Profile = {
  id: string;
  displayName: string;
  preferredLanguage: Language;
  explorationDepth: string;
  assistanceMode: string;
};

type VocabularyItem = {
  id?: number;
  profileId?: string;
  word: string;
  displayWord: string;
  status: string;
  meaning: string;
  sourceSentence: string;
  sourceAnchor: string;
  lastOpenedSection?: string;
  createdAt?: string;
  updatedAt?: string;
  anchor?: string;
  contextCount?: number;
};

type VocabularyContext = {
  id?: number;
  profileId?: string;
  word: string;
  displayWord: string;
  meaning: string;
  sourceSentence: string;
  sourceAnchor: string;
  sourceType: "authored" | "private_import";
  sourceId: string;
  createdAt?: string;
  updatedAt?: string;
};

type ReadingProgress = {
  lessonId: string;
  paragraphId: string;
  percentage: number;
  selectedWord?: string | null;
  selectedAnchor?: string | null;
  updatedAt?: string;
};

type PracticeAttempt = {
  id: number;
  promptId: string;
  draft: string;
  revision: string | null;
  assistanceMode: string;
  signals: string;
  status: string;
  updatedAt: string;
};

type PracticeSignal = {
  id: string;
  label: string;
  met: boolean;
  guidance: string;
};

type PracticeAssessment = {
  status: "empty" | "not-assessed" | "assessed";
  message: string;
  signals: PracticeSignal[];
};

type ExpressionWork = {
  draft: string;
  revision: string;
  checked: boolean;
  stanceId: string;
};

type ReviewEvidence = {
  status: "Encountered" | "Active" | "Owned";
  successfulChecks: number;
  useAttempts: number;
  naturalUses: number;
  notYet: number;
  spacedRecall: boolean;
  differentForms: boolean;
  nextStep: "check" | "use" | "natural" | "complete";
  nextReviewAt: string | null;
  due: boolean;
  canBecomeActive: boolean;
  canBecomeOwned: boolean;
  state: "ready" | "wait" | "complete" | "content_gap" | "read_only";
  promptExhausted: boolean;
  legacyEvents: number;
  firstCheckAt: string | null;
  spacedCheckAt: string | null;
  spacingHours: number | null;
  receiptSummary: string;
  latestReceipt: null | {
    receiptId: number;
    occurredAt: string;
    promptId: string;
    assessmentForm: string;
    outcome: string;
    effect: string;
    policyVersion: string;
    claim: string;
  };
};

type WordReview = {
  item: VocabularyItem & { createdAt: string; updatedAt: string };
  authored: boolean;
  prompt: null | {
    id: string;
    type: "check" | "use" | "natural";
    assessmentForm: "context_cloze" | "sense_discrimination" | "self_attested_production" | "self_reported_transfer";
    senseId: string;
    stem: string | null;
    options: string[];
    direction: string | null;
    criteria: Array<{ id: string; label: string }>;
    cue: string | null;
  };
  evidence: ReviewEvidence;
};

type LearningEvidenceWeek = {
  weekStart: string;
  firstChecks: number;
  delayedChecks: number;
  originalUses: number;
  naturalUses: number;
  notYet: number;
  demotions: number;
};

type LearningEvidenceEvent = {
  word: string;
  evidenceEffect: string;
  outcome: string;
  policyVersion: string;
  occurredAt: string;
  claim: string;
};

type LearningEvidenceSummary = {
  window: { start: string; endExclusive: string; weeks: number };
  states: { encountered: number; active: number; owned: number };
  totals: Omit<LearningEvidenceWeek, "weekStart">;
  weeks: LearningEvidenceWeek[];
  recent: LearningEvidenceEvent[];
};

type SessionUser = {
  id: string;
  subject: string;
  email: string;
  displayName: string;
};

type PrivateReadingSummary = {
  id: string;
  profileId: string;
  title: string;
  privacyAcceptedAt: string;
  privacyNoticeVersion: string;
  createdAt: string;
  updatedAt: string;
  preview: string;
  wordCount: number;
};

type PrivateReadingRecord = PrivateReadingSummary & {
  sourceText: string;
};

type ReadingListEntry = {
  profileId: string;
  readingId: string;
  addedAt: string;
  updatedAt: string;
};

type LibraryView = "home" | "words" | "reading-list" | "private";

function calmLearningDate(value?: string) {
  if (!value) return "This session";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date preserved";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function contextSourceName(context: VocabularyContext, privateReadings: PrivateReadingSummary[]) {
  if (context.sourceType === "private_import") {
    return privateReadings.find((reading) => reading.id === context.sourceId)?.title || "Private passage";
  }
  if (context.sourceId.startsWith("command-vocabulary:")) return "Words Worth Having";
  return READING_BY_ID.get(context.sourceId)?.title || "Verbalore reading";
}

function evidenceReceipt(review?: WordReview) {
  if (!review) return "Saved in context. No learning assessment has been made.";
  return review.evidence.receiptSummary;
}

const DEFAULT_PROFILE: Profile = {
  id: "guest",
  displayName: "Reader",
  preferredLanguage: "English",
  explorationDepth: "Glance first",
  assistanceMode: "Build with me",
};

const WORD_PATTERN = /^\p{L}+(?:[’'-]\p{L}+)*$/u;
function pronunciationLabel(voice: SpeechSynthesisVoice) {
  const accent = new Intl.DisplayNames(["en"], { type: "region" });
  const region = voice.lang.split("-")[1]?.toUpperCase();
  const regionName = region ? accent.of(region) : undefined;
  return `${regionName || voice.lang} · ${voice.name}${voice.localService ? " · device" : ""}`;
}

function normalizeWord(value: string) {
  return value.toLocaleLowerCase().replace(/^[^\p{L}]+|[^\p{L}]+$/gu, "");
}

const MODAL_FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function useModalFocus(open: boolean, requestClose: () => void) {
  const dialogRef = useRef<HTMLElement>(null);
  const requestCloseRef = useRef(requestClose);

  useEffect(() => {
    requestCloseRef.current = requestClose;
  }, [requestClose]);

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const returnTarget = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const focusable = () => Array.from(
      dialog.querySelectorAll<HTMLElement>(MODAL_FOCUSABLE_SELECTOR),
    ).filter((element) => element.getClientRects().length > 0);
    const focusFrame = window.requestAnimationFrame(() => {
      (focusable()[0] || dialog).focus({ preventScroll: true });
    });
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        requestCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const elements = focusable();
      if (!elements.length) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
        return;
      }
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      if (returnTarget?.isConnected) {
        window.requestAnimationFrame(() => returnTarget.focus({ preventScroll: true }));
      }
    };
  }, [open]);

  return dialogRef;
}

function displayLexicalText(value: string) {
  return value.replace(/\s+—\s+/g, "; ").replaceAll("—", ", ");
}

function sentenceContainingOffset(text: string, offset: number) {
  let start = 0;
  for (let index = 0; index < text.length; index += 1) {
    if (!/[.!?]/u.test(text[index])) continue;
    let end = index + 1;
    while (end < text.length && /[”"'’)]/u.test(text[end])) end += 1;
    if (end < text.length && !/\s/u.test(text[end])) continue;
    if (offset < end) return text.slice(start, end).trim();
    while (end < text.length && /\s/u.test(text[end])) end += 1;
    start = end;
    index = end - 1;
  }
  return text.slice(start).trim();
}

function completeLexicalSentence(value: string) {
  const cleaned = displayLexicalText(value).trim().replace(/[.;:,]+$/u, "");
  if (!cleaned) return "";
  const sentence = `${cleaned[0].toLocaleUpperCase()}${cleaned.slice(1)}`;
  return `${sentence}.`;
}

function comparableMeaning(value: string) {
  return displayLexicalText(value)
    .toLocaleLowerCase()
    .normalize("NFKC")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

function vocabularyStatusRank(status: string) {
  return status === "Owned" ? 2 : status === "Active" ? 1 : 0;
}

function reviewStepName(step: ReviewEvidence["nextStep"]) {
  if (step === "check") return "Check it";
  if (step === "use") return "Use it";
  if (step === "natural") return "Notice it in life";
  return "Evidence complete";
}

function calmReviewDate(value: string | null) {
  if (!value) return "when new evidence is available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "when you are ready";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function reviewAvailabilityText(review: WordReview) {
  if (review.evidence.state === "ready") return `Ready to ${reviewStepName(review.evidence.nextStep).toLocaleLowerCase()}.`;
  if (review.evidence.state === "wait") return `Next different check ${calmReviewDate(review.evidence.nextReviewAt)}.`;
  if (review.evidence.state === "complete") return "Current evidence cycle complete.";
  if (review.evidence.state === "read_only") return "Receipts visible; new evidence temporarily read-only.";
  return "More authored evidence is required; no prompt will be repeated for a score.";
}

function learningEvidenceLabel(effect: string) {
  if (effect === "first_check") return "Authored check";
  if (effect === "spaced_distinct_check") return "Delayed distinct check";
  if (effect === "original_use_self_attested") return "Original use recorded";
  if (effect === "natural_use_self_reported") return "Natural use self-reported";
  if (effect === "demotion") return "Confidence corrected";
  return "Not yet";
}

function calmWeekDate(value: string) {
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "Week";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
}

const DISCOVERY_LANDING_PHRASES: Record<ReadingArticulation, string> = {
  direct: "directly",
  tactful: "tactfully",
  "warm-authority": "with warm authority",
  restrained: "with restraint",
  punchy: "with punch",
};

const DISCOVERY_COMMITMENT_PHRASES: Record<ReadingCommitment, string> = {
  brief: "a few minutes",
  sitting: "a quiet sitting",
  immersion: "settling in",
};

const DISCOVERY_DIFFICULTY_PHRASES: Record<ReadingLexicalDifficulty, string> = {
  approachable: "comfortable vocabulary",
  advanced: "vocabulary that stretches you",
  "advanced-plus": "demanding vocabulary",
};

const DISCOVERY_FORM_PHRASES: Record<ReadingForm, string> = {
  story: "as a story",
  "dramatic-scene": "as a dramatic scene",
  dialogue: "as dialogue",
  essay: "as an essay",
  letter: "as letters",
  speech: "as a speech",
  "fable-parable": "as a fable or parable",
  document: "as a document",
};

function lowerFirst(value: string) {
  return value ? `${value[0].toLocaleLowerCase()}${value.slice(1)}` : value;
}

function composeDiscoveryAsk({
  commitment,
  appetite,
  articulation,
  intent,
  difficulty,
  form,
}: {
  commitment: ReadingCommitment;
  appetite: ReadingAppetite;
  articulation: ReadingArticulation | null;
  intent: ReadingIntent | null;
  difficulty: ReadingLexicalDifficulty | null;
  form: ReadingForm | null;
}) {
  const appetiteLabel = READING_APPETITES.find(({ id }) => id === appetite)?.label || "Surprise me";
  const intentLabel = READING_INTENTS.find(({ id }) => id === intent)?.label;
  const request = [
    lowerFirst(appetiteLabel),
    articulation ? `that lands ${DISCOVERY_LANDING_PHRASES[articulation]}` : "",
    intentLabel ? `and helps you ${lowerFirst(intentLabel)}` : "",
  ].filter(Boolean).join(" ");
  const refinements = [
    difficulty ? `with ${DISCOVERY_DIFFICULTY_PHRASES[difficulty]}` : null,
    form ? DISCOVERY_FORM_PHRASES[form] : null,
  ].filter(Boolean);
  return `For ${DISCOVERY_COMMITMENT_PHRASES[commitment]}: ${request}${refinements.length ? `, ${refinements.join(", ")}` : ""}.`;
}

function handleChoiceArrow(
  event: ReactKeyboardEvent<HTMLButtonElement>,
  onSelect: (value: string) => void,
) {
  if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
  const group = event.currentTarget.closest<HTMLElement>("[role='radiogroup']");
  const choices = group
    ? Array.from(group.querySelectorAll<HTMLButtonElement>("[role='radio'][data-choice-value]"))
    : [];
  if (!choices.length) return;
  const currentIndex = Math.max(0, choices.indexOf(event.currentTarget));
  const nextIndex = event.key === "Home"
    ? 0
    : event.key === "End"
      ? choices.length - 1
      : event.key === "ArrowLeft" || event.key === "ArrowUp"
        ? (currentIndex - 1 + choices.length) % choices.length
        : (currentIndex + 1) % choices.length;
  const next = choices[nextIndex];
  const value = next.dataset.choiceValue;
  if (!value) return;
  event.preventDefault();
  onSelect(value);
  next.focus({ preventScroll: true });
}

function LearningEvidenceHistory({ events }: { events: LearningEvidenceEvent[] }) {
  if (!events.length) return null;
  return (
    <details className="learning-evidence-history">
      <summary>Recent evidence history <span>{events.length}</span></summary>
      <ol>
        {events.map((event, index) => (
          <li key={`${event.word}-${event.occurredAt}-${index}`}>
            <span><strong>{event.word}</strong><small>{calmLearningDate(event.occurredAt)}</small></span>
            <p>{learningEvidenceLabel(event.evidenceEffect)}. {event.claim}</p>
          </li>
        ))}
      </ol>
    </details>
  );
}

function translationPresentation(insight: WordInsight, language: Language) {
  const value = insight.translations[language]?.trim() || "";
  const status = insight.translationStatus?.[language]
    || (language === "English"
      ? "editorially-verified"
      : "model-assisted-unverified");
  if (language === "English") {
    return {
      label: "Meaning in English",
      value: value || insight.alternateMeaning || insight.meaning,
      note: "",
    };
  }
  if (status === "withheld" || !value) {
    return {
      label: `In ${language} · withheld`,
      value: "Not published until independently reviewed.",
      note: "The English explanation remains available without inventing a translation.",
    };
  }
  if (status === "human-verified") {
    return {
      label: `In ${language} · human verified`,
      value,
      note: "Reviewed by a qualified speaker.",
    };
  }
  return {
    label: `In ${language} · unverified translation`,
    value,
    note: "Use as a reading aid. Independent speaker verification is pending.",
  };
}

function pronunciationEvidenceText(insight: WordInsight) {
  const evidence = insight.pronunciationEvidence;
  if (!evidence) return "";
  if (evidence.status === "human-verified") {
    return `Human-verified pronunciation evidence. ${evidence.source}`;
  }
  if (evidence.status === "source-exact") {
    return `The displayed form has exact source evidence. ${evidence.source}`;
  }
  if (evidence.status === "source-exact-variety-unspecified") {
    return `The displayed form has source evidence, but the variety is unspecified. ${evidence.source}`;
  }
  if (evidence.status === "open-related-form-only") {
    return `Only a related form has source evidence. Exact-form review remains open. ${evidence.source}`;
  }
  return `No source pronunciation value is available yet. ${evidence.source}`;
}

function fallbackInsight(selection: SelectionState): WordInsight {
  return {
    word: selection.word,
    phonetic: "Selected from the passage",
    partOfSpeech: selection.word.includes(" ") ? "phrase" : "word",
    meaning:
      "No local lexical entry is available for this selection. Its sentence remains preserved, and nothing was sent outside Verbalore.",
    synonym: "Not available",
    antonym: "Not available",
    translations: {
      English: "No local entry",
      Hindi: "स्थानीय प्रविष्टि उपलब्ध नहीं है",
      Punjabi: "ਸਥਾਨਕ ਐਂਟਰੀ ਉਪਲਬਧ ਨਹੀਂ ਹੈ",
    },
    translationStatus: {
      English: "editorially-verified",
      Hindi: "model-assisted-unverified",
      Punjabi: "model-assisted-unverified",
    },
    shade: "Verbalore records the boundary honestly when its maintained local vocabulary does not cover a private selection.",
    whyHere: "The surrounding sentence remains available, but Verbalore does not invent a definition from context.",
    intonation: "No context-specific guidance has been authored for this selection.",
    family: "No maintained local entry",
    example: selection.sentence,
    sentence: selection.sentence,
  };
}

function dictionaryEntryToInsight(selection: SelectionState, entry: LessonLexiconEntry): WordInsight {
  const sense = entry.senses.find((candidate) => candidate.id === entry.preferredSenseId)
    || entry.senses[0];
  const pronunciation = entry.pronunciations[0];
  const pronunciationEvidence = lexicalPronunciationEvidence(selection.word, entry);
  const nearestWord = sense.synonyms[0] || entry.lemma;
  const isReadingSpecificSummary =
    entry.contextStatus === "reading-curated"
    || entry.source.includes("Verbalore maintained context-safe lexical notes");
  const lexicalMeaning = completeLexicalSentence(sense.definition);
  const presentedMeaning = entry.learnerMeaning
    ? completeLexicalSentence(entry.learnerMeaning)
    : isReadingSpecificSummary
      ? lexicalMeaning
      : `One maintained lexical possibility is: ${lexicalMeaning}`;
  return {
    word: selection.word,
    phonetic: pronunciation
      ? `/${pronunciation.value}/${pronunciation.variety ? ` · ${pronunciation.variety}` : ""}`
      : `lemma · ${entry.lemma}`,
    partOfSpeech: sense.partOfSpeech,
    meaning: presentedMeaning,
    synonym: nearestWord,
    antonym: "Not supplied by this local entry",
    translations: {
      English: presentedMeaning,
    },
    translationStatus: {
      English: "editorially-verified",
      Hindi: "withheld",
      Punjabi: "withheld",
    },
    shade: isReadingSpecificSummary
      ? "This meaning was selected for this reading. Alternate dictionary senses remain available when you want them."
      : "A maintained local dictionary lead, not yet an editorial claim about this exact sentence.",
    whyHere: isReadingSpecificSummary
      ? "This is the reading’s reusable meaning for the word. A position-specific note replaces it only when the sentence genuinely changes the sense."
      : "Verbalore has not yet verified which listed sense is active here, so Glance labels this as a possibility instead of presenting a guess as the answer.",
    intonation: "Context-specific voice guidance is reserved for editorially taught words.",
    family: `${isReadingSpecificSummary ? "Maintained" : "Dictionary"} lemma · ${entry.lemma}`,
    pronunciationEvidence,
    example: sense.examples[0] ? displayLexicalText(sense.examples[0]) : selection.sentence,
    sentence: selection.sentence,
  };
}

function normalizePronunciationForm(value: string) {
  return value.toLocaleLowerCase().replaceAll("_", " ").replace(/[’']/gu, "'").trim();
}

function lexicalPronunciationEvidence(
  displayedWord: string,
  entry?: LessonLexiconEntry,
): NonNullable<WordInsight["pronunciationEvidence"]> {
  const values = entry?.pronunciations || [];
  if (!values.length) {
    return {
      status: "open-no-source-value",
      source: "No maintained source pronunciation value is available for this displayed form.",
      values: [],
    };
  }
  const matchedLemma =
    entry?.pronunciationProvenance?.matchedLemma
    || entry?.provenance?.matchedLemma
    || entry?.lemma
    || "";
  const curatedForDisplayedForm =
    entry?.pronunciationProvenance?.kind === "cmu-pronouncing-dictionary"
    || entry?.pronunciationProvenance?.kind === "verbalore-authorial";
  const exactDisplayedForm =
    curatedForDisplayedForm
    || normalizePronunciationForm(matchedLemma) === normalizePronunciationForm(displayedWord);
  if (!exactDisplayedForm) {
    return {
      status: "open-related-form-only",
      source:
        `The maintained source value belongs to the related lemma “${entry?.lemma || matchedLemma}”; exact-form review remains open.`,
      values,
    };
  }
  const hasUnspecifiedVariety = values.some((value) => !value.variety?.trim());
  const maintainedSource = entry?.pronunciationProvenance;
  const sourceDescription = maintainedSource
    && maintainedSource.kind !== "open-english-wordnet"
    ? `${maintainedSource.source}${maintainedSource.note ? `. ${maintainedSource.note}` : ""}`
    : hasUnspecifiedVariety
      ? "The maintained lexical source contains this exact displayed form, but does not identify the pronunciation variety for every value."
      : "The maintained lexical source contains this exact displayed form and identifies the pronunciation variety.";
  return {
    status: hasUnspecifiedVariety
      ? "source-exact-variety-unspecified"
      : "source-exact",
    source: sourceDescription,
    values,
  };
}

function withBestAvailablePronunciation(
  insight: WordInsight,
  displayedWord: string,
  entry?: LessonLexiconEntry,
) {
  const available = lexicalPronunciationEvidence(displayedWord, entry);
  const currentRank = pronunciationEvidenceRank(insight.pronunciationEvidence?.status);
  const availableRank = pronunciationEvidenceRank(available.status);
  const pronunciation = available.values[0];
  if (!pronunciation || availableRank <= currentRank) return insight;
  const hasPlainReadingRespelling = Boolean(
    insight.phonetic
    && !insight.phonetic.includes("/")
    && !insight.phonetic.toLocaleLowerCase().includes("under review")
    && insight.phonetic !== "Selected from the passage",
  );
  return {
    ...insight,
    phonetic: hasPlainReadingRespelling
      ? insight.phonetic
      : `/${pronunciation.value}/${pronunciation.variety ? ` · ${pronunciation.variety}` : ""}`,
    pronunciationEvidence: available,
  };
}

function pronunciationEvidenceRank(
  status?: NonNullable<WordInsight["pronunciationEvidence"]>["status"],
) {
  return {
    "open-no-source-value": 0,
    "open-related-form-only": 1,
    "source-exact-variety-unspecified": 2,
    "source-exact": 3,
    "human-verified": 4,
  }[status || "open-no-source-value"];
}

function glanceTreatmentToInsight(
  selection: SelectionState,
  treatment: GlanceContextTreatment,
  entry?: LessonLexiconEntry,
): WordInsight {
  const pronunciation = treatment.pronunciation?.values[0] || entry?.pronunciations[0];
  const fallbackPronunciationEvidence = lexicalPronunciationEvidence(selection.word, entry);
  const pronunciationStatus = treatment.pronunciation?.status
    || fallbackPronunciationEvidence.status;
  const sourceSense = treatment.sourceSenseIds
    .map((id) => entry?.senses.find((sense) => sense.id === id))
    .find(Boolean);
  return {
    word: selection.word,
    phonetic: pronunciation
      ? `/${pronunciation.value}/${pronunciation.variety ? ` · ${pronunciation.variety}` : ""}`
      : "Written pronunciation still under review",
    partOfSpeech: treatment.partOfSpeech,
    meaning: treatment.meaningHere,
    alternateMeaning: treatment.plainMeaning,
    synonym: sourceSense?.synonyms[0] || entry?.lemma || "No simpler single-word substitute",
    antonym: "No context-safe opposite supplied",
    translations: {
      English: treatment.stillUnclear,
    },
    translationStatus: {
      English: "editorially-verified",
      Hindi: "withheld",
      Punjabi: "withheld",
    },
    shade: treatment.sentenceRole
      || "This explanation is bound to the exact place where the word appears.",
    whyHere: treatment.stillUnclear,
    intonation: "Use the sentence rhythm; no separate intonation claim has been authored for this word.",
    family: entry?.lemma ? `Dictionary lemma · ${entry.lemma}` : "No word-family claim supplied",
    pronunciationEvidence: {
      status: pronunciationStatus,
      source: treatment.pronunciation?.source
        || fallbackPronunciationEvidence.source,
      values: treatment.pronunciation?.values || fallbackPronunciationEvidence.values,
    },
    example: selection.sentence,
    sentence: selection.sentence,
  };
}

function occurrenceTreatment(
  pack: GlanceContextPack | undefined,
  selection: SelectionState | null,
) {
  if (!pack || !selection) return undefined;
  const treatmentId = pack.occurrences[selection.tokenId];
  if (!treatmentId) return undefined;
  const treatment = pack.treatments[treatmentId];
  if (!treatment || treatment.word !== selection.key) return undefined;
  return treatment;
}

function privateReadingToReading(record: PrivateReadingRecord): Reading {
  const paragraphs = record.sourceText
    .split(/\n\s*\n/g)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  return {
    id: `private:${record.id}`,
    edition: record.updatedAt.slice(0, 10) + ".1",
    title: record.title,
    subtitle: "Private reading · visible only in your account",
    deck: "Read at your pace. Nothing in this passage is analysed automatically.",
    whyItFits: "You brought this passage because its language matters to you.",
    provenance: "Private import",
    taxonomyVersion: "taxonomy_v2",
    editorialState: "approved",
    receipts: [],
    taxonomy: {
      outcomes: [],
      rhetoricalMoves: [],
      lexicalChallenge: "advanced",
      syntacticComplexity: "layered",
      narrativeComplexity: "linear",
      form: "essay",
      pace: "unhurried",
      registerFeel: "contemporary",
      emotionalIntensity: "cool",
      ambiguity: "stated",
      pointOfView: "first",
      mood: ["lucid"],
      craftFamily: "argument-essay",
      topics: ["ideas-science"],
      setting: { place: "Private import", era: "contemporary" },
      sensitivityFlags: ["none"],
    },
    segments: (paragraphs.length ? paragraphs : [record.sourceText]).map((text, index) => ({
      id: `p${index}`,
      kind: "prose",
      text,
    })),
    words: {},
    exercise: {
      kicker: "Private passage",
      title: "Keep only what earns a place.",
      prompt: "Select a word when it asks for attention; continue reading when it does not.",
      targetWords: [],
    },
  };
}

function evaluateBoundaryPractice(value: string): PracticeAssessment {
  const text = value.trim();
  const tokens = text.match(/\p{L}+(?:[’'-]\p{L}+)*/gu) || [];
  const words = tokens.length;
  const hasFirstPerson = /\b(?:i|me|my|mine)\b/i.test(text);
  const hasTurnAction = /\b(?:finish|complete|continue|speak|point|thought|turn|floor|respond|response|hear|listen|interrupt|wait)\b/i.test(text);
  const hasBoundary = /\b(?:let me|i(?:['’]ll| will)|i need|i want|finish|complete)\b/i.test(text);
  const hasHedges = /\b(?:just|maybe|perhaps|wondering|sort of|kind of|sorry)\b|if (?:that(?:’|')s|this is) okay/i.test(text);
  const hasReciprocity = /\b(?:then|after|hear|listen|respond|response|your turn|the floor|please)\b/i.test(text);

  const signals = [
    {
      id: "boundary",
      label: "The boundary is stated",
      met: hasBoundary,
      guidance: hasBoundary
        ? "The sentence says what will happen instead of asking whether it may happen."
        : "Name the action directly: “Let me finish…” or “I’ll complete the point…”",
    },
    {
      id: "hedges",
      label: "Permission-seeking is removed",
      met: Boolean(text) && !hasHedges,
      guidance: hasHedges
        ? "Remove softeners such as just, maybe, wondering, and if that’s okay."
        : "Courtesy remains possible without shrinking the request.",
    },
    {
      id: "reciprocity",
      label: "The other person keeps their dignity",
      met: hasReciprocity,
      guidance: hasReciprocity
        ? "You have made room for the other person after protecting your own turn."
        : "Consider a second clause: “then I want to hear your response.”",
    },
    {
      id: "economy",
      label: "The sentence carries its own weight",
      met: words >= 5 && words <= 22,
      guidance:
        words > 22
          ? "Compress the line. A boundary weakens when it must survive too much explanation."
          : words > 0
            ? "Aim for roughly five to twenty-two words: enough for clarity, little enough for force."
            : "Write the boundary in your own words before comparing it with the models.",
    },
  ];

  if (!text) {
    return {
      status: "empty",
      message: "No sentence was assessed. The examples below are authored demonstrations.",
      signals,
    };
  }

  if (words < 3) {
    return {
      status: "not-assessed",
      message: "That is too short for this check. Write the complete line you would actually say.",
      signals,
    };
  }

  if (!hasFirstPerson || !hasTurnAction) {
    return {
      status: "not-assessed",
      message: "This check is built for a spoken boundary. State your action: finish, continue, or speak. Make the order of turns clear.",
      signals,
    };
  }

  return {
    status: "assessed",
    message: "This authored check notices visible sentence patterns. It does not judge meaning, intention, or social context.",
    signals,
  };
}

const EXPRESSION_PATTERN_PROFILES: Record<string, { intention: RegExp; invariant: RegExp; contradiction?: RegExp }> = {
  "disagree-without-a-fight": {
    intention: /\b(?:disagree|don['’]t agree|not convinced|push back|not comfortable|against it|wrong call)\b/i,
    invariant: /\b(?:why|because|show|better|appeal|team|group|still|go with)\b/i,
  },
  "a-no-that-stays-no": {
    intention: /\b(?:no|can['’]t|cannot|won['’]t|will not|not able|not mine to carry)\b/i,
    invariant: /\b(?:no|can['’]t|cannot|won['’]t|will not|not able|not mine to carry|decision is made|not open)\b/i,
    contradiction: /\b(?:maybe|tomorrow|later|for now|this time|ask (?:me )?again|another time)\b/i,
  },
  "correct-dont-crush": {
    intention: /\b(?:error|mistake|wrong|off|doesn['’]t add up|needs? another pass|need to fix|needs? fixing)\b/i,
    invariant: /\b(?:fix|check|look|pass|before|send|ship|goes out)\b/i,
  },
  "say-what-you-want": {
    intention: /\b(?:can you|could you|would you|are you free|i['’]d like you|you in)\b/i,
    invariant: /\b(?:friday|session|cover|take)\b/i,
  },
  "the-truth-whole": {
    intention: /\b(?:isn['’]t working|not working|not going to work|don['’]t think .* work|over|finished|don['’]t see .* turning around)\b/i,
    invariant: /\b(?:more time|won['’]t change|will not change|not going to change|turning around|over|finished|ending|end it)\b/i,
  },
  "the-exact-feeling": {
    intention: /\b(?:hurt|left out|overlooked|stung|disappointed|dismissed|excluded|betrayed|uneasy)\b/i,
    invariant: /\b(?:because|that|when|you|not)\b/i,
  },
  "move-them-dont-push": {
    intention: /\b(?:back (?:this|a)|pilot|hear me out|give this|consider|worth your time|make the case)\b/i,
    invariant: /\b(?:two weeks?|risk|evidence|decide|commitment)\b/i,
  },
  "sorry-not-smaller": {
    intention: /\b(?:my fault|my mistake|i was wrong|i got .* wrong|this was on me|i own)\b/i,
    invariant: /\b(?:sorry|fix|make it right|cost you|put you through|own)\b/i,
  },
  "cut-to-the-point": {
    intention: /\bthursday\b/i,
    invariant: /\b(?:no review|review['’]s cancelled|review is cancelled|skipping the review)\b/i,
  },
  "on-the-record": {
    intention: /\b(?:disagree|against it|wrong call|not comfortable|silence read as agreement)\b/i,
    invariant: /\b(?:record|team|group|go with|cooperate|respect where)\b/i,
  },
  "light-and-still-standing": {
    intention: /\b(?:review|step|rework|later|rescue|rescues)\b/i,
    invariant: /\b(?:prevents?|saves?|avoid|more work|rework|rescue|rescues|later)\b/i,
  },
};

function evaluatePractice(value: string, study: ExpressionStudy): PracticeAssessment {
  if (study.id === BOUNDARY_STUDY.id) return evaluateBoundaryPractice(value);

  const text = value.trim();
  const tokens = text.match(/\p{L}+(?:[’'-]\p{L}+)*/gu) || [];
  const words = tokens.length;
  const pattern = EXPRESSION_PATTERN_PROFILES[study.id];
  const intentionVisible = Boolean(text && pattern?.intention.test(text));
  const invariantVisible = Boolean(
    text
    && intentionVisible
    && pattern?.invariant.test(text)
    && !pattern?.contradiction?.test(text),
  );
  const hasObviousPersonalAttack = /\b(?:idiot|moron|fool|bully|stupid|selfish|useless|pathetic|incompetent|hopeless|dishonest|lazy|hate you|shut up)\b/i.test(text);
  const upperBound = study.size === "quick" ? 22 : study.size === "standard" ? 34 : 46;

  const signals: PracticeSignal[] = [
    {
      id: "intention",
      label: "The intention is visible",
      met: intentionVisible,
      guidance: intentionVisible
        ? `The line contains the visible move this ${study.skill.toLocaleLowerCase()} study is built to practise.`
        : `State the move plainly: ${study.intention}`,
    },
    {
      id: "invariant",
      label: "The invariant survives",
      met: invariantVisible,
      guidance: invariantVisible
        ? "The authored facts this study must preserve are still available in the sentence."
        : `Check the sentence against this contract: ${study.invariant}`,
    },
    {
      id: "dignity",
      label: "No obvious personal attack is visible",
      met: Boolean(text) && !hasObviousPersonalAttack,
      guidance: hasObviousPersonalAttack
        ? "Name the problem, decision, or feeling without reducing the other person to an insult."
        : "This limited check found no obvious insult. Dignity still depends on context and delivery.",
    },
    {
      id: "economy",
      label: "The sentence is proportionate",
      met: words >= 3 && words <= upperBound,
      guidance: words > upperBound
        ? `This ${study.size} study asks for no more than ${upperBound} words. Cut explanation that does not protect the intention.`
        : "The sentence has enough room for clarity without making the listener excavate the point.",
    },
  ];

  if (!text) {
    return {
      status: "empty",
      message: "No sentence was assessed. The articulations above remain authored demonstrations.",
      signals,
    };
  }

  if (words < 3 || !intentionVisible) {
    return {
      status: "not-assessed",
      message: `This narrow authored check could not verify the visible move for “${study.title}”. It shows no score rather than guessing at your meaning.`,
      signals,
    };
  }

  return {
    status: "assessed",
    message: "This authored check notices only visible wording patterns. It does not judge your intention, relationship, delivery, or whether this stance is best for the moment.",
    signals,
  };
}

function StoryText({
  segment,
  words,
  glance,
}: {
  segment: StorySegment;
  words: Record<string, WordInsight>;
  glance?: GlanceContextPack;
}) {
  const parts = segment.text.split(/(\p{L}+(?:[’'-]\p{L}+)*)/gu);
  const offsets = parts.map((_, index) =>
    parts.slice(0, index).reduce((total, part) => total + part.length, 0)
  );
  return (
    <>
      {parts.map((part, index) => {
        const offset = offsets[index];
        if (!WORD_PATTERN.test(part)) return part;
        const key = normalizeWord(part);
        const tokenId = `${segment.id}-t${index}`;
        const sentence = sentenceContainingOffset(segment.text, offset);
        const wordInsight = words[key];
        const insight = wordInsight?.sentence === sentence ? wordInsight : undefined;
        const glanceTreatmentId = glance?.occurrences[tokenId];
        const glanceTreatment = glanceTreatmentId
          ? glance?.treatments[glanceTreatmentId]
          : undefined;
        const occurrenceIsEnriched = glanceTreatment?.word === key;
        const shared = {
          "data-selectable-word": "true",
          "data-word-key": key,
          "data-display-word": part,
          "data-token-id": tokenId,
          "data-paragraph-id": segment.id,
          "data-sentence": sentence,
        };

        if (insight || occurrenceIsEnriched) {
          return (
            <span
              key={tokenId}
              className="selectable-word enriched-word"
              role="button"
              tabIndex={0}
              aria-label={`Explore ${part}`}
              {...shared}
            >
              {part}
            </span>
          );
        }

        return (
          <span key={tokenId} className="selectable-word" tabIndex={-1} {...shared}>
            {part}
          </span>
        );
      })}
    </>
  );
}

type PageSearchParams = Record<string, string | string[] | undefined>;

function firstSearchValue(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

function calmMembershipDate(value: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}

function membershipStateCopy(entitlements: EntitlementSnapshot) {
  const accessEnd = calmMembershipDate(entitlements.commerce.validUntil);
  const graceEnd = calmMembershipDate(entitlements.commerce.providerGraceUntil);
  if (entitlements.status === "verification_grace") {
    return {
      heading: "Membership check delayed",
      detail: graceEnd
        ? `Your previously verified Plus access remains available through ${graceEnd} while Gumroad recovers.`
        : "Your previously verified Plus access remains available during this brief provider interruption.",
    };
  }
  if (entitlements.status === "verification_expired") {
    return {
      heading: "Membership needs verification",
      detail: "The 72-hour provider grace period ended. Your complete Free experience and everything you own remain available.",
    };
  }
  if (entitlements.status === "cancelled" && entitlements.plan === "plus") {
    return {
      heading: "Cancellation recorded",
      detail: accessEnd
        ? `Plus remains available through ${accessEnd}. Your learning and saved data remain yours afterwards.`
        : "Plus remains available for the paid period. Your learning and saved data remain yours afterwards.",
    };
  }
  if (entitlements.status === "cancelled") {
    return {
      heading: "Membership ended",
      detail: accessEnd
        ? `Plus ended on ${accessEnd}. The complete Free experience and everything you own remain available.`
        : "The paid period has ended. The complete Free experience and everything you own remain available.",
    };
  }
  if (entitlements.status === "active") {
    return {
      heading: "Membership current",
      detail: "Your Verbalore Plus access is current. The complete Free experience remains unchanged.",
    };
  }
  if (entitlements.status === "refunded") {
    return {
      heading: "Refund recorded",
      detail: "The account has returned to Free. Your saved learning, history, exports, and deletion controls remain available.",
    };
  }
  if (entitlements.status === "disputed" || entitlements.status === "chargebacked") {
    return {
      heading: "Payment review recorded",
      detail: "Plus is not active while the payment is unresolved. Your Free learning experience and everything you own remain available.",
    };
  }
  return {
    heading: entitlements.billingActive ? "Free membership" : "Plus is not for sale yet",
    detail: "The complete Reading-to-Command loop, one private-passage experience, and everything you own remain whole on Free.",
  };
}

function shareableHrefFromSearchParams(searchParams: PageSearchParams) {
  const params = new URLSearchParams();
  for (const [key, rawValue] of Object.entries(searchParams)) {
    const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
    if (value) params.set(key, value);
  }
  const query = params.toString();
  return query ? `/?${query}` : "/";
}

export default function Home({
  searchParams,
}: {
  searchParams: Promise<PageSearchParams>;
}) {
  const resolvedSearchParams = use(searchParams);
  const initialShareLocation = parseShareableLocation(
    shareableHrefFromSearchParams(resolvedSearchParams),
  );
  const returningFromMembership = firstSearchValue(resolvedSearchParams.membership) === "return";
  const explicitPracticeView = Boolean(firstSearchValue(resolvedSearchParams.practice));
  const storyRef = useRef<HTMLElement>(null);
  const inspectorRef = useRef<HTMLElement>(null);
  const pointerStart = useRef({ x: 0, y: 0, at: 0, pointerType: "mouse" });
  const meaningSwipeStart = useRef<{
    x: number;
    y: number;
    at: number;
    pointerId: number;
  } | null>(null);
  const pendingTap = useRef<number | null>(null);
  const lastTouchRelease = useRef<{ at: number; tokenId: string }>({ at: 0, tokenId: "" });
  const wordOrigin = useRef({ x: 0, y: 0 });
  const progressReady = useRef(false);
  const readingOpenRequest = useRef(0);
  const readingSizeMenuRef = useRef<HTMLDetailsElement>(null);
  const readingSizeAnchorRef = useRef<{ element: HTMLElement; top: number } | null>(null);
  const learningOriginRef = useRef<{
    view: "home" | "read" | "practice";
    readingId: string;
    paragraphId: string;
    paragraphTop: number | null;
    practiceView: PracticeView;
    selection: SelectionState | null;
    deepOpen: boolean;
    panelMode: PanelMode;
  } | null>(null);
  const intendedLearningSectionRef = useRef<LearningSection | null>(
    initialShareLocation.view === "learning" ? initialShareLocation.learningSection : null,
  );
  const vocabularyPageRequest = useRef(0);
  const loadedWordThreads = useRef(new Set<string>());
  const [showHome, setShowHome] = useState(initialShareLocation.view === "home");
  const [currentReadingId, setCurrentReadingId] = useState(
    initialShareLocation.workId || READINGS[0].id,
  );
  const [lessonLexiconState, setLessonLexiconState] = useState<{
    readingId: string;
    lexicon: LessonLexicon | null;
  }>(() => currentReadingId === READINGS[0].id
    ? { readingId: READINGS[0].id, lexicon: INITIAL_LESSON_LEXICON }
    : { readingId: currentReadingId, lexicon: null });
  const [discoveryOpen, setDiscoveryOpen] = useState(initialShareLocation.browse);
  const [discoveryParticular, setDiscoveryParticular] = useState(
    initialShareLocation.particular,
  );
  const [readingIntent, setReadingIntent] = useState<ReadingIntent | null>(initialShareLocation.intent);
  const [readingCommitmentChoice, setReadingCommitmentChoice] = useState<ReadingCommitment>(initialShareLocation.commitment);
  const [readingAppetite, setReadingAppetite] = useState<ReadingAppetite>(initialShareLocation.appetite);
  const [readingDifficulty, setReadingDifficulty] = useState<ReadingLexicalDifficulty | null>(initialShareLocation.difficulty);
  const [readingForm, setReadingForm] = useState<ReadingForm | null>(initialShareLocation.form);
  const [readingArticulation, setReadingArticulation] = useState<ReadingArticulation | null>(initialShareLocation.articulation);
  const [profile, setProfile] = useState<Profile>(DEFAULT_PROFILE);
  const [entitlements, setEntitlements] = useState<EntitlementSnapshot>(FREE_ENTITLEMENT);
  const [membershipLicenseKey, setMembershipLicenseKey] = useState("");
  const [membershipRefreshing, setMembershipRefreshing] = useState(false);
  const [membershipMessage, setMembershipMessage] = useState("");
  const [savedWords, setSavedWords] = useState<VocabularyItem[]>([]);
  const [warmWordItems, setWarmWordItems] = useState<VocabularyItem[]>([]);
  const [savedWordKeys, setSavedWordKeys] = useState<string[]>([]);
  const [savedWordTotal, setSavedWordTotal] = useState(0);
  const [savedContexts, setSavedContexts] = useState<VocabularyContext[]>([]);
  const [privateReadings, setPrivateReadings] = useState<PrivateReadingSummary[]>([]);
  const [readingList, setReadingList] = useState<ReadingListEntry[]>([]);
  const [libraryView, setLibraryView] = useState<LibraryView>("home");
  const [learningSection, setLearningSection] = useState<LearningSection>(
    initialShareLocation.learningSection,
  );
  const [wordSearch, setWordSearch] = useState("");
  const [wordAppliedSearch, setWordAppliedSearch] = useState("");
  const [wordGrouping, setWordGrouping] = useState<"state" | "story">("state");
  const [wordResultTotal, setWordResultTotal] = useState(0);
  const [wordNextCursor, setWordNextCursor] = useState<string | null>(null);
  const [wordPageLoading, setWordPageLoading] = useState(false);
  const [privateReadingRecords, setPrivateReadingRecords] = useState<Record<string, PrivateReadingRecord>>({});
  const [importOpen, setImportOpen] = useState(false);
  const [importTitle, setImportTitle] = useState("");
  const [importSource, setImportSource] = useState("");
  const [importAccepted, setImportAccepted] = useState(false);
  const [importError, setImportError] = useState("");
  const [importNeedsPlus, setImportNeedsPlus] = useState(false);
  const [savingImport, setSavingImport] = useState(false);
  const [confirmDeleteImport, setConfirmDeleteImport] = useState<string | null>(null);
  const [selection, setSelection] = useState<SelectionState | null>(null);
  const [pendingLibraryAnchor, setPendingLibraryAnchor] = useState<VocabularyItem | null>(null);
  const [wordMotion, setWordMotion] = useState<"open" | "replace">("replace");
  const [panelMode, setPanelMode] = useState<PanelMode>("empty");
  const [deepOpen, setDeepOpen] = useState(false);
  const [currentParagraph, setCurrentParagraph] = useState("p0");
  const [progress, setProgress] = useState(0);
  const [resumeParagraph, setResumeParagraph] = useState<string | null>(null);
  const [readingProgresses, setReadingProgresses] = useState<Record<string, ReadingProgress>>({});
  const [showReturn, setShowReturn] = useState(false);
  const [resumeCue, setResumeCue] = useState<{
    tokenId: string;
    paragraphId: string;
    word: string;
  } | null>(null);
  const [meaningView, setMeaningView] = useState(0);
  const [pronunciationGuideOpen, setPronunciationGuideOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [experienceMode, setExperienceMode] = useState<ExperienceMode>(
    initialShareLocation.view === "practice"
      ? "practice"
      : initialShareLocation.view === "learning"
        ? "learning"
        : "read",
  );
  const [activeExpressionStudyId, setActiveExpressionStudyId] = useState(
    initialShareLocation.view === "practice" && initialShareLocation.practiceView === "expression"
      ? initialShareLocation.studyId
      : BOUNDARY_STUDY.id,
  );
  const [expressionWork, setExpressionWork] = useState<Record<string, ExpressionWork>>({});
  const [practiceDraft, setPracticeDraft] = useState("");
  const [practiceRevision, setPracticeRevision] = useState("");
  const [practiceChecked, setPracticeChecked] = useState(false);
  const [activeStanceId, setActiveStanceId] = useState(
    initialShareLocation.view === "practice" && initialShareLocation.practiceView === "expression"
      ? initialShareLocation.stanceId
      : "tactful",
  );
  const [practiceAttempts, setPracticeAttempts] = useState<PracticeAttempt[]>([]);
  const [savingPractice, setSavingPractice] = useState(false);
  const [practiceView, setPracticeView] = useState<PracticeView>(initialShareLocation.practiceView);
  const [practiceVisited, setPracticeVisited] = useState(false);
  const [commandCatalogEntries, setCommandCatalogEntries] = useState<CommandVocabularyCatalogEntry[]>(
    INITIAL_COMMAND_VOCABULARY_INDEX,
  );
  const [loadedCommandEntries, setLoadedCommandEntries] = useState<Map<string, CommandVocabularyEntry>>(
    () => new Map(FREE_COMMAND_VOCABULARY.map((entry) => [entry.id, entry])),
  );
  const [commandWordId, setCommandWordId] = useState(
    initialShareLocation.wordId || INITIAL_COMMAND_VOCABULARY_INDEX[0]?.id || "",
  );
  const [commandRelatedWordId, setCommandRelatedWordId] = useState(
    initialShareLocation.relatedWordId,
  );
  const [commandWordQuery, setCommandWordQuery] = useState(initialShareLocation.wordQuery);
  const [commandWordHandle, setCommandWordHandle] = useState<CommandVocabularyHandle | "all">(
    initialShareLocation.wordHandle,
  );
  const [commandWordLevel, setCommandWordLevel] = useState<CommandVocabularyCatalogEntry["difficulty"] | "all">(
    initialShareLocation.wordLevel,
  );
  const [commandWordRegister, setCommandWordRegister] = useState<CommandVocabularyRegister | "all">(
    initialShareLocation.wordRegister,
  );
  const [commandWordArticulation, setCommandWordArticulation] = useState<CommandVocabularyArticulation | "all">(
    initialShareLocation.wordArticulation,
  );
  const [commandWordLoading, setCommandWordLoading] = useState(false);
  const [commandWordLoadError, setCommandWordLoadError] = useState("");
  const [commandWordLoadAttempt, setCommandWordLoadAttempt] = useState(0);
  const [wordReviews, setWordReviews] = useState<WordReview[]>([]);
  const [learningEvidence, setLearningEvidence] = useState<LearningEvidenceSummary | null>(null);
  const [learningEvidenceLoading, setLearningEvidenceLoading] = useState(false);
  const [learningEvidenceError, setLearningEvidenceError] = useState("");
  const [reviewWord, setReviewWord] = useState<string | null>(null);
  const [reviewChoice, setReviewChoice] = useState("");
  const [reviewDraft, setReviewDraft] = useState("");
  const [reviewSignals, setReviewSignals] = useState<string[]>([]);
  const [reviewFeedback, setReviewFeedback] = useState("");
  const [savingReview, setSavingReview] = useState(false);
  const [currentUser, setCurrentUser] = useState<SessionUser | null>(null);
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [plusOfferOpen, setPlusOfferOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [authError, setAuthError] = useState("");
  const [accountAction, setAccountAction] = useState<AccountAction>("idle");
  const [accountDeleteOpen, setAccountDeleteOpen] = useState(false);
  const [accountActionError, setAccountActionError] = useState("");
  const [speechVoices, setSpeechVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [speechVoiceUri, setSpeechVoiceUri] = useState("default");
  const [readingSize, setReadingSize] = useState<ReadingSize>("balanced");
  const [readingMeasure, setReadingMeasure] = useState<ReadingMeasure>("balanced");
  const [theme, setTheme] = useState<"paper" | "night">("paper");
  const [devicePreferencesReady, setDevicePreferencesReady] = useState(false);
  const closeDiscovery = useCallback(() => {
    if (window.history.state?.verbaloreBrowseEntry) {
      window.history.back();
      return;
    }
    setDiscoveryOpen(false);
  }, []);
  const discoveryDialogRef = useModalFocus(discoveryOpen, closeDiscovery);
  const importDialogRef = useModalFocus(importOpen, () => {
    if (!savingImport) setImportOpen(false);
  });
  const signInDialogRef = useModalFocus(signInOpen, () => {
    if (!signingIn) setSignInOpen(false);
  });
  const plusOfferDialogRef = useModalFocus(plusOfferOpen, () => setPlusOfferOpen(false));
  const accountMenuRef = useModalFocus(accountMenuOpen, () => setAccountMenuOpen(false));
  const pronunciationGuideDialogRef = useModalFocus(
    pronunciationGuideOpen,
    () => setPronunciationGuideOpen(false),
  );

  const activePrivateId = currentReadingId.startsWith("private:")
    ? currentReadingId.slice("private:".length)
    : null;
  const reading = useMemo(() => {
    if (activePrivateId && privateReadingRecords[activePrivateId]) {
      return privateReadingToReading(privateReadingRecords[activePrivateId]);
    }
    return READING_BY_ID.get(currentReadingId) || READINGS[0];
  }, [activePrivateId, currentReadingId, privateReadingRecords]);
  const isPrivateReading = reading.provenance === "Private import";
  const activeLessonLexicon = lessonLexiconState.readingId === reading.id
    ? lessonLexiconState.lexicon
    : null;
  const story = reading.segments;
  const words = reading.words;
  const storyTokenIds = useMemo(() => readingTokenIds(reading), [reading]);
  const readingRecommendations = useMemo(
    () => recommendReadings(
      readingIntent,
      reading.id,
      readingCommitmentChoice,
      readingAppetite,
      readingDifficulty,
      readingForm,
      readingArticulation,
    ),
    [
      reading.id,
      readingAppetite,
      readingArticulation,
      readingCommitmentChoice,
      readingDifficulty,
      readingForm,
      readingIntent,
    ],
  );
  const discoveryAsk = useMemo(
    () => composeDiscoveryAsk({
      commitment: readingCommitmentChoice,
      appetite: readingAppetite,
      articulation: readingArticulation,
      intent: readingIntent,
      difficulty: readingDifficulty,
      form: readingForm,
    }),
    [
      readingAppetite,
      readingArticulation,
      readingCommitmentChoice,
      readingDifficulty,
      readingForm,
      readingIntent,
    ],
  );
  const chaptersByOpening = useMemo(
    () => new Map((reading.chapters || []).map((chapter) => [chapter.openingSegmentId, chapter])),
    [reading.chapters],
  );

  useEffect(() => {
    let current = true;
    if (isPrivateReading) {
      return () => { current = false; };
    }
    if (reading.id === READINGS[0].id) {
      return () => { current = false; };
    }
    void loadLessonLexicon(reading.id).then((lexicon) => {
      if (current) setLessonLexiconState({ readingId: reading.id, lexicon });
    });
    return () => { current = false; };
  }, [isPrivateReading, reading.id]);

  const privateBundledInsight = useMemo(
    () => isPrivateReading && selection ? findWordInsight(selection.key) : undefined,
    [isPrivateReading, selection],
  );
  const contextualInsight = useMemo(
    () => {
      if (!selection) return undefined;
      const candidate = words[selection.key];
      return candidate?.sentence === selection.sentence ? candidate : undefined;
    },
    [selection, words],
  );
  const activeOccurrenceTreatment = useMemo(
    () => occurrenceTreatment(activeLessonLexicon?.glance, selection),
    [activeLessonLexicon?.glance, selection],
  );
  const dictionaryEntry = useMemo(
    () => (selection ? findDictionaryEntry(selection.key, activeLessonLexicon) : undefined),
    [activeLessonLexicon, selection],
  );
  const occurrenceInsight = useMemo(
    () => (
      selection && activeOccurrenceTreatment
        ? glanceTreatmentToInsight(selection, activeOccurrenceTreatment, dictionaryEntry)
        : undefined
    ),
    [activeOccurrenceTreatment, dictionaryEntry, selection],
  );
  const dictionaryOnly = Boolean(
    selection && dictionaryEntry && !contextualInsight && !occurrenceInsight,
  );
  const dictionaryContextVerified =
    dictionaryEntry?.contextStatus === "reading-curated";
  const noLexicalEntry = Boolean(
    selection
    && !contextualInsight
    && !occurrenceInsight
    && !dictionaryEntry
    && !privateBundledInsight,
  );
  const insight = useMemo(
    () => {
      if (!selection) return null;
      const selectedInsight =
        contextualInsight
        || occurrenceInsight
        || (dictionaryEntry ? dictionaryEntryToInsight(selection, dictionaryEntry) : undefined)
        || privateBundledInsight
        || fallbackInsight(selection);
      return withBestAvailablePronunciation(
        selectedInsight,
        selection.word,
        dictionaryEntry,
      );
    },
    [contextualInsight, dictionaryEntry, occurrenceInsight, privateBundledInsight, selection],
  );
  const meaningLabel = noLexicalEntry
    ? "No local entry"
    : dictionaryOnly
      ? dictionaryEntry?.senseStatus === "grammar"
        ? "Grammar note"
        : dictionaryEntry?.senseStatus === "entity"
          ? "Identity"
          : dictionaryContextVerified
            ? "Meaning in this reading"
            : "Meaning"
      : isPrivateReading
        ? "Maintained meaning · sense unverified"
        : "Meaning here";
  const preferredTranslation = insight
    ? translationPresentation(insight, profile.preferredLanguage)
    : null;
  const pronunciationEvidence = insight
    ? pronunciationEvidenceText(insight)
    : "";
  const meaningViews = useMemo(() => {
    if (!insight) return [];
    const candidates: Array<{ label: string; text: string; authored: boolean }> = [];
    if ((contextualInsight || occurrenceInsight) && !isPrivateReading && insight.alternateMeaning) {
      candidates.push(
        {
          label: "Plain words",
          text: insight.alternateMeaning,
          authored: true,
        },
        {
          label: meaningLabel,
          text: insight.meaning,
          authored: true,
        },
      );
    } else {
      candidates.push({
        label: meaningLabel,
        text: insight.meaning,
        authored: (!dictionaryOnly || dictionaryContextVerified) && !noLexicalEntry,
      });
      if (
        dictionaryContextVerified
        && dictionaryEntry?.simplerMeaning
      ) {
        candidates.push({
          label: "Simpler explanation",
          text: completeLexicalSentence(dictionaryEntry.simplerMeaning),
          authored: true,
        });
      }
    }
    if (dictionaryEntry?.senseStatus === "dictionary") {
      dictionaryEntry.senses.slice(0, 5).forEach((sense, index) => {
        candidates.push({
          label: dictionaryContextVerified
            ? `Another meaning ${index + 1}`
            : `Dictionary sense ${index + 1}`,
          text: displayLexicalText(sense.definition),
          authored: false,
        });
      });
    }
    return candidates
      .filter((candidate, index, all) =>
        comparableMeaning(candidate.text)
        && all.findIndex((item) =>
          comparableMeaning(item.text) === comparableMeaning(candidate.text)
        ) === index
      )
      .slice(0, 5);
  }, [
    contextualInsight,
    dictionaryContextVerified,
    dictionaryEntry,
    dictionaryOnly,
    insight,
    isPrivateReading,
    meaningLabel,
    noLexicalEntry,
    occurrenceInsight,
  ]);
  const activeMeaningIndex = Math.min(meaningView, Math.max(0, meaningViews.length - 1));
  const activeMeaning = meaningViews[activeMeaningIndex];
  const quickFacts = selection && insight && !noLexicalEntry
    ? buildLexicalQuickFacts({
      displayedWord: selection.word,
      dictionaryOnly,
      baseWord: dictionaryEntry?.lemma,
      translation: !dictionaryOnly && preferredTranslation
        ? {
          label: preferredTranslation.label,
          value: preferredTranslation.value,
          note: preferredTranslation.note,
        }
        : null,
    })
    : [];
  const handleMeaningSwipeStart = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (
      event.pointerType !== "touch"
      || !event.isPrimary
      || meaningViews.length < 2
    ) {
      meaningSwipeStart.current = null;
      return;
    }
    meaningSwipeStart.current = {
      x: event.clientX,
      y: event.clientY,
      at: window.performance.now(),
      pointerId: event.pointerId,
    };
  };
  const handleMeaningSwipeEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = meaningSwipeStart.current;
    meaningSwipeStart.current = null;
    if (
      !start
      || event.pointerType !== "touch"
      || event.pointerId !== start.pointerId
    ) {
      return;
    }
    const step = meaningSwipeStep({
      startX: start.x,
      startY: start.y,
      endX: event.clientX,
      endY: event.clientY,
      durationMs: window.performance.now() - start.at,
    });
    if (step) {
      setMeaningView((current) => moveMeaningIndex(current, meaningViews.length, step));
    }
  };
  const contextSentence = selection && insight ? selection.sentence : "";
  const savedKeys = useMemo(
    () => new Set([...savedWordKeys, ...savedWords.map((item) => item.word)]),
    [savedWordKeys, savedWords],
  );
  const commandCatalogById = useMemo(
    () => new Map(commandCatalogEntries.map((entry) => [entry.id, entry])),
    [commandCatalogEntries],
  );
  const activeCommandCatalogEntry = commandCatalogById.get(commandWordId)
    || commandCatalogEntries[0]
    || null;
  const activeCommandWord = activeCommandCatalogEntry
    ? loadedCommandEntries.get(activeCommandCatalogEntry.id) || null
    : null;
  const contextsByWord = useMemo(() => {
    const grouped = new Map<string, VocabularyContext[]>();
    for (const context of savedContexts) {
      const current = grouped.get(context.word) || [];
      current.push(context);
      grouped.set(context.word, current);
    }
    return grouped;
  }, [savedContexts]);
  const warmWords = useMemo(
    () => [...warmWordItems]
      .sort((a, b) => (b.updatedAt || b.createdAt || "").localeCompare(a.updatedAt || a.createdAt || ""))
      .slice(0, 7),
    [warmWordItems],
  );
  const groupedVisibleWords = useMemo(() => {
    const groups = new Map<string, VocabularyItem[]>();
    for (const item of savedWords) {
      const firstContext = contextsByWord.get(item.word)?.[0];
      const label = wordGrouping === "state"
        ? item.status
        : firstContext
          ? contextSourceName(firstContext, privateReadings)
          : "Earlier reading";
      groups.set(label, [...(groups.get(label) || []), item]);
    }
    const stateOrder = new Map([["Active", 0], ["Encountered", 1], ["Owned", 2]]);
    return [...groups.entries()]
      .sort(([a], [b]) => wordGrouping === "state"
        ? (stateOrder.get(a) ?? 9) - (stateOrder.get(b) ?? 9)
        : a.localeCompare(b))
      .map(([label, items]) => ({ label, items }));
  }, [contextsByWord, privateReadings, savedWords, wordGrouping]);

  const reviewsByWord = useMemo(
    () => new Map(wordReviews.map((review) => [review.item.word, review])),
    [wordReviews],
  );
  const isSaved = selection ? savedKeys.has(selection.key) : false;
  const practiceText = practiceRevision.trim() || practiceDraft.trim();
  const expressionStudy = useMemo(
    () => EXPRESSION_STUDY_BY_ID.get(activeExpressionStudyId) || BOUNDARY_STUDY,
    [activeExpressionStudyId],
  );
  const expressionStudyIndex = ALL_EXPRESSION_STUDIES.findIndex((study) => study.id === expressionStudy.id);
  const expressionTitleParts = expressionStudy.title.split(", ");
  const practiceAssessment = useMemo(
    () => evaluatePractice(practiceText, expressionStudy),
    [expressionStudy, practiceText],
  );
  const practiceSignals = practiceAssessment.signals;
  const activeStance = useMemo(
    () => expressionStudy.stances.find((stance) => stance.id === activeStanceId) || expressionStudy.stances[0],
    [activeStanceId, expressionStudy],
  );
  const shareableLocation = useMemo<ShareableLocation>(() => ({
    view: showHome ? "home" : experienceMode,
    workId: READING_BY_ID.has(currentReadingId) ? currentReadingId : null,
    browse: !showHome && experienceMode === "read" && discoveryOpen,
    particular: discoveryParticular,
    commitment: readingCommitmentChoice,
    appetite: readingAppetite,
    difficulty: readingDifficulty,
    intent: readingIntent,
    form: readingForm,
    articulation: readingArticulation,
    practiceView,
    learningSection,
    wordId: practiceView === "discover" ? commandWordId || null : null,
    relatedWordId: practiceView === "discover" ? commandRelatedWordId : null,
    wordQuery: practiceView === "discover" ? commandWordQuery : "",
    wordHandle: commandWordHandle,
    wordLevel: commandWordLevel,
    wordRegister: commandWordRegister,
    wordArticulation: commandWordArticulation,
    studyId: expressionStudy.id,
    stanceId: activeStance.id,
  }), [
    activeStance.id,
    currentReadingId,
    discoveryOpen,
    discoveryParticular,
    experienceMode,
    expressionStudy.id,
    learningSection,
    practiceView,
    commandWordId,
    commandRelatedWordId,
    commandWordQuery,
    commandWordArticulation,
    commandWordHandle,
    commandWordLevel,
    commandWordRegister,
    readingAppetite,
    readingArticulation,
    readingCommitmentChoice,
    readingDifficulty,
    readingForm,
    readingIntent,
    showHome,
  ]);

  const captureReadingViewportAnchor = useCallback((): ReadingViewportAnchor | null => {
    if (showHome || experienceMode !== "read") return null;
    const paragraphs = Array.from(
      document.querySelectorAll<HTMLElement>("[data-reading-paragraph]"),
    );
    if (!paragraphs.length) return null;
    const readingLine = Math.min(window.innerHeight * 0.35, 320);
    const visibleParagraph = paragraphs.find((paragraph) => {
      const bounds = paragraph.getBoundingClientRect();
      return bounds.top <= readingLine && bounds.bottom > readingLine;
    }) || paragraphs.reduce((nearest, paragraph) => (
      Math.abs(paragraph.getBoundingClientRect().top - readingLine)
        < Math.abs(nearest.getBoundingClientRect().top - readingLine)
        ? paragraph
        : nearest
    ));
    return {
      readingId: currentReadingId,
      paragraphId: visibleParagraph.dataset.paragraphId || currentParagraph,
      paragraphTop: visibleParagraph.getBoundingClientRect().top,
    };
  }, [currentParagraph, currentReadingId, experienceMode, showHome]);

  const pushShareableLocation = useCallback((next: ShareableLocation) => {
    if (next.view !== "read") {
      const readingAnchor = captureReadingViewportAnchor();
      if (readingAnchor) {
        const currentState = window.history.state && typeof window.history.state === "object"
          ? window.history.state
          : {};
        window.history.replaceState(
          { ...currentState, verbaloreReadingAnchor: readingAnchor },
          "",
          window.location.href,
        );
      }
    }
    window.history.pushState(
      { verbaloreView: next.view },
      "",
      buildShareableHref(window.location.href, next),
    );
  }, [captureReadingViewportAnchor]);
  const openDiscovery = useCallback(() => {
    if (!discoveryOpen) {
      const next = {
        ...shareableLocation,
        view: "read",
        workId: READING_BY_ID.has(currentReadingId) ? currentReadingId : READINGS[0].id,
        browse: true,
        particular: discoveryParticular,
      } satisfies ShareableLocation;
      window.history.pushState(
        { verbaloreView: "read", verbaloreBrowseEntry: true },
        "",
        buildShareableHref(window.location.href, next),
      );
    }
    setShowHome(false);
    setExperienceMode("read");
    setPanelMode("empty");
    setDiscoveryOpen(true);
  }, [
    currentReadingId,
    discoveryOpen,
    discoveryParticular,
    shareableLocation,
  ]);

  const actionableReviews = useMemo(
    () => wordReviews.filter((review) => review.authored),
    [wordReviews],
  );
  const currentReview = useMemo(
    () =>
      actionableReviews.find((review) => review.item.word === reviewWord) ||
      actionableReviews[0] ||
      null,
    [actionableReviews, reviewWord],
  );
  const dueReviewCount = useMemo(
    () => actionableReviews.filter((review) => review.evidence.due).length,
    [actionableReviews],
  );
  const adjacentTokenIds = useMemo(() => {
    if (!selection || selection.tokenId.startsWith("range-")) {
      return { previous: null, next: null };
    }
    const index = storyTokenIds.indexOf(selection.tokenId);
    return {
      previous: index > 0 ? storyTokenIds[index - 1] : null,
      next: index >= 0 && index < storyTokenIds.length - 1 ? storyTokenIds[index + 1] : null,
    };
  }, [selection, storyTokenIds]);

  const notify = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2600);
  }, []);

  const loadVocabularyPage = useCallback(async ({
    append,
    cursor = "",
    query,
  }: {
    append: boolean;
    cursor?: string;
    query: string;
  }) => {
    const requestId = vocabularyPageRequest.current + 1;
    vocabularyPageRequest.current = requestId;
    setWordPageLoading(true);
    try {
      const parameters = new URLSearchParams({ limit: "120" });
      if (query.trim()) parameters.set("q", query.trim());
      if (cursor) parameters.set("cursor", cursor);
      const response = await fetch(`/api/vocabulary?${parameters.toString()}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "My Words could not be opened.");
      if (requestId !== vocabularyPageRequest.current) return;
      const items = (data.vocabulary || []) as VocabularyItem[];
      const contexts = (data.contexts || []) as VocabularyContext[];
      const mergeWords = (current: VocabularyItem[]) => {
        if (!append) return items;
        const merged = new Map(current.map((item) => [item.word, item]));
        items.forEach((item) => merged.set(item.word, item));
        return [...merged.values()];
      };
      const contextKey = (context: VocabularyContext) => context.id
        ? `id:${context.id}`
        : `${context.word}:${context.sourceType}:${context.sourceId}:${context.sourceAnchor}`;
      const mergeContexts = (current: VocabularyContext[]) => {
        if (!append) return contexts;
        const merged = new Map(current.map((context) => [contextKey(context), context]));
        contexts.forEach((context) => merged.set(contextKey(context), context));
        return [...merged.values()];
      };
      setSavedWords(mergeWords);
      setSavedContexts(mergeContexts);
      setSavedWordKeys((current) => [...new Set([...current, ...items.map((item) => item.word)])]);
      setWordNextCursor(data.page?.nextCursor || null);
      setWordResultTotal(Number(data.page?.total || 0));
      setWordAppliedSearch(String(data.page?.search || ""));
      if (!query.trim()) setSavedWordTotal(Number(data.page?.total || 0));
    } catch (error) {
      if (requestId === vocabularyPageRequest.current) {
        notify(error instanceof Error ? error.message : "My Words could not be opened.");
      }
    } finally {
      if (requestId === vocabularyPageRequest.current) setWordPageLoading(false);
    }
  }, [notify]);

  const loadWordThread = useCallback(async (word: string) => {
    if (loadedWordThreads.current.has(word)) return;
    try {
      const [threadResponse, reviewResponse] = await Promise.all([
        fetch(`/api/vocabulary?word=${encodeURIComponent(word)}`),
        fetch(`/api/review?word=${encodeURIComponent(word)}`),
      ]);
      const [threadData, reviewData] = await Promise.all([threadResponse.json(), reviewResponse.json()]);
      if (!threadResponse.ok) throw new Error(threadData.error || "This learning thread could not be opened.");
      setSavedWords((current) => current.map((item) => item.word === word ? threadData.item : item));
      setSavedContexts((current) => [
        ...(threadData.contexts || []),
        ...current.filter((context) => context.word !== word),
      ]);
      if (reviewResponse.ok && reviewData.review) {
        setWordReviews((current) => [
          reviewData.review,
          ...current.filter((review) => review.item.word !== word),
        ]);
      }
      loadedWordThreads.current.add(word);
    } catch (error) {
      notify(error instanceof Error ? error.message : "This learning thread could not be opened.");
    }
  }, [notify]);

  useEffect(() => {
    if (!currentUser || panelMode !== "library" || libraryView !== "words") return;
    const delay = wordSearch.trim() ? 240 : 0;
    const timer = window.setTimeout(() => {
      loadedWordThreads.current.clear();
      void loadVocabularyPage({ append: false, query: wordSearch });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [currentUser, libraryView, loadVocabularyPage, panelMode, wordSearch]);

  const openReading = useCallback(async (readingId: string) => {
    if (!READING_BY_ID.has(readingId)) return false;
    pushShareableLocation({
      ...shareableLocation,
      view: "read",
      workId: readingId,
      browse: false,
    });
    const requestId = readingOpenRequest.current + 1;
    readingOpenRequest.current = requestId;
    setLessonLexiconState({ readingId, lexicon: null });
    setCurrentReadingId(readingId);
    window.localStorage.setItem("verbalore-current-reading", readingId);
    setShowHome(false);
    setExperienceMode("read");
    setPanelMode("empty");
    setSelection(null);
    setDeepOpen(false);
    setDiscoveryOpen(false);
    setImportOpen(false);
    const savedProgress = readingProgresses[readingId];
    setCurrentParagraph(savedProgress?.paragraphId || "p0");
    setProgress(savedProgress?.percentage || 0);
    setResumeParagraph(savedProgress?.paragraphId || null);
    window.setTimeout(() => document.getElementById("lesson-start")?.scrollIntoView({ block: "start" }), 0);
    void loadLessonLexicon(readingId).then((lexicon) => {
      if (readingOpenRequest.current !== requestId) return;
      if (!lexicon) {
        notify("This reading is open, but its word pack is unavailable. Your place remains safe.");
        return;
      }
      setLessonLexiconState({ readingId, lexicon });
    });
    return true;
  }, [notify, pushShareableLocation, readingProgresses, shareableLocation]);

  useEffect(() => {
    if (!discoveryOpen) return;
    for (const { reading: candidate } of readingRecommendations) {
      void loadLessonLexicon(candidate.id);
    }
  }, [discoveryOpen, readingRecommendations]);

  const activatePrivateReading = useCallback((record: PrivateReadingRecord) => {
    const readingId = `private:${record.id}`;
    pushShareableLocation({
      ...shareableLocation,
      view: "read",
      workId: null,
      browse: false,
    });
    setPrivateReadingRecords((current) => ({ ...current, [record.id]: record }));
    setCurrentReadingId(readingId);
    window.localStorage.setItem("verbalore-current-reading", readingId);
    setExperienceMode("read");
    setPanelMode("empty");
    setSelection(null);
    setDeepOpen(false);
    setDiscoveryOpen(false);
    setImportOpen(false);
    const savedProgress = readingProgresses[readingId];
    setCurrentParagraph(savedProgress?.paragraphId || "p0");
    setProgress(savedProgress?.percentage || 0);
    setResumeParagraph(savedProgress?.paragraphId || null);
    window.setTimeout(() => document.getElementById("lesson-start")?.scrollIntoView({ block: "start" }), 0);
  }, [pushShareableLocation, readingProgresses, shareableLocation]);

  const openPrivateReading = useCallback(async (id: string) => {
    const cached = privateReadingRecords[id];
    if (cached) {
      activatePrivateReading(cached);
      return cached;
    }
    try {
      const response = await fetch(`/api/imports?id=${encodeURIComponent(id)}`);
      const data = await response.json();
      if (!response.ok || !data.reading) {
        throw new Error(data.error || "Private reading could not be opened.");
      }
      activatePrivateReading(data.reading);
      return data.reading as PrivateReadingRecord;
    } catch (error) {
      notify(error instanceof Error ? error.message : "Private reading could not be opened.");
      return null;
    }
  }, [activatePrivateReading, notify, privateReadingRecords]);

  const loadLearnerData = useCallback(async () => {
    setLoadingProfile(true);
    progressReady.current = false;
    try {
      const [profileResponse, practiceResponse, reviewResponse, importsResponse, readingListResponse] = await Promise.all([
        fetch("/api/profile"),
        fetch("/api/practice"),
        fetch("/api/review?limit=120"),
        fetch("/api/imports"),
        fetch("/api/reading-list"),
      ]);
      if (!profileResponse.ok || !practiceResponse.ok || !reviewResponse.ok || !importsResponse.ok || !readingListResponse.ok) {
        throw new Error("Private profile unavailable");
      }
      const [profileData, practiceData, reviewData, importsData, readingListData] = await Promise.all([
        profileResponse.json(),
        practiceResponse.json(),
        reviewResponse.json(),
        importsResponse.json(),
        readingListResponse.json(),
      ]);
      if (profileData.profile) setProfile(profileData.profile);
      if (profileData.entitlements) {
        setEntitlements(profileData.entitlements as EntitlementSnapshot);
      }
      if (profileData.vocabulary) {
        setSavedWords(profileData.vocabulary);
        setWarmWordItems(profileData.vocabulary);
      }
      if (profileData.contexts) setSavedContexts(profileData.contexts);
      setSavedWordKeys((profileData.vocabularyWords || []) as string[]);
      setSavedWordTotal(Number(profileData.vocabularyTotal || 0));
      setWordResultTotal(Number(profileData.vocabularyTotal || 0));
      setWordNextCursor(null);
      setWordAppliedSearch("");
      loadedWordThreads.current.clear();
      if (profileData.progresses) {
        const byReading = Object.fromEntries(
          (profileData.progresses as ReadingProgress[]).map((item) => [item.lessonId, item]),
        );
        setReadingProgresses(byReading);
        const rememberedReading = window.localStorage.getItem("verbalore-current-reading") || READINGS[0].id;
        const current = byReading[rememberedReading];
        if (current) {
          setResumeParagraph(current.paragraphId);
          setProgress(current.percentage || 0);
        }
      } else if (profileData.progress?.lessonId === (window.localStorage.getItem("verbalore-current-reading") || READINGS[0].id)) {
        setResumeParagraph(profileData.progress.paragraphId);
        setProgress(profileData.progress.percentage || 0);
      }
      if (practiceData.attempts) setPracticeAttempts(practiceData.attempts);
      if (reviewData.reviews) {
        setWordReviews(reviewData.reviews);
        setReviewWord((current) => current || reviewData.reviews[0]?.item?.word || null);
      }
      const imported = (importsData.readings || []) as PrivateReadingSummary[];
      setPrivateReadings(imported);
      setReadingList((readingListData.readings || []) as ReadingListEntry[]);
      const rememberedReading = window.localStorage.getItem("verbalore-current-reading") || READINGS[0].id;
      const rememberedPrivateId = rememberedReading.startsWith("private:")
        ? rememberedReading.slice("private:".length)
        : null;
      if (rememberedPrivateId && imported.some((item) => item.id === rememberedPrivateId)) {
        const sourceResponse = await fetch(`/api/imports?id=${encodeURIComponent(rememberedPrivateId)}`);
        if (sourceResponse.ok) {
          const sourceData = await sourceResponse.json();
          if (sourceData.reading) {
            setPrivateReadingRecords((current) => ({
              ...current,
              [rememberedPrivateId]: sourceData.reading,
            }));
            setCurrentReadingId(rememberedReading);
          }
        }
      } else if (rememberedPrivateId) {
        setCurrentReadingId(READINGS[0].id);
        window.localStorage.setItem("verbalore-current-reading", READINGS[0].id);
      }
      window.setTimeout(() => {
        progressReady.current = true;
      }, 500);
      return profileData.entitlements as EntitlementSnapshot | undefined;
    } catch {
      notify("Your account opened, but its learning data could not be loaded yet.");
      return undefined;
    } finally {
      setLoadingProfile(false);
    }
  }, [notify]);

  const syncViewFromLocation = useCallback(() => {
    const next = parseShareableLocation(window.location.href);
    setShowHome(next.view === "home");
    setDiscoveryOpen(next.browse);
    setDiscoveryParticular(next.particular);
    setReadingCommitmentChoice(next.commitment);
    setReadingAppetite(next.appetite);
    setReadingDifficulty(next.difficulty);
    setReadingIntent(next.intent);
    setReadingForm(next.form);
    setReadingArticulation(next.articulation);
    setPracticeView(next.practiceView);
    setLearningSection(next.learningSection);
    setCommandWordId(next.wordId || INITIAL_COMMAND_VOCABULARY_INDEX[0]?.id || "");
    setCommandRelatedWordId(next.relatedWordId);
    setCommandWordQuery(next.wordQuery);
    setCommandWordHandle(next.wordHandle);
    setCommandWordLevel(next.wordLevel);
    setCommandWordRegister(next.wordRegister);
    setCommandWordArticulation(next.wordArticulation);
    setActiveExpressionStudyId(next.studyId);
    setActiveStanceId(next.stanceId);
    if (next.view === "read") {
      setExperienceMode("read");
      const origin = learningOriginRef.current;
      const returningToLearningOrigin = experienceMode === "learning"
        && origin?.view === "read"
        && (!next.workId || next.workId === origin.readingId);
      const historyAnchor = !returningToLearningOrigin
        && window.history.state?.verbaloreReadingAnchor
        && typeof window.history.state.verbaloreReadingAnchor === "object"
        ? window.history.state.verbaloreReadingAnchor as ReadingViewportAnchor
        : null;
      const readingAnchor = returningToLearningOrigin && origin
        ? {
            readingId: origin.readingId,
            paragraphId: origin.paragraphId,
            paragraphTop: origin.paragraphTop,
          }
        : historyAnchor;
      setPanelMode(
        returningToLearningOrigin && origin.selection && origin.panelMode === "word"
          ? "word"
          : "empty",
      );
      setSelection(returningToLearningOrigin ? origin.selection : null);
      setDeepOpen(returningToLearningOrigin ? origin.deepOpen : false);
      const nextReadingId = readingAnchor?.readingId || next.workId;
      if (nextReadingId) {
        setCurrentReadingId(nextReadingId);
        window.localStorage.setItem("verbalore-current-reading", nextReadingId);
      }
      if (readingAnchor) {
        let frame = 0;
        let attempts = 0;
        const restoreReadingAnchor = () => {
          const paragraph = document.querySelector<HTMLElement>(
            `[data-reading-paragraph][data-paragraph-id="${CSS.escape(readingAnchor.paragraphId)}"]`,
          );
          const renderedReadingId = storyRef.current?.dataset.readingId;
          if (!paragraph || renderedReadingId !== readingAnchor.readingId) {
            attempts += 1;
            if (attempts < 8) frame = window.requestAnimationFrame(restoreReadingAnchor);
            return;
          }
          const difference = paragraph.getBoundingClientRect().top - readingAnchor.paragraphTop;
          if (Math.abs(difference) > 0.5) window.scrollBy(0, difference);
          attempts += 1;
          if (attempts < 8) frame = window.requestAnimationFrame(restoreReadingAnchor);
        };
        frame = window.requestAnimationFrame(restoreReadingAnchor);
        window.setTimeout(() => window.cancelAnimationFrame(frame), 1000);
      }
    } else if (next.view === "practice") {
      setExperienceMode("practice");
      setPanelMode("empty");
    } else if (next.view === "learning") {
      setExperienceMode("learning");
      setPanelMode(
        next.learningSection === "overview"
        || next.learningSection === "readings"
        || next.learningSection === "words"
          ? "library"
          : "profile",
      );
      intendedLearningSectionRef.current = next.learningSection;
    }
  }, [experienceMode]);

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    window.addEventListener("popstate", syncViewFromLocation);
    window.addEventListener("hashchange", syncViewFromLocation);
    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
      window.removeEventListener("popstate", syncViewFromLocation);
      window.removeEventListener("hashchange", syncViewFromLocation);
    };
  }, [syncViewFromLocation]);

  useEffect(() => {
    let cancelled = false;
    void loadCommandVocabularyIndex()
      .then((entries) => {
        if (cancelled) return;
        setCommandCatalogEntries(entries);
        setCommandWordId((current) => entries.some((entry) => entry.id === current)
          ? current
          : entries[0]?.id || "");
      })
      .catch(() => {
        // The permanent Free 50 remains complete if the Plus catalogue is unavailable.
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const meta = commandCatalogById.get(commandWordId);
    if (!meta || loadedCommandEntries.has(meta.id) || (meta.access === "plus" && entitlements.plan !== "plus")) {
      window.queueMicrotask(() => {
        if (cancelled) return;
        setCommandWordLoading(false);
        setCommandWordLoadError("");
      });
      return () => { cancelled = true; };
    }
    window.queueMicrotask(() => {
      if (cancelled) return;
      setCommandWordLoading(true);
      setCommandWordLoadError("");
    });
    void loadCommandVocabularyEntry(meta)
      .then((entry) => {
        if (cancelled || !entry) return;
        setLoadedCommandEntries((current) => new Map(current).set(entry.id, entry));
      })
      .catch((error) => {
        if (!cancelled) {
          setCommandWordLoadError(error instanceof Error ? error.message : "This word could not be opened.");
        }
      })
      .finally(() => {
        if (!cancelled) setCommandWordLoading(false);
      });
    return () => { cancelled = true; };
  }, [commandCatalogById, commandWordId, commandWordLoadAttempt, entitlements.plan, loadedCommandEntries]);

  useEffect(() => {
    const preferences = readStoredDevicePreferences(window.localStorage);
    let cancelled = false;
    if (preferences.theme === "night") {
      document.documentElement.dataset.theme = "night";
    }
    window.queueMicrotask(() => {
      if (cancelled) return;
      if (!initialShareLocation.workId) {
        const rememberedReading = preferences.currentReadingId;
        if (
          rememberedReading
          && (READING_BY_ID.has(rememberedReading) || /^private:[a-f0-9-]{36}$/i.test(rememberedReading))
        ) {
          setCurrentReadingId(rememberedReading);
        }
      }
      if (
        initialShareLocation.view !== "practice"
        || initialShareLocation.practiceView !== "expression"
      ) {
        if (
          preferences.expressionStudyId
          && EXPRESSION_STUDY_BY_ID.has(preferences.expressionStudyId)
        ) {
          setActiveExpressionStudyId(preferences.expressionStudyId);
        }
        const rememberedStance = preferences.expressionStanceId;
        if (rememberedStance && BOUNDARY_STUDY.stances.some(({ id }) => id === rememberedStance)) {
          setActiveStanceId(rememberedStance);
        }
      }
      setSpeechVoiceUri(preferences.pronunciationVoiceUri);
      setReadingSize(preferences.readingSize);
      setReadingMeasure(preferences.readingMeasure);
      setPracticeVisited(preferences.practiceVisited);
      if (
        initialShareLocation.view === "practice"
        && initialShareLocation.practiceView === "overview"
        && !explicitPracticeView
        && preferences.practiceVisited
      ) {
        setPracticeView(preferences.lastPracticeView);
      }
      setTheme(preferences.theme);
      setDevicePreferencesReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [
    initialShareLocation.practiceView,
    initialShareLocation.view,
    initialShareLocation.workId,
    explicitPracticeView,
  ]);

  useEffect(() => {
    if (!devicePreferencesReady) return;
    const nextHref = buildShareableHref(window.location.href, shareableLocation);
    const currentHref = `${window.location.pathname}${window.location.search}`;
    if (nextHref !== currentHref || window.location.hash) {
      window.history.replaceState(
        { ...window.history.state, verbaloreView: shareableLocation.view },
        "",
        nextHref,
      );
    }
  }, [devicePreferencesReady, shareableLocation]);

  useEffect(() => {
    const publicReading = !showHome && !isPrivateReading;
    const canonical = publicReading
      ? `https://verbalore.com/?view=read&work=${encodeURIComponent(reading.id)}`
      : "https://verbalore.com/";
    const title = publicReading
      ? `${reading.title} | Verbalore`
      : "Verbalore | Choose how your English lands";
    const description = publicReading
      ? reading.deck
      : "Understand English in context, then learn to choose how your English lands with clarity, tact, warmth, restraint, and force.";
    document.title = title;
    document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute("href", canonical);
    document.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.setAttribute("content", title);
    document.querySelector<HTMLMetaElement>('meta[property="og:description"]')?.setAttribute("content", description);
    document.querySelector<HTMLMetaElement>('meta[property="og:url"]')?.setAttribute("content", canonical);
    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", description);
  }, [isPrivateReading, reading.deck, reading.id, reading.title, showHome]);

  useEffect(() => {
    let cancelled = false;
    const membershipReturnRequested = returningFromMembership
      || new URL(window.location.href).searchParams.get("membership") === "return";
    Promise.all([
      fetch("/api/auth/config"),
      fetch("/api/auth/session"),
      fetch("/api/membership/offer")
        .then(async (response) => response.ok ? response.json() : null)
        .catch(() => null),
    ])
      .then(async ([configResponse, sessionResponse, offerData]) => {
        const [configData, sessionData] = await Promise.all([
          configResponse.json(),
          sessionResponse.json(),
        ]);
        if (cancelled) return;
        setGoogleClientId(configData.googleClientId || null);
        setAuthReady(true);
        if (offerData?.entitlements) {
          setEntitlements(offerData.entitlements as EntitlementSnapshot);
        }
        if (sessionData.user) {
          setCurrentUser(sessionData.user);
          const loadedEntitlements = await loadLearnerData();
          if (cancelled) return;
          if (membershipReturnRequested) {
            setShowHome(false);
            setExperienceMode("learning");
            setLearningSection("membership");
            setPanelMode("profile");
            window.history.replaceState(
              { verbaloreView: "learning" },
              "",
              buildShareableHref(window.location.href, {
                ...parseShareableLocation(window.location.href),
                view: "learning",
                browse: false,
                learningSection: "membership",
              }),
            );
            setMembershipMessage(
              loadedEntitlements?.plan === "plus"
                ? "Welcome back. Your Verbalore Plus membership is connected."
                : "Welcome back. If the purchase is still arriving, use the recovery option below once it appears on your receipt.",
            );
          }
        } else {
          setLoadingProfile(false);
          if (membershipReturnRequested || initialShareLocation.view === "learning") {
            if (membershipReturnRequested) intendedLearningSectionRef.current = "membership";
            setAuthError("");
            setSignInOpen(true);
            notify(membershipReturnRequested
              ? "Sign in with the email used for your Verbalore purchase."
              : "Sign in to continue to your private learning record.");
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        setAuthReady(true);
        setLoadingProfile(false);
        if (membershipReturnRequested) {
          setAuthError("Verbalore received your return from Gumroad, but could not check this session yet.");
          setSignInOpen(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [initialShareLocation.view, loadLearnerData, notify, returningFromMembership]);

  useEffect(() => {
    const openChooser = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (event.key !== "/" || target?.matches("input, textarea, select, [contenteditable='true']")) return;
      event.preventDefault();
      openDiscovery();
    };
    window.addEventListener("keydown", openChooser);
    return () => window.removeEventListener("keydown", openChooser);
  }, [openDiscovery]);

  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    const loadVoices = () => {
      const englishVoices = window.speechSynthesis
        .getVoices()
        .filter((voice) => voice.lang.toLowerCase().startsWith("en"))
        .sort((a, b) => a.lang.localeCompare(b.lang) || a.name.localeCompare(b.name));
      setSpeechVoices(englishVoices);
    };
    loadVoices();
    window.speechSynthesis.addEventListener("voiceschanged", loadVoices);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
  }, []);

  useEffect(() => {
    if (showHome) return;
    const paragraphs = Array.from(
      document.querySelectorAll<HTMLElement>("[data-reading-paragraph]"),
    );
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const id = (visible.target as HTMLElement).dataset.paragraphId || "p0";
        const index = story.findIndex((segment) => segment.id === id);
        setCurrentParagraph(id);
        setProgress(Math.round(((index + 1) / story.length) * 100));
      },
      { rootMargin: "-25% 0px -58% 0px", threshold: [0.05, 0.4, 0.8] },
    );
    paragraphs.forEach((paragraph) => observer.observe(paragraph));
    return () => observer.disconnect();
  }, [reading.id, showHome, story]);

  useEffect(() => {
    if (!currentUser || !progressReady.current) return;
    const timer = window.setTimeout(() => {
      void fetch("/api/progress", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lessonId: reading.id,
          paragraphId: currentParagraph,
          percentage: progress,
          selectedWord: selection?.word || null,
          selectedAnchor: selection?.paragraphId || null,
        }),
      });
    }, 650);
    return () => window.clearTimeout(timer);
  }, [currentParagraph, currentUser, progress, reading.id, selection]);

  useEffect(() => {
    window.localStorage.setItem("verbalore-expression-stance", activeStanceId);
  }, [activeStanceId]);

  useLayoutEffect(() => {
    const anchor = readingSizeAnchorRef.current;
    if (!anchor) return;
    readingSizeAnchorRef.current = null;

    const restoreAnchor = () => {
      if (!anchor.element.isConnected) return;
      const difference = anchor.element.getBoundingClientRect().top - anchor.top;
      if (Math.abs(difference) > 0.5) window.scrollBy(0, difference);
    };

    restoreAnchor();
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      restoreAnchor();
      secondFrame = window.requestAnimationFrame(restoreAnchor);
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [readingMeasure, readingSize]);

  useLayoutEffect(() => {
    let scheduled = false;
    const checkAnchor = () => {
      scheduled = false;
      if (!selection) {
        setShowReturn(false);
        return;
      }
      const target = selection.tokenId.startsWith("range-")
        ? document.querySelector<HTMLElement>(
            `[data-reading-paragraph][data-paragraph-id="${selection.paragraphId}"]`,
          )
        : document.querySelector<HTMLElement>(
            `[data-token-id="${selection.tokenId}"]`,
          );
      if (!target) return;
      const rect = target.getBoundingClientRect();
      setShowReturn(rect.bottom < 76 || rect.top > window.innerHeight - 96);
    };
    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(checkAnchor);
    };
    checkAnchor();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [selection]);

  useLayoutEffect(() => {
    if (!selection || wordMotion !== "open" || !inspectorRef.current) return;
    const panelRect = inspectorRef.current.getBoundingClientRect();
    inspectorRef.current.style.setProperty(
      "--word-origin-x",
      `${wordOrigin.current.x - panelRect.left}px`,
    );
    inspectorRef.current.style.setProperty(
      "--word-origin-y",
      `${wordOrigin.current.y - panelRect.top}px`,
    );
  }, [selection, wordMotion]);

  useLayoutEffect(() => {
    if (panelMode !== "word" || !selection || !inspectorRef.current) return;
    inspectorRef.current.scrollTo({ top: 0, behavior: "auto" });
  }, [deepOpen, panelMode, selection]);

  useLayoutEffect(() => {
    if (!discoveryOpen && !signInOpen && !importOpen && !plusOfferOpen) return;
    const body = document.body;
    const scrollY = window.scrollY;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflowY: body.style.overflowY,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflowY = "scroll";
    return () => {
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.width = previous.width;
      body.style.overflowY = previous.overflowY;
      window.scrollTo(0, scrollY);
    };
  }, [discoveryOpen, importOpen, plusOfferOpen, signInOpen]);

  useEffect(() => () => {
    if (pendingTap.current !== null) window.clearTimeout(pendingTap.current);
    document
      .querySelectorAll(".tap-acknowledged")
      .forEach((element) => element.classList.remove("tap-acknowledged"));
  }, []);

  useEffect(() => {
    document
      .querySelectorAll(".current-selection")
      .forEach((element) => element.classList.remove("current-selection"));
    if (!selection || selection.tokenId.startsWith("range-")) return;
    const selectedElement = document.querySelector<HTMLElement>(
      `[data-token-id="${selection.tokenId}"]`,
    );
    selectedElement?.classList.add("current-selection");
    return () => selectedElement?.classList.remove("current-selection");
  }, [selection]);

  useEffect(() => {
    if (!resumeCue) return;
    const target = resumeCue.tokenId.startsWith("range-")
      ? document.querySelector<HTMLElement>(
          `[data-reading-paragraph][data-paragraph-id="${resumeCue.paragraphId}"]`,
        )
      : document.querySelector<HTMLElement>(
          `[data-token-id="${CSS.escape(resumeCue.tokenId)}"]`,
        );
    target?.classList.add("resume-location");
    const timer = window.setTimeout(() => {
      target?.classList.remove("resume-location");
      setResumeCue(null);
    }, 3600);
    return () => {
      window.clearTimeout(timer);
      target?.classList.remove("resume-location");
    };
  }, [resumeCue]);

  const selectElement = useCallback((element: HTMLElement) => {
    const word = element.dataset.displayWord || element.textContent || "";
    const key = element.dataset.wordKey || normalizeWord(word);
    const next: SelectionState = {
      word,
      key,
      tokenId: element.dataset.tokenId || `word-${Date.now()}`,
      paragraphId: element.dataset.paragraphId || "p0",
      sentence: element.dataset.sentence || element.closest("p")?.textContent || "",
      curated: Boolean(
        words[key]
        || activeLessonLexicon?.glance?.occurrences[element.dataset.tokenId || ""],
      ),
    };
    const rect = element.getBoundingClientRect();
    wordOrigin.current = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    const revealSelection = () => {
      if (!document.contains(element)) return;
      setWordMotion(panelMode === "word" ? "replace" : "open");
      setMeaningView(0);
      setSelection(next);
      setPanelMode("word");
      setDeepOpen(profile.explorationDepth === "Open deeply");
      window.getSelection()?.removeAllRanges();
    };
    if (!isPrivateReading && !activeLessonLexicon) {
      void loadLessonLexicon(reading.id).then((lexicon) => {
        if (!lexicon) {
          notify("That word pack could not be opened. The reading remains in place.");
          return;
        }
        setLessonLexiconState({ readingId: reading.id, lexicon });
        revealSelection();
      });
      return;
    }
    revealSelection();
  }, [activeLessonLexicon, isPrivateReading, notify, panelMode, profile.explorationDepth, reading.id, words]);

  useEffect(() => {
    if (!pendingLibraryAnchor) return;
    const sourceAnchor = pendingLibraryAnchor.sourceAnchor;
    const isPrivateAnchor = sourceAnchor.startsWith("private:");
    const parts = sourceAnchor.split(":");
    const sourceReadingId = isPrivateAnchor ? `private:${parts[1] || ""}` : parts[0] || reading.id;
    const sourceParagraphId = isPrivateAnchor ? parts[2] || "p0" : parts.slice(1).join(":") || "p0";
    if (sourceReadingId !== reading.id) return;

    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        const paragraph = document.querySelector<HTMLElement>(
          `[data-reading-paragraph][data-paragraph-id="${CSS.escape(sourceParagraphId)}"]`,
        );
        const element = paragraph?.querySelector<HTMLElement>(
          `[data-word-key="${CSS.escape(pendingLibraryAnchor.word)}"]`,
        );
        (element || paragraph)?.scrollIntoView({ block: "center", behavior: "auto" });
        if (element) {
          selectElement(element);
        } else {
          setWordMotion("open");
          setMeaningView(0);
          setSelection({
            word: pendingLibraryAnchor.displayWord,
            key: pendingLibraryAnchor.word,
            tokenId: `range-${Date.now()}`,
            paragraphId: sourceParagraphId,
            sentence: pendingLibraryAnchor.sourceSentence,
            curated: Boolean(findWordInsight(pendingLibraryAnchor.word)),
          });
          setPanelMode("word");
          setDeepOpen(false);
        }
        setPendingLibraryAnchor(null);
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [pendingLibraryAnchor, reading.id, selectElement]);

  const selectNativeRange = useCallback(() => {
    const nativeSelection = window.getSelection();
    if (!nativeSelection || nativeSelection.isCollapsed || !nativeSelection.rangeCount) {
      return false;
    }
    const text = nativeSelection.toString().trim().replace(/\s+/g, " ");
    if (!text || text.length > 160) return false;
    const range = nativeSelection.getRangeAt(0);
    const commonNode = range.commonAncestorContainer;
    const commonElement =
      commonNode.nodeType === Node.ELEMENT_NODE
        ? (commonNode as Element)
        : commonNode.parentElement;
    if (!commonElement || !storyRef.current?.contains(commonElement)) return false;
    const paragraph = commonElement.closest<HTMLElement>("[data-reading-paragraph]");
    const key = normalizeWord(text);
    const rect = range.getBoundingClientRect();
    wordOrigin.current = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    setWordMotion(panelMode === "word" ? "replace" : "open");
    setMeaningView(0);
    setSelection({
      word: text,
      key,
      tokenId: `range-${Date.now()}`,
      paragraphId: paragraph?.dataset.paragraphId || "p0",
      sentence: paragraph?.textContent?.trim() || text,
      curated: Boolean(words[key]),
    });
    setPanelMode("word");
    setDeepOpen(false);
    return true;
  }, [panelMode, words]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (pendingTap.current !== null) {
      window.clearTimeout(pendingTap.current);
      pendingTap.current = null;
    }
    document
      .querySelectorAll(".tap-acknowledged")
      .forEach((element) => element.classList.remove("tap-acknowledged"));
    pointerStart.current = {
      x: event.clientX,
      y: event.clientY,
      at: window.performance.now(),
      pointerType: event.pointerType,
    };
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    const nativeSelection = window.getSelection();
    if (nativeSelection && !nativeSelection.isCollapsed) return;
    const distance = Math.hypot(
      event.clientX - pointerStart.current.x,
      event.clientY - pointerStart.current.y,
    );
    const duration = window.performance.now() - pointerStart.current.at;
    if (distance > 10 || (event.pointerType === "touch" && duration > 280)) return;
    const element = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-selectable-word]",
    );
    if (!element) return;
    const tokenId = element.dataset.tokenId || "";
    const releasedAt = window.performance.now();
    if (
      event.pointerType === "touch"
      && lastTouchRelease.current.tokenId === tokenId
      && releasedAt - lastTouchRelease.current.at < 360
    ) {
      lastTouchRelease.current = { at: 0, tokenId: "" };
      element.classList.remove("tap-acknowledged");
      return;
    }
    if (event.pointerType === "touch") {
      lastTouchRelease.current = { at: releasedAt, tokenId };
    }
    element.classList.add("tap-acknowledged");
    const open = () => {
      element.classList.remove("tap-acknowledged");
      if (window.getSelection()?.isCollapsed === false) return;
      selectElement(element);
      pendingTap.current = null;
    };
    if (event.pointerType === "touch") {
      pendingTap.current = window.setTimeout(open, 160);
    } else {
      open();
    }
  };

  const handlePointerCancel = () => {
    if (pendingTap.current !== null) window.clearTimeout(pendingTap.current);
    pendingTap.current = null;
    document
      .querySelectorAll(".tap-acknowledged")
      .forEach((element) => element.classList.remove("tap-acknowledged"));
  };

  const handleStoryKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const element = (event.target as HTMLElement).closest<HTMLElement>(
      ".enriched-word[data-selectable-word]",
    );
    if (!element) return;
    event.preventDefault();
    selectElement(element);
  };

  const closeWord = () => {
    if (selection) {
      setResumeCue({
        tokenId: selection.tokenId,
        paragraphId: selection.paragraphId,
        word: selection.word,
      });
      window.localStorage.setItem(
        `verbalore-last-anchor:${reading.id}`,
        JSON.stringify({
          tokenId: selection.tokenId,
          paragraphId: selection.paragraphId,
          word: selection.word,
        }),
      );
    }
    setPanelMode("empty");
    setSelection(null);
    setDeepOpen(false);
    window.getSelection()?.removeAllRanges();
  };

  const chooseAdjacent = (direction: -1 | 1) => {
    if (!selection || selection.tokenId.startsWith("range-")) return;
    const nextTokenId = direction < 0 ? adjacentTokenIds.previous : adjacentTokenIds.next;
    if (!nextTokenId) return;
    const next = document.querySelector<HTMLElement>(
      `[data-token-id="${CSS.escape(nextTokenId)}"]`,
    );
    if (next) selectElement(next);
  };

  const returnToSelection = () => {
    if (!selection) return;
    const target = selection.tokenId.startsWith("range-")
      ? document.querySelector<HTMLElement>(
          `[data-reading-paragraph][data-paragraph-id="${selection.paragraphId}"]`,
        )
      : document.querySelector<HTMLElement>(
          `[data-token-id="${selection.tokenId}"]`,
        );
    target?.scrollIntoView({ block: "center", behavior: "smooth" });
    window.setTimeout(() => target?.focus({ preventScroll: true }), 450);
  };

  const resumeReading = () => {
    const target = document.querySelector<HTMLElement>(
      `[data-reading-paragraph][data-paragraph-id="${resumeParagraph || "p0"}"]`,
    );
    target?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  const hearWord = () => {
    if (isPrivateReading) {
      notify("Pronunciation providers are withheld for private passages in this alpha.");
      return;
    }
    if (!selection || !("speechSynthesis" in window)) {
      notify("Pronunciation audio is unavailable on this device.");
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(selection.word);
    const selectedVoice = speechVoices.find((voice) => voice.voiceURI === speechVoiceUri);
    if (selectedVoice) utterance.voice = selectedVoice;
    utterance.lang = selectedVoice?.lang || "en-GB";
    utterance.rate = 0.82;
    window.speechSynthesis.speak(utterance);
  };

  const chooseSpeechVoice = (voiceUri: string) => {
    setSpeechVoiceUri(voiceUri);
    window.localStorage.setItem("verbalore-pronunciation-voice", voiceUri);
  };

  const toggleReadingList = async (readingId: string) => {
    if (!currentUser) {
      requestSignIn("Sign in to keep a Reading list across devices.");
      return;
    }
    const exists = readingList.some((entry) => entry.readingId === readingId);
    try {
      const response = await fetch(
        exists ? `/api/reading-list?readingId=${encodeURIComponent(readingId)}` : "/api/reading-list",
        exists ? { method: "DELETE" } : {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ readingId }),
        },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Reading list could not be updated.");
      if (exists) {
        setReadingList((current) => current.filter((entry) => entry.readingId !== readingId));
        notify("Removed from Reading list.");
      } else {
        setReadingList((current) => [data.reading, ...current.filter((entry) => entry.readingId !== readingId)]);
        notify("Added to Reading list.");
      }
    } catch (error) {
      notify(error instanceof Error ? error.message : "Reading list could not be updated.");
    }
  };

  const requestSignIn = useCallback((reason?: string) => {
    setAuthError("");
    setSignInOpen(true);
    if (reason) notify(reason);
  }, [notify]);

  const loadLearningEvidence = useCallback(async () => {
    setLearningEvidenceLoading(true);
    setLearningEvidenceError("");
    try {
      const response = await fetch("/api/learning-evidence?weeks=8");
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Learning evidence could not be opened.");
      setLearningEvidence(data);
    } catch (error) {
      setLearningEvidenceError(error instanceof Error ? error.message : "Learning evidence could not be opened.");
    } finally {
      setLearningEvidenceLoading(false);
    }
  }, []);

  const openLearningSection = useCallback((section: LearningSection) => {
    if (experienceMode !== "learning") {
      const readingParagraphs = experienceMode === "read"
        ? Array.from(document.querySelectorAll<HTMLElement>("[data-reading-paragraph]"))
        : [];
      const readingLine = Math.min(window.innerHeight * 0.35, 320);
      const visibleParagraph = readingParagraphs.find((paragraph) => {
        const bounds = paragraph.getBoundingClientRect();
        return bounds.top <= readingLine && bounds.bottom > readingLine;
      }) || readingParagraphs.reduce<HTMLElement | null>((nearest, paragraph) => {
        if (!nearest) return paragraph;
        return Math.abs(paragraph.getBoundingClientRect().top - readingLine)
          < Math.abs(nearest.getBoundingClientRect().top - readingLine)
          ? paragraph
          : nearest;
      }, null);
      learningOriginRef.current = {
        view: showHome ? "home" : experienceMode === "practice" ? "practice" : "read",
        readingId: currentReadingId,
        paragraphId: visibleParagraph?.dataset.paragraphId || currentParagraph,
        paragraphTop: visibleParagraph?.getBoundingClientRect().top ?? null,
        practiceView,
        selection,
        deepOpen,
        panelMode,
      };
    }
    if (!currentUser) {
      intendedLearningSectionRef.current = section;
      requestSignIn("Sign in to continue to your private learning record.");
      return;
    }
    intendedLearningSectionRef.current = null;
    setAccountMenuOpen(false);
    setPlusOfferOpen(false);
    setDiscoveryOpen(false);
    setSelection(null);
    setDeepOpen(false);
    setShowHome(false);
    setLearningSection(section);
    setLibraryView(section === "words" ? "words" : section === "readings" ? "reading-list" : "home");
    setExperienceMode("learning");
    setPanelMode(
      section === "overview" || section === "readings" || section === "words"
        ? "library"
        : "profile",
    );
    const learningLocation = {
      ...shareableLocation,
      view: "learning",
      browse: false,
      learningSection: section,
    } satisfies ShareableLocation;
    if (experienceMode === "learning") {
      window.history.replaceState(
        { ...window.history.state, verbaloreView: "learning" },
        "",
        buildShareableHref(window.location.href, learningLocation),
      );
    } else {
      pushShareableLocation(learningLocation);
    }
    if ((section === "overview" || section === "progress") && !learningEvidence && !learningEvidenceLoading) {
      void loadLearningEvidence();
    }
    window.requestAnimationFrame(() => {
      document.getElementById("learning-destination-title")?.focus({ preventScroll: true });
      window.scrollTo({ top: 0, behavior: "auto" });
    });
  }, [
    currentParagraph,
    currentReadingId,
    currentUser,
    deepOpen,
    experienceMode,
    learningEvidence,
    learningEvidenceLoading,
    loadLearningEvidence,
    practiceView,
    panelMode,
    pushShareableLocation,
    requestSignIn,
    shareableLocation,
    showHome,
    selection,
  ]);

  const openLibrary = (view: LibraryView = "home") => {
    const section: LearningSection = view === "words" ? "words" : view === "home" ? "overview" : "readings";
    setLibraryView(view);
    openLearningSection(section);
  };

  const openLearningProfile = () => openLearningSection("profile");

  const returnFromLearning = useCallback(() => {
    const origin = learningOriginRef.current || {
      view: "read" as const,
      readingId: currentReadingId,
      paragraphId: currentParagraph,
      paragraphTop: null,
      practiceView,
      selection: null,
      deepOpen: false,
      panelMode: "empty" as PanelMode,
    };
    const nextView = origin.view;
    const next = {
      ...shareableLocation,
      view: nextView,
      workId: nextView === "read" && READING_BY_ID.has(origin.readingId) ? origin.readingId : null,
      browse: false,
      practiceView: origin.practiceView,
    } satisfies ShareableLocation;
    window.history.replaceState(
      { verbaloreView: nextView },
      "",
      buildShareableHref(window.location.href, next),
    );
    setPanelMode(
      nextView === "read" && origin.selection && origin.panelMode === "word"
        ? "word"
        : "empty",
    );
    setSelection(nextView === "read" ? origin.selection : null);
    setDeepOpen(nextView === "read" ? origin.deepOpen : false);
    setShowHome(nextView === "home");
    if (nextView !== "home") setExperienceMode(nextView);
    if (nextView === "read") {
      setCurrentReadingId(origin.readingId);
      window.setTimeout(() => {
        const paragraph = document.querySelector<HTMLElement>(
          `[data-reading-paragraph][data-paragraph-id="${CSS.escape(origin.paragraphId)}"]`,
        );
        if (!paragraph) return;
        if (origin.paragraphTop === null) {
          paragraph.scrollIntoView({ block: "center", behavior: "auto" });
          return;
        }
        window.scrollBy(0, paragraph.getBoundingClientRect().top - origin.paragraphTop);
      }, 0);
    } else if (nextView === "practice") {
      setPracticeView(origin.practiceView);
      window.scrollTo({ top: 0, behavior: "auto" });
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [currentParagraph, currentReadingId, practiceView, shareableLocation]);

  useEffect(() => {
    if (
      !currentUser
      || experienceMode !== "learning"
      || (learningSection !== "overview" && learningSection !== "progress")
      || learningEvidence
      || learningEvidenceLoading
    ) return;
    const timer = window.setTimeout(() => void loadLearningEvidence(), 0);
    return () => window.clearTimeout(timer);
  }, [currentUser, experienceMode, learningEvidence, learningEvidenceLoading, learningSection, loadLearningEvidence]);

  const openPlusMembership = () => {
    setAccountMenuOpen(false);
    if (entitlements.plan === "plus" && currentUser) {
      openLearningSection("membership");
      return;
    }
    setPlusOfferOpen(true);
  };

  const openAccountMenu = () => {
    if (!currentUser) {
      requestSignIn("Sign in to open your private learning profile.");
      return;
    }
    setAccountMenuOpen(true);
  };

  const clearLearnerState = useCallback((message: string) => {
    setCurrentUser(null);
    setProfile(DEFAULT_PROFILE);
    setSavedWords([]);
    setWarmWordItems([]);
    setSavedWordKeys([]);
    setSavedWordTotal(0);
    setWordResultTotal(0);
    setWordNextCursor(null);
    setWordAppliedSearch("");
    vocabularyPageRequest.current += 1;
    setWordPageLoading(false);
    loadedWordThreads.current.clear();
    setSavedContexts([]);
    setReadingList([]);
    setPrivateReadings([]);
    setPrivateReadingRecords({});
    setPracticeAttempts([]);
    setPracticeDraft("");
    setPracticeRevision("");
    setExpressionWork({});
    setWordReviews([]);
    setLearningEvidence(null);
    setLearningEvidenceError("");
    setLearningEvidenceLoading(false);
    setReviewWord(null);
    setResumeParagraph(null);
    setReadingProgresses({});
    setProgress(0);
    setSelection(null);
    setDeepOpen(false);
    setImportTitle("");
    setImportSource("");
    setImportAccepted(false);
    setConfirmDeleteImport(null);
    setAccountDeleteOpen(false);
    setAccountActionError("");
    setAccountAction("idle");
    if (currentReadingId.startsWith("private:")) {
      setCurrentReadingId(READINGS[0].id);
      window.localStorage.setItem("verbalore-current-reading", READINGS[0].id);
    }
    progressReady.current = false;
    setPanelMode("empty");
    notify(message);
  }, [currentReadingId, notify]);

  const handleGoogleCredential = useCallback(async (credential: string) => {
    setSigningIn(true);
    setAuthError("");
    try {
      const response = await fetch("/api/auth/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential }),
      });
      const data = await response.json();
      if (!response.ok || !data.user) {
        throw new Error(data.error || "Google sign-in could not be completed.");
      }
      setCurrentUser(data.user);
      setProfile((current) => ({
        ...current,
        id: data.user.id,
        displayName: data.user.displayName,
      }));
      await loadLearnerData();
      const intendedLearningSection = intendedLearningSectionRef.current;
      if (intendedLearningSection) {
        intendedLearningSectionRef.current = null;
        setShowHome(false);
        setLearningSection(intendedLearningSection);
        setLibraryView(intendedLearningSection === "words" ? "words" : intendedLearningSection === "readings" ? "reading-list" : "home");
        setExperienceMode("learning");
        setPanelMode(
          intendedLearningSection === "overview"
          || intendedLearningSection === "readings"
          || intendedLearningSection === "words"
            ? "library"
            : "profile",
        );
        pushShareableLocation({
          ...shareableLocation,
          view: "learning",
          browse: false,
          learningSection: intendedLearningSection,
        });
      }
      setSignInOpen(false);
      notify("Welcome. Verbalore can now remember your learning.");
    } catch (error) {
      setAuthError(
        error instanceof Error
          ? error.message
          : "Google sign-in could not be completed.",
      );
    } finally {
      setSigningIn(false);
    }
  }, [loadLearnerData, notify, pushShareableLocation, shareableLocation]);

  const signOut = async () => {
    try {
      const response = await fetch("/api/auth/logout", { method: "POST" });
      if (!response.ok) throw new Error("Sign out failed");
      clearLearnerState("Signed out. Reading remains open; private learning data stays in your account.");
    } catch {
      notify("Verbalore could not securely end this session yet. Please try again.");
    }
  };

  const exportLearningData = async () => {
    setAccountAction("exporting");
    setAccountActionError("");
    try {
      const response = await fetch("/api/account/export");
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Your learning export could not be prepared.");
      }
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `verbalore-learning-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
      notify("Your complete Verbalore learning export is ready.");
    } catch (error) {
      setAccountActionError(error instanceof Error ? error.message : "Your learning export could not be prepared.");
    } finally {
      setAccountAction("idle");
    }
  };

  const deleteAccountData = async () => {
    setAccountAction("deleting");
    setAccountActionError("");
    try {
      const response = await fetch("/api/account", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmation: ACCOUNT_DELETE_CONFIRMATION }),
      });
      const data = await response.json();
      if (!response.ok || data.deleted !== true) {
        throw new Error(data.error || "Your learning data could not be deleted.");
      }
      clearLearnerState("Your Verbalore learning data was deleted. Public reading remains available.");
    } catch (error) {
      setAccountActionError(error instanceof Error ? error.message : "Your learning data could not be deleted.");
    } finally {
      setAccountAction("idle");
    }
  };

  const saveWord = async () => {
    if (!selection || !insight) return;
    if (!currentUser) {
      requestSignIn("Sign in when you want Verbalore to remember a word.");
      return;
    }
    const sourceType = isPrivateReading ? "private_import" : "authored";
    const sourceId = isPrivateReading ? activePrivateId || "" : reading.id;
    const sourceSentence = isPrivateReading ? selection.sentence : insight.sentence;
    const existing = savedWords.find((word) => word.word === selection.key);
    const wasSaved = savedKeys.has(selection.key);
    const item: VocabularyItem = {
      word: selection.key,
      displayWord: selection.word,
      status: existing?.status || "Encountered",
      meaning: insight.meaning,
      sourceSentence,
      sourceAnchor: isPrivateReading
        ? `private:${sourceId}:${selection.paragraphId}`
        : `${sourceId}:${selection.paragraphId}`,
      lastOpenedSection: deepOpen ? "deep" : "glance",
    };
    setSavedWords((current) => [item, ...current.filter((word) => word.word !== item.word)]);
    setWarmWordItems((current) => [item, ...current.filter((word) => word.word !== item.word)].slice(0, 7));
    setSavedWordKeys((current) => [...new Set([...current, item.word])]);
    if (!wasSaved) {
      setSavedWordTotal((current) => current + 1);
      setWordResultTotal((current) => wordSearch.trim() ? current : current + 1);
    }
    const now = new Date().toISOString();
    const existingContext = savedContexts.find((context) =>
      context.word === item.word && context.sourceAnchor === item.sourceAnchor
    );
    const optimisticContext: VocabularyContext = {
      ...item,
      sourceType,
      sourceId,
      createdAt: existingContext?.createdAt || now,
      updatedAt: now,
    };
    let persisted = false;
    const rollbackOptimisticSave = () => {
      setSavedWords((current) => existing
        ? [existing, ...current.filter((word) => word.word !== item.word)]
        : current.filter((word) => word.word !== item.word));
      setWarmWordItems((current) => existing
        ? [existing, ...current.filter((word) => word.word !== item.word)].slice(0, 7)
        : current.filter((word) => word.word !== item.word));
      if (!wasSaved) {
        setSavedWordKeys((current) => current.filter((word) => word !== item.word));
        setSavedWordTotal((current) => Math.max(0, current - 1));
        setWordResultTotal((current) => wordSearch.trim() ? current : Math.max(0, current - 1));
      }
      setSavedContexts((current) => existingContext
        ? [existingContext, ...current.filter((context) => !(
          context.word === item.word && context.sourceAnchor === item.sourceAnchor
        ))]
        : current.filter((context) => !(
          context.word === item.word && context.sourceAnchor === item.sourceAnchor
        )));
    };
    setSavedContexts((current) => [
      optimisticContext,
      ...current.filter((context) => !(context.word === item.word && context.sourceAnchor === item.sourceAnchor)),
    ]);
    notify(wasSaved ? "This encounter joined the word’s context." : "Saved with its sentence. Continue whenever you like.");
    try {
      const response = await fetch("/api/vocabulary", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...item,
          sourceType,
          sourceId,
          paragraphId: selection.paragraphId,
        }),
      });
      if (response.status === 401) {
        rollbackOptimisticSave();
        setCurrentUser(null);
        requestSignIn("Your session ended. Sign in again to save this word.");
        return;
      }
      if (!response.ok) throw new Error("Save failed");
      persisted = true;
      const data = await response.json();
      if (data.item) {
        setSavedWords((current) => [
          data.item,
          ...current.filter((word) => word.word !== data.item.word),
        ]);
        setWarmWordItems((current) => [
          data.item,
          ...current.filter((word) => word.word !== data.item.word),
        ].slice(0, 7));
        loadedWordThreads.current.delete(data.item.word);
        const reviewResponse = await fetch("/api/review?limit=120");
        if (reviewResponse.ok) {
          const reviewData = await reviewResponse.json();
          if (reviewData.reviews) {
            setWordReviews(reviewData.reviews);
            setReviewWord((current) => current || data.item.word);
          }
        }
      }
    } catch {
      if (!persisted) {
        rollbackOptimisticSave();
        notify("That save did not reach your profile. Your reading place is unchanged; try again when ready.");
      } else {
        notify("The word is saved. Practice could not refresh yet.");
      }
    }
  };

  const removeWord = async (word: string) => {
    const removedItem = savedWords.find((item) => item.word === word);
    const wasKnown = savedKeys.has(word);
    let mayReload = true;
    setSavedWords((current) => current.filter((item) => item.word !== word));
    setWarmWordItems((current) => current.filter((item) => item.word !== word));
    setSavedWordKeys((current) => current.filter((item) => item !== word));
    if (wasKnown) {
      setSavedWordTotal((current) => Math.max(0, current - 1));
      setWordResultTotal((current) => Math.max(0, current - 1));
    }
    setSavedContexts((current) => current.filter((context) => context.word !== word));
    setWordReviews((current) => current.filter((review) => review.item.word !== word));
    setReviewWord((current) => current === word ? null : current);
    try {
      const response = await fetch(`/api/vocabulary?word=${encodeURIComponent(word)}`, {
        method: "DELETE",
      });
      if (response.status === 401) {
        mayReload = false;
        setCurrentUser(null);
        requestSignIn("Your session ended. Sign in again before removing this word.");
        throw new Error("Session ended");
      }
      if (!response.ok) throw new Error("Remove failed");
      loadedWordThreads.current.delete(word);
      void fetch("/api/vocabulary?limit=7")
        .then(async (warmResponse) => warmResponse.ok ? warmResponse.json() : null)
        .then((warmData) => {
          if (warmData?.vocabulary) setWarmWordItems(warmData.vocabulary);
        })
        .catch(() => undefined);
      notify("Removed from your library and review history.");
    } catch {
      if (removedItem) {
        setSavedWords((current) => [
          removedItem,
          ...current.filter((item) => item.word !== word),
        ]);
        setWarmWordItems((current) => [
          removedItem,
          ...current.filter((item) => item.word !== word),
        ].slice(0, 7));
      }
      if (wasKnown) {
        setSavedWordKeys((current) => [...new Set([...current, word])]);
        setSavedWordTotal((current) => current + 1);
        setWordResultTotal((current) => current + 1);
      }
      notify("That word could not be removed; your library has been restored.");
      if (mayReload && currentUser) await loadLearnerData();
    }
  };

  const openSavedWord = async (item: VocabularyItem) => {
    if (item.sourceAnchor.startsWith("command-vocabulary:")) {
      const sourceSenseId = item.sourceAnchor
        .slice("command-vocabulary:".length)
        .replace(/:p1$/, "");
      const entry = commandCatalogEntries.find((candidate) =>
        candidate.id === item.word || candidate.sourceSenseId === sourceSenseId
      );
      if (!entry) {
        notify("That maintained word lesson is not available yet. Your saved context remains safe.");
        return;
      }
      setCommandWordId(entry.id);
      setCommandRelatedWordId(null);
      setCommandWordQuery("");
      setPracticeView("discover");
      setExperienceMode("practice");
      setShowHome(false);
      setPanelMode("empty");
      pushShareableLocation({
        ...shareableLocation,
        view: "practice",
        browse: false,
        practiceView: "discover",
        wordId: entry.id,
        relatedWordId: null,
        wordQuery: "",
      });
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }
    if (item.sourceAnchor.startsWith("private:")) {
      const [, sourceId] = item.sourceAnchor.split(":");
      const opened = await openPrivateReading(sourceId);
      if (!opened) return;
      setPendingLibraryAnchor(item);
      return;
    }
    const separator = item.sourceAnchor.indexOf(":");
    const sourceReadingId = separator > 0 ? item.sourceAnchor.slice(0, separator) : reading.id;
    if (sourceReadingId !== reading.id && READING_BY_ID.has(sourceReadingId)) {
      const opened = await openReading(sourceReadingId);
      if (!opened) return;
    }
    setPendingLibraryAnchor(item);
  };

  const openImportDialog = () => {
    if (!currentUser) {
      requestSignIn("Sign in before bringing a private passage into Verbalore.");
      return;
    }
    setImportError("");
    setImportNeedsPlus(false);
    setImportOpen(true);
  };

  const savePrivateReading = async () => {
    if (!importAccepted || importSource.trim().length < 80) return;
    setSavingImport(true);
    setImportError("");
    setImportNeedsPlus(false);
    try {
      const response = await fetch("/api/imports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: importTitle,
          sourceText: importSource,
          privacyAccepted: importAccepted,
        }),
      });
      const data = await response.json();
      if (response.status === 401) {
        setCurrentUser(null);
        setImportOpen(false);
        requestSignIn("Your session ended. Sign in again before saving this passage.");
        return;
      }
      if (!response.ok || !data.reading) {
        setImportNeedsPlus(
          response.status === 409
          && entitlements.billingActive
          && !entitlements.capabilities.sustainedPrivateImports,
        );
        throw new Error(data.error || "Private passage could not be saved.");
      }
      const record = data.reading as PrivateReadingRecord;
      const { sourceText: _sourceText, ...summary } = record;
      void _sourceText;
      setPrivateReadings((current) => [
        summary,
        ...current.filter((item) => item.id !== record.id),
      ]);
      setImportTitle("");
      setImportSource("");
      setImportAccepted(false);
      setImportNeedsPlus(false);
      activatePrivateReading(record);
      notify("Private passage saved. Nothing was analysed automatically.");
    } catch (error) {
      setImportError(error instanceof Error ? error.message : "Private passage could not be saved.");
    } finally {
      setSavingImport(false);
    }
  };

  const deletePrivateReading = async (id: string) => {
    try {
      const response = await fetch(`/api/imports?id=${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Private passage could not be deleted.");
      setPrivateReadings((current) => current.filter((item) => item.id !== id));
      setPrivateReadingRecords((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
      setConfirmDeleteImport(null);
      if (currentReadingId === `private:${id}`) void openReading(READINGS[0].id);
      await loadLearnerData();
      setPanelMode("library");
      notify("Private passage and its derived private context were deleted.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Private passage could not be deleted.");
    }
  };

  const saveProfile = async () => {
    if (!currentUser) {
      requestSignIn("Sign in to create a private learning profile.");
      return;
    }
    try {
      const response = await fetch("/api/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profile),
      });
      if (response.status === 401) {
        setCurrentUser(null);
        requestSignIn("Your session ended. Sign in again to save these preferences.");
        return;
      }
      if (!response.ok) throw new Error("Profile save failed");
      const data = await response.json();
      if (data.profile) setProfile(data.profile);
      notify("Profile preferences saved.");
    } catch {
      notify("Preferences remain active; sync will retry later.");
    }
  };

  const refreshMembership = async () => {
    if (!currentUser || !membershipLicenseKey.trim()) return;
    setMembershipRefreshing(true);
    setMembershipMessage("");
    try {
      const response = await fetch("/api/membership/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ licenseKey: membershipLicenseKey.trim() }),
      });
      const data = await response.json();
      if (response.status === 401) {
        setCurrentUser(null);
        requestSignIn("Your session ended. Sign in again to refresh your membership.");
        return;
      }
      if (!response.ok || !data.entitlements) {
        throw new Error(data.error || "That purchase could not be verified yet.");
      }
      setEntitlements(data.entitlements as EntitlementSnapshot);
      setMembershipLicenseKey("");
      setMembershipMessage(
        data.entitlements.plan === "plus"
          ? "Your Verbalore Plus access is current."
          : "The purchase was checked. Plus is not active on it now.",
      );
    } catch (error) {
      setMembershipMessage(error instanceof Error ? error.message : "That purchase could not be verified yet.");
    } finally {
      setMembershipRefreshing(false);
    }
  };

  const toggleTheme = (event: ReactMouseEvent<HTMLButtonElement>) => {
    const source = event.currentTarget.getBoundingClientRect();
    document.documentElement.style.setProperty("--theme-origin-x", `${source.left + source.width / 2}px`);
    document.documentElement.style.setProperty("--theme-origin-y", `${source.top + source.height / 2}px`);
    const next = theme === "night" ? "paper" : "night";
    const transitionDuration = next === "paper" ? 1150 : 780;
    document.documentElement.style.setProperty("--theme-transition-duration", `${transitionDuration}ms`);
    document.documentElement.style.setProperty(
      "--theme-reveal-start-brightness",
      next === "paper" ? "0.72" : "1.08",
    );
    document.documentElement.style.setProperty(
      "--theme-reveal-mid-brightness",
      next === "paper" ? "0.9" : "1.03",
    );
    const applyTheme = () => {
      document.documentElement.dataset.theme = next;
      window.localStorage.setItem("verbalore-theme", next);
      setTheme(next);
    };
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      applyTheme();
      return;
    }

    const transitionDocument = document as Document & {
      startViewTransition?: (update: () => void) => { finished: Promise<void> };
    };
    if (transitionDocument.startViewTransition) {
      document.documentElement.classList.add("theme-view-transition");
      const transition = transitionDocument.startViewTransition(applyTheme);
      void transition.finished.finally(() => document.documentElement.classList.remove("theme-view-transition"));
      return;
    }

    document.documentElement.classList.add("theme-transition");
    window.requestAnimationFrame(applyTheme);
    window.setTimeout(() => document.documentElement.classList.remove("theme-transition"), transitionDuration + 70);
  };

  const captureVisibleReadingAnchor = () => {
    const paragraphs = Array.from(
      document.querySelectorAll<HTMLElement>("[data-reading-paragraph]"),
    );
    if (!paragraphs.length) return;
    const readingLine = Math.min(window.innerHeight * 0.35, 320);
    const visible = paragraphs.find((paragraph) => {
      const bounds = paragraph.getBoundingClientRect();
      return bounds.top <= readingLine && bounds.bottom > readingLine;
    });
    const anchor = visible || paragraphs.reduce((nearest, paragraph) => {
      const nearestDistance = Math.abs(nearest.getBoundingClientRect().top - readingLine);
      const paragraphDistance = Math.abs(paragraph.getBoundingClientRect().top - readingLine);
      return paragraphDistance < nearestDistance ? paragraph : nearest;
    });
    readingSizeAnchorRef.current = { element: anchor, top: anchor.getBoundingClientRect().top };
  };

  const chooseReadingSize = (next: ReadingSize) => {
    captureVisibleReadingAnchor();
    setReadingSize(next);
    window.localStorage.setItem("verbalore-reading-size", next);
  };

  const chooseReadingMeasure = (next: ReadingMeasure) => {
    captureVisibleReadingAnchor();
    setReadingMeasure(next);
    window.localStorage.setItem("verbalore-reading-measure", next);
  };

  const switchExperience = (mode: ExperienceMode) => {
    pushShareableLocation({
      ...shareableLocation,
      view: mode,
      browse: false,
    });
    setShowHome(false);
    setExperienceMode(mode);
    setPanelMode("empty");
    setSelection(null);
    setDeepOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const enterReadingRoom = () => {
    pushShareableLocation({
      ...shareableLocation,
      view: "read",
      workId: READING_BY_ID.has(currentReadingId) ? currentReadingId : READINGS[0].id,
      browse: false,
    });
    setShowHome(false);
    setExperienceMode("read");
    setPanelMode("empty");
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const browseReadingsFromHome = () => {
    const readLocation = {
      ...shareableLocation,
      view: "read",
      workId: READING_BY_ID.has(currentReadingId) ? currentReadingId : READINGS[0].id,
      browse: false,
    } satisfies ShareableLocation;
    window.history.replaceState(
      { verbaloreView: "read" },
      "",
      buildShareableHref(window.location.href, readLocation),
    );
    window.history.pushState(
      { verbaloreView: "read", verbaloreBrowseEntry: true },
      "",
      buildShareableHref(window.location.href, {
        ...readLocation,
        browse: true,
      }),
    );
    setShowHome(false);
    setExperienceMode("read");
    setPanelMode("empty");
    setDiscoveryOpen(true);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const enterPracticeRoom = (view?: PracticeView) => {
    const nextView = view || (practiceVisited && practiceView !== "overview" ? practiceView : "overview");
    pushShareableLocation({
      ...shareableLocation,
      view: "practice",
      browse: false,
      practiceView: nextView,
    });
    setShowHome(false);
    setPracticeView(nextView);
    setExperienceMode("practice");
    setPanelMode("empty");
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const enterNeedStudyFromHome = (studyId: string) => {
    const study = EXPRESSION_STUDY_BY_ID.get(studyId);
    if (!study) return;
    const stanceId = study.stances.some((stance) => stance.id === "tactful")
      ? "tactful"
      : study.stances[0]?.id || "direct";
    pushShareableLocation({
      ...shareableLocation,
      view: "practice",
      browse: false,
      practiceView: "expression",
      studyId: study.id,
      stanceId,
    });
    setActiveExpressionStudyId(study.id);
    setActiveStanceId(stanceId);
    window.localStorage.setItem("verbalore-expression-study", study.id);
    setShowHome(false);
    setPracticeView("expression");
    setExperienceMode("practice");
    setPanelMode("empty");
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const changeExpressionStudy = (nextIndex: number) => {
    const nextStudy = ALL_EXPRESSION_STUDIES[nextIndex];
    if (!nextStudy || nextStudy.id === expressionStudy.id) return;
    setExpressionWork((current) => ({
      ...current,
      [expressionStudy.id]: {
        draft: practiceDraft,
        revision: practiceRevision,
        checked: practiceChecked,
        stanceId: activeStance.id,
      },
    }));
    const restored = expressionWork[nextStudy.id];
    setActiveExpressionStudyId(nextStudy.id);
    setPracticeDraft(restored?.draft || "");
    setPracticeRevision(restored?.revision || "");
    setPracticeChecked(restored?.checked || false);
    setActiveStanceId(restored?.stanceId || "tactful");
    window.localStorage.setItem("verbalore-expression-study", nextStudy.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const returnHome = () => {
    pushShareableLocation({
      ...shareableLocation,
      view: "home",
      workId: null,
      browse: false,
    });
    setShowHome(true);
    setPanelMode("empty");
    setSelection(null);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const openPracticeView = (view: PracticeView) => {
    setPracticeView(view);
    if (view !== "overview") {
      setPracticeVisited(true);
      window.localStorage.setItem("verbalore-practice-visited", "1");
      window.localStorage.setItem("verbalore-last-practice-view", view);
    }
    setReviewChoice("");
    setReviewDraft("");
    setReviewSignals([]);
    setReviewFeedback("");
    pushShareableLocation({
      ...shareableLocation,
      view: "practice",
      browse: false,
      practiceView: view,
    });
    setShowHome(false);
    setExperienceMode("practice");
    setPanelMode("empty");
    setSelection(null);
    setDeepOpen(false);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const selectCommandWord = (id: string, relatedWordId: string | null = null) => {
    if (!commandCatalogById.has(id)) return;
    if (id === commandWordId) setCommandWordLoadAttempt((current) => current + 1);
    setCommandWordId(id);
    setCommandRelatedWordId(relatedWordId);
    const next = {
      ...shareableLocation,
      view: "practice",
      browse: false,
      practiceView: "discover",
      wordId: id,
      relatedWordId,
    } satisfies ShareableLocation;
    window.history.replaceState(
      { verbaloreView: "practice", verbaloreWord: id },
      "",
      buildShareableHref(window.location.href, next),
    );
  };

  const replaceCommandWordQuery = (query: string, nextActiveId: string) => {
    setCommandWordQuery(query);
    setCommandWordId(nextActiveId);
    setCommandRelatedWordId(null);
    const next = {
      ...shareableLocation,
      view: "practice",
      browse: false,
      practiceView: "discover",
      wordId: nextActiveId || null,
      relatedWordId: null,
      wordQuery: query,
    } satisfies ShareableLocation;
    window.history.replaceState(
      { verbaloreView: "practice", verbaloreWord: nextActiveId },
      "",
      buildShareableHref(window.location.href, next),
    );
  };

  const replaceCommandDiscoveryCriteria = (
    criteria: {
      handle: CommandVocabularyHandle | "all";
      level: CommandVocabularyCatalogEntry["difficulty"] | "all";
      register: CommandVocabularyRegister | "all";
      articulation: CommandVocabularyArticulation | "all";
    },
    nextActiveId: string,
  ) => {
    setCommandWordHandle(criteria.handle);
    setCommandWordLevel(criteria.level);
    setCommandWordRegister(criteria.register);
    setCommandWordArticulation(criteria.articulation);
    setCommandWordId(nextActiveId);
    setCommandRelatedWordId(null);
    const next = {
      ...shareableLocation,
      view: "practice",
      browse: false,
      practiceView: "discover",
      wordId: nextActiveId || null,
      relatedWordId: null,
      wordHandle: criteria.handle,
      wordLevel: criteria.level,
      wordRegister: criteria.register,
      wordArticulation: criteria.articulation,
    } satisfies ShareableLocation;
    window.history.replaceState(
      { verbaloreView: "practice", verbaloreWord: nextActiveId },
      "",
      buildShareableHref(window.location.href, next),
    );
  };

  const hearCommandWord = (entry: CommandVocabularyEntry) => {
    if (!("speechSynthesis" in window)) {
      notify("Pronunciation audio is unavailable on this device.");
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(entry.word);
    const selectedVoice = speechVoices.find((voice) => voice.voiceURI === speechVoiceUri);
    if (selectedVoice) utterance.voice = selectedVoice;
    utterance.lang = selectedVoice?.lang || "en-GB";
    utterance.rate = 0.82;
    window.speechSynthesis.speak(utterance);
  };

  const saveCommandWord = async (entry: CommandVocabularyEntry, continueToPractice = false) => {
    if (!currentUser) {
      requestSignIn("Sign in when you want Verbalore to remember a word.");
      return;
    }
    const commandVocabulary = entry.sourceReadingId.startsWith("command-vocabulary:");
    const sourceType = "authored" as const;
    const sourceId = commandVocabulary
      ? `command-vocabulary:${entry.dictionaryIdentity.sourceSenseId}`
      : entry.sourceReadingId;
    const wasSaved = savedKeys.has(entry.word);
    const existing = savedWords.find((word) => word.word === entry.word);
    const item: VocabularyItem = {
      word: entry.word,
      displayWord: entry.word,
      status: existing?.status || "Encountered",
      meaning: entry.meaning,
      sourceSentence: entry.sourceSentence,
      sourceAnchor: commandVocabulary
        ? `${sourceId}:${entry.sourceParagraphId}`
        : `${entry.sourceReadingId}:${entry.sourceParagraphId}`,
      lastOpenedSection: "word-discovery",
    };
    const now = new Date().toISOString();
    const existingContext = savedContexts.find((context) =>
      context.word === item.word && context.sourceAnchor === item.sourceAnchor
    );
    const optimisticContext: VocabularyContext = {
      ...item,
      sourceType,
      sourceId,
      createdAt: existingContext?.createdAt || now,
      updatedAt: now,
    };
    let persisted = false;
    const rollbackOptimisticSave = () => {
      setSavedWords((current) => existing
        ? [existing, ...current.filter((word) => word.word !== item.word)]
        : current.filter((word) => word.word !== item.word));
      setWarmWordItems((current) => existing
        ? [existing, ...current.filter((word) => word.word !== item.word)].slice(0, 7)
        : current.filter((word) => word.word !== item.word));
      if (!wasSaved) {
        setSavedWordKeys((current) => current.filter((word) => word !== item.word));
        setSavedWordTotal((current) => Math.max(0, current - 1));
        setWordResultTotal((current) => wordSearch.trim() ? current : Math.max(0, current - 1));
      }
      setSavedContexts((current) => existingContext
        ? [existingContext, ...current.filter((context) => !(
          context.word === item.word && context.sourceAnchor === item.sourceAnchor
        ))]
        : current.filter((context) => !(
          context.word === item.word && context.sourceAnchor === item.sourceAnchor
        )));
    };
    setSavedWords((current) => [item, ...current.filter((word) => word.word !== item.word)]);
    setWarmWordItems((current) => [item, ...current.filter((word) => word.word !== item.word)].slice(0, 7));
    setSavedWordKeys((current) => [...new Set([...current, item.word])]);
    setSavedContexts((current) => [
      optimisticContext,
      ...current.filter((context) => !(context.word === item.word && context.sourceAnchor === item.sourceAnchor)),
    ]);
    if (!wasSaved) {
      setSavedWordTotal((current) => current + 1);
      setWordResultTotal((current) => wordSearch.trim() ? current : current + 1);
    }
    notify(wasSaved ? "This encounter joined the word’s context." : "Added to My Words with its reading context.");
    try {
      const response = await fetch("/api/vocabulary", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...item,
          sourceType,
          sourceId,
          paragraphId: entry.sourceParagraphId,
        }),
      });
      if (response.status === 401) {
        rollbackOptimisticSave();
        setCurrentUser(null);
        requestSignIn("Your session ended. Sign in again to save this word.");
        return;
      }
      if (!response.ok) throw new Error("Save failed");
      persisted = true;
      const data = await response.json();
      if (data.item) {
        setSavedWords((current) => [data.item, ...current.filter((word) => word.word !== data.item.word)]);
        loadedWordThreads.current.delete(data.item.word);
      }
      const reviewResponse = await fetch("/api/review?limit=120");
      if (reviewResponse.ok) {
        const reviewData = await reviewResponse.json();
        if (reviewData.reviews) {
          setWordReviews(reviewData.reviews);
          setReviewWord(entry.word);
        }
      }
      if (continueToPractice) openPracticeView("words");
    } catch {
      if (!persisted) {
        rollbackOptimisticSave();
        notify("That save did not reach your profile. The lesson remains open; try again when ready.");
      } else {
        notify("The word is saved. Practice could not refresh yet.");
      }
    }
  };

  const chooseReviewWord = (word: string) => {
    setReviewWord(word);
    setReviewChoice("");
    setReviewDraft("");
    setReviewSignals([]);
    setReviewFeedback("");
  };

  const submitWordReview = async () => {
    if (!currentUser) {
      requestSignIn("Sign in to preserve evidence of the words you are learning.");
      return;
    }
    if (!currentReview || !currentReview.prompt) return;
    const issued = currentReview.prompt;
    if (issued.type === "check" && !reviewChoice) {
      notify("Choose the meaning that feels most exact.");
      return;
    }
    if ((issued.type === "use" || issued.type === "natural") && !reviewDraft.trim()) {
      notify(issued.type === "use" ? "Write one original use first." : "Record where the word entered real life.");
      return;
    }
    setSavingReview(true);
    setReviewFeedback("");
    try {
      const response = await fetch("/api/review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          word: currentReview.item.word,
          promptId: issued.id,
          senseId: issued.senseId,
          submissionId: crypto.randomUUID(),
          answer: reviewChoice,
          response: reviewDraft,
          signals: reviewSignals,
        }),
      });
      if (response.status === 401) {
        setCurrentUser(null);
        requestSignIn("Your session ended. Sign in again to preserve this review.");
        return;
      }
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 409) await loadLearnerData();
        throw new Error(data.error || "Review could not be saved.");
      }
      setWordReviews((current) => current.map((review) =>
        review.item.word === currentReview.item.word
          ? { ...review, item: data.item, authored: data.authored, prompt: data.prompt, evidence: data.evidence }
          : review,
      ));
      setSavedWords((current) => current.map((item) =>
        item.word === data.item.word ? data.item : item,
      ));
      const outcome = data.attempt?.outcome;
      const statusChanged = data.item.status !== currentReview.item.status;
      setReviewFeedback(
        outcome === "not_yet"
          ? data.correction
            ? `Not yet. The authored answer is “${data.correction}”. Nothing has been lost; this is the evidence that decides what comes next.`
            : "Not yet. Revisit the criteria and keep the sentence on screen; an honest pause is more useful than a false success."
          : statusChanged
            ? `${currentReview.item.displayWord} is now ${data.item.status}. The change came from evidence, not a manual badge.`
            : issued.type === "natural"
              ? "Natural use recorded. Verbalore keeps the context, not a streak."
              : data.receipt?.claim || "Evidence recorded. The next step has changed with it.",
      );
      setReviewChoice("");
      setReviewDraft("");
      setReviewSignals([]);
    } catch (error) {
      setReviewFeedback(error instanceof Error ? error.message : "Review could not be saved.");
    } finally {
      setSavingReview(false);
    }
  };

  const demoteCurrentWord = async () => {
    if (!currentReview || currentReview.evidence.status === "Encountered") return;
    const nextStatus = currentReview.evidence.status === "Owned" ? "Active" : "Encountered";
    try {
      const response = await fetch("/api/review", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ word: currentReview.item.word, status: nextStatus }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Status could not be changed.");
      setWordReviews((current) => current.map((review) =>
        review.item.word === currentReview.item.word
          ? { ...review, item: data.item, authored: data.authored, prompt: data.prompt, evidence: data.evidence }
          : review,
      ));
      setSavedWords((current) => current.map((item) =>
        item.word === data.item.word ? data.item : item,
      ));
      setReviewFeedback(`Moved back to ${nextStatus}. Earlier evidence remains visible in history, but new evidence must rebuild command.`);
    } catch (error) {
      setReviewFeedback(error instanceof Error ? error.message : "Status could not be changed.");
    }
  };

  const savePractice = async () => {
    if (!practiceDraft.trim()) {
      notify("Write a first attempt before saving it.");
      return;
    }
    if (!currentUser) {
      requestSignIn("Sign in only when you want this attempt preserved.");
      return;
    }
    setSavingPractice(true);
    try {
      const response = await fetch("/api/practice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          promptId: expressionStudy.id,
          draft: practiceDraft,
          revision: practiceRevision,
          assistanceMode: profile.assistanceMode,
          signals: practiceAssessment.status === "assessed"
            ? [
                `stance:${activeStance.id}`,
                ...practiceSignals.filter((signal) => signal.met).map((signal) => signal.id),
              ]
            : [`stance:${activeStance.id}`],
        }),
      });
      if (response.status === 401) {
        setCurrentUser(null);
        requestSignIn("Your session ended. Sign in again to preserve this attempt.");
        return;
      }
      if (!response.ok) throw new Error("Practice save failed");
      const data = await response.json();
      if (data.attempt) {
        setPracticeAttempts((current) => [data.attempt, ...current]);
      }
      notify("Attempt saved. Your first wording and revision remain together.");
    } catch {
      notify("The attempt remains on screen, but could not be saved yet.");
    } finally {
      setSavingPractice(false);
    }
  };

  const reopenPractice = (attempt: PracticeAttempt) => {
    const attemptStudy = EXPRESSION_STUDY_BY_ID.get(attempt.promptId);
    if (attemptStudy) {
      setExpressionWork((current) => ({
        ...current,
        [expressionStudy.id]: {
          draft: practiceDraft,
          revision: practiceRevision,
          checked: practiceChecked,
          stanceId: activeStance.id,
        },
      }));
      setActiveExpressionStudyId(attemptStudy.id);
      window.localStorage.setItem("verbalore-expression-study", attemptStudy.id);
    }
    try {
      const signals = JSON.parse(attempt.signals) as string[];
      const stance = signals.find((signal) => signal.startsWith("stance:"))?.slice("stance:".length);
      if (stance && attemptStudy?.stances.some((candidate) => candidate.id === stance)) setActiveStanceId(stance);
    } catch {
      setActiveStanceId("tactful");
    }
    setPracticeDraft(attempt.draft);
    setPracticeRevision(attempt.revision || "");
    setPracticeChecked(true);
    document.getElementById("practice-editor")?.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  const removePractice = async (id: number) => {
    setPracticeAttempts((current) => current.filter((attempt) => attempt.id !== id));
    await fetch(`/api/practice?id=${id}`, { method: "DELETE" }).catch(() => undefined);
  };

  const evidenceWeekMax = Math.max(1, ...(learningEvidence?.weeks || []).map((week) =>
    week.firstChecks + week.delayedChecks + week.originalUses + week.naturalUses + week.notYet + week.demotions));
  const evidenceChecks = learningEvidence
    ? learningEvidence.totals.firstChecks + learningEvidence.totals.delayedChecks
    : 0;
  const evidenceUses = learningEvidence
    ? learningEvidence.totals.originalUses + learningEvidence.totals.naturalUses
    : 0;
  const evidenceCorrections = learningEvidence
    ? learningEvidence.totals.notYet + learningEvidence.totals.demotions
    : 0;
  const evidenceEventsInWindow = evidenceChecks + evidenceUses + evidenceCorrections;
  const practiceMap = (
    <nav className="practice-map" aria-label="Choose a way to practise">
      <button type="button" className={practiceView === "overview" ? "active" : ""} aria-current={practiceView === "overview" ? "page" : undefined} onClick={() => openPracticeView("overview")}>Overview</button>
      <button type="button" className={practiceView === "discover" ? "active" : ""} aria-current={practiceView === "discover" ? "page" : undefined} onClick={() => openPracticeView("discover")}>Learn a useful word</button>
      <button type="button" className={practiceView === "words" ? "active" : ""} aria-current={practiceView === "words" ? "page" : undefined} onClick={() => openPracticeView("words")}>Practise My Words</button>
      <button type="button" className={practiceView === "expression" ? "active" : ""} aria-current={practiceView === "expression" ? "page" : undefined} onClick={() => openPracticeView("expression")}>Expression Studio</button>
    </nav>
  );
  const learningNavigation = (
    <nav className="learning-central-nav" aria-label="My learning sections">
      {([
        ["overview", "Overview"],
        ["readings", "Readings"],
        ["words", "My Words"],
        ["expression", "My expression"],
        ["progress", "Progress"],
        ["profile", "Preferences"],
        ["membership", "Membership"],
        ["data", "Data and privacy"],
      ] as const).map(([section, label]) => (
        <button
          key={section}
          type="button"
          className={learningSection === section ? "active" : ""}
          aria-current={learningSection === section ? "page" : undefined}
          onClick={() => openLearningSection(section)}
        >{label}</button>
      ))}
    </nav>
  );
  const accountMenu = accountMenuOpen && currentUser ? (
    <div className="account-menu-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) setAccountMenuOpen(false);
    }}>
      <section
        ref={accountMenuRef}
        className="account-menu"
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-menu-title"
        tabIndex={-1}
      >
        <header>
          <span className="avatar">{profile.displayName.slice(0, 1).toUpperCase()}</span>
          <div>
            <strong id="account-menu-title">{profile.displayName}</strong>
            <small>{currentUser.email}</small>
          </div>
          <button type="button" onClick={() => setAccountMenuOpen(false)} aria-label="Close account menu">×</button>
        </header>
        <nav aria-label="Account options">
          <button type="button" onClick={openLearningProfile}>
            <span>Profile and preferences</span><small>Language, assistance and reading appearance</small>
          </button>
          <button type="button" onClick={() => { setAccountMenuOpen(false); openLibrary(); }}>
            <span>My learning</span><small>Words, reading list and private passages</small>
          </button>
          <button type="button" onClick={openPlusMembership}>
            <span>{entitlements.plan === "plus" ? "Your Verbalore Plus" : "Explore Verbalore Plus"}</span><small>Membership and purchase recovery</small>
          </button>
          <button type="button" onClick={(event) => { setAccountMenuOpen(false); toggleTheme(event); }}>
            <span>Appearance</span><small>Switch to {theme === "night" ? "light" : "dark"} reading</small>
          </button>
        </nav>
        <button className="account-menu-sign-out" type="button" onClick={() => { setAccountMenuOpen(false); void signOut(); }}>
          Sign out of this account
        </button>
      </section>
    </div>
  ) : null;
  const plusOfferDialog = plusOfferOpen ? (
    <div className="plus-offer-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) setPlusOfferOpen(false);
    }}>
      <section
        ref={plusOfferDialogRef}
        className="plus-offer-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="plus-offer-title"
        tabIndex={-1}
      >
        <button className="plus-offer-close" type="button" onClick={() => setPlusOfferOpen(false)} aria-label="Close Verbalore Plus">×</button>
        <span className="section-kicker">Verbalore Plus</span>
        <h2 id="plus-offer-title">Keep more of the reading that matters to you.</h2>
        <p className="plus-offer-lead">Continue beyond the complete Free foundation into the growing command-vocabulary programme, and bring more private reading into the Verbalore experience.</p>
        <div className="plus-offer-visual" aria-label="A Verbalore reading opening into a Glance">
          <article className="plus-offer-reading-preview">
            <span>From the reading</span>
            <p>“She did not raise her voice. She merely gave the silence the <mark>finality</mark> of a locked door.”</p>
            <small>Keep the sentence.</small>
          </article>
          <aside className="plus-offer-glance-preview">
            <span>Glance</span>
            <strong>finality</strong>
            <p>The quality of being settled and impossible to change.</p>
          </aside>
        </div>
        <div className="plus-offer-price"><strong>$9</strong><span>monthly</span><i>or</i><strong>$79</strong><span>yearly</span></div>
        <div className="plus-offer-comparison" aria-label="Compare Free and Verbalore Plus">
          <div className="plus-comparison-row plus-comparison-heading" aria-hidden="true">
            <span>What you get</span>
            <strong>Free</strong>
            <strong>Plus</strong>
          </div>
          {[
            ["Every current public reading", "Included", "Included"],
            ["Every word, with Glance and Explore", "Included", "Included"],
            ["Standalone command vocabulary", "50 complete words", `${commandCatalogEntries.length} maintained now`],
            ["Saved words, practice, reading list, and learning evidence", "Included", "Included"],
            ["Complete private-passage reading", "1 passage", "Up to 50"],
            ["Readable and exportable learning after cancellation", "Included", "Included"],
          ].map(([feature, free, plus]) => (
            <div className="plus-comparison-row" key={feature}>
              <span>{feature}</span>
              <span className="plus-comparison-value"><b aria-hidden="true">✓</b>{free}</span>
              <span className="plus-comparison-value plus-comparison-value-paid"><b aria-hidden="true">✓</b>{plus}</span>
            </div>
          ))}
        </div>
        <div className="plus-offer-actions">
          {entitlements.plan === "plus" && entitlements.commerce.manageUrl ? (
            <a href={entitlements.commerce.manageUrl}>Manage billing on Gumroad</a>
          ) : entitlements.commerce.purchasesEnabled && entitlements.commerce.checkoutUrl ? (
            <a href={entitlements.commerce.checkoutUrl}>Proceed to buy</a>
          ) : <p role="status">Plus purchasing is not available yet. Your complete Free experience remains open.</p>}
        </div>
        <small>Purchase securely on Gumroad. Afterwards, sign in to Verbalore with the same email used at checkout.</small>
      </section>
    </div>
  ) : null;
  const authDialog = signInOpen ? (
    <div className="auth-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !signingIn) setSignInOpen(false);
    }}>
      <section ref={signInDialogRef} className="auth-dialog" role="dialog" aria-modal="true" aria-labelledby="auth-title" tabIndex={-1}>
        <button className="auth-close" type="button" onClick={() => setSignInOpen(false)} disabled={signingIn} aria-label="Close sign in">×</button>
        <VerbaloreMark className="auth-mark" />
        {signingIn ? (
          <div className="auth-return-state" role="status" aria-live="polite">
            <span className="section-kicker">{currentUser ? "Secure session confirmed" : "Google response received"}</span>
            <h2 id="auth-title">You’re back.</h2>
            <span className="auth-return-check" aria-hidden="true">✓</span>
            <p>{currentUser
              ? "You are signed in. Opening your private learning profile now."
              : "Google has returned you to Verbalore. We are securely confirming your account now."}</p>
            <div className="auth-return-progress" aria-hidden="true"><i /></div>
            <small>This usually takes only a moment.</small>
          </div>
        ) : (
          <>
            <span className="section-kicker">Your place, kept privately</span>
            <h2 id="auth-title">Read freely.<br />Sign in to keep what you learn.</h2>
            <p>The story and every word universe remain open. Google sign-in is used only to keep your progress, vocabulary, preferences, and practice across visits.</p>
            {googleClientId ? (
              <GoogleSignIn clientId={googleClientId} onCredential={handleGoogleCredential} />
            ) : authReady ? (
              <p className="auth-configuration">Google sign-in is being connected. Reading remains available in the meantime.</p>
            ) : (
              <p className="auth-configuration">Preparing secure sign-in…</p>
            )}
            {authError ? <p className="auth-error" role="alert">{authError}</p> : null}
            <p className="auth-privacy">Verbalore receives your basic Google identity for account access. It does not receive your password, Drive files, contacts, or email contents.</p>
          </>
        )}
      </section>
    </div>
  ) : null;

  if (showHome) {
    return (
      <>
        <HomePage
          continueTitle={reading.title}
          plusActive={entitlements.plan === "plus"}
          accountLabel={!authReady || loadingProfile ? "Opening…" : currentUser ? profile.displayName : "Sign in"}
          accountInitial={currentUser ? profile.displayName.slice(0, 1).toUpperCase() : "V"}
          accountSignedIn={Boolean(currentUser)}
          onBegin={enterReadingRoom}
          onBrowse={browseReadingsFromHome}
          onPlus={openPlusMembership}
          onAccount={openAccountMenu}
          theme={theme}
          onTheme={toggleTheme}
          onOpenNeed={enterNeedStudyFromHome}
          onOpenReading={(readingId) => { void openReading(readingId); }}
          onPractice={enterPracticeRoom}
        />
        {accountMenu}
        {authDialog}
        {plusOfferDialog}
        {toast ? <div className="toast" role="status">{toast}</div> : null}
      </>
    );
  }

  return (
    <main className={`app-shell mode-${experienceMode}`} data-reading-size={readingSize} data-reading-measure={readingMeasure}>
      <header className="topbar">
        <button className="brand brand-button" type="button" onClick={returnHome} aria-label="Verbalore home">
          <VerbaloreMark className="brand-mark" />
          <span>
            <strong>Verbalore</strong>
            <small>The Weight of Words</small>
          </span>
        </button>
        <nav className="experience-switch" aria-label="Learning world">
          <button type="button" className={experienceMode === "read" ? "active" : ""} onClick={() => experienceMode === "learning" ? returnFromLearning() : switchExperience("read")}>
            <span>Read</span><small>Encounter language</small>
          </button>
          <button type="button" className={experienceMode === "practice" ? "active" : ""} onClick={() => enterPracticeRoom()}>
            <span>Practise</span><small>Command language</small>
          </button>
          <button type="button" className={experienceMode === "learning" ? "active" : ""} onClick={() => openLearningSection("overview")} aria-label="Open My learning">
            <span><span className="learning-switch-long">My learning</span><span className="learning-switch-short" aria-hidden="true">Mine</span></span><small>Your record</small>
          </button>
        </nav>
        <div className="topbar-actions">
          <button
            className="topbar-choose-button"
            type="button"
            onClick={openDiscovery}
            aria-label="Choose a reading"
          >
            <span className="topbar-choose-long">Choose a reading</span>
            <span className="topbar-choose-short" aria-hidden="true">Choose</span>
          </button>
          <button
            className={`plus-access-button${entitlements.plan === "plus" ? " active" : ""}`}
            type="button"
            onClick={openPlusMembership}
            aria-label={entitlements.plan === "plus" ? "Open Verbalore Plus membership" : "Get Verbalore Plus"}
          >
            <span className="plus-access-long">{entitlements.plan === "plus" ? "Verbalore Plus" : "Get Verbalore Plus"}</span>
            <span className="plus-access-short" aria-hidden="true">{entitlements.plan === "plus" ? "Your Plus" : "Get Plus"}</span>
          </button>
          <button className="mobile-library-button" type="button" onClick={() => openLibrary()} aria-label="Open my learning">
            <span aria-hidden="true">◇</span>
            {savedWordTotal ? <small>{savedWordTotal > 99 ? "99+" : savedWordTotal}</small> : null}
          </button>
          <button className="icon-button" type="button" onClick={toggleTheme} aria-label={theme === "night" ? "Switch to light theme" : "Switch to dark theme"}>
            <span className="theme-icon theme-icon-paper" aria-hidden="true">☾</span>
            <span className="theme-icon theme-icon-night" aria-hidden="true">☀</span>
          </button>
          <button
            className="profile-button"
            type="button"
            onClick={openAccountMenu}
            aria-label={currentUser ? "Open account menu" : "Sign in with Google"}
          >
            <span className="avatar">{currentUser ? profile.displayName.slice(0, 1).toUpperCase() : "V"}</span>
            <span>{!authReady || loadingProfile ? "Opening…" : currentUser ? profile.displayName : "Sign in"}</span>
          </button>
        </div>
      </header>

      {experienceMode === "read" ? (
        <div className="mobile-reading-progress" aria-label={`${progress}% read`}>
          <span style={{ width: `${progress}%` }} />
          <strong>{progress}%</strong>
        </div>
      ) : null}

      <div className="workspace">
        <aside className="lesson-rail" aria-label="Lesson navigation">
          {experienceMode === "read" ? (
            <button
              className="reading-summary-button"
              type="button"
              onClick={openDiscovery}
              aria-label={`Choose a reading. Current reading: ${reading.title}`}
            >
              <span><span className="lesson-label">Current reading</span><small>Choose ↗</small></span>
              <h2>{reading.title}</h2>
            </button>
          ) : experienceMode === "learning" ? (
            <>
              <div className="lesson-label">My learning</div>
              <h2>{learningSection === "overview" ? "Where do I continue?" : learningSection === "readings" ? "My readings" : learningSection === "words" ? "My Words" : learningSection === "expression" ? "My expression" : learningSection === "progress" ? "Progress and history" : learningSection === "profile" ? "Profile and preferences" : learningSection === "membership" ? "Membership and billing" : "Data and privacy"}</h2>
            </>
          ) : (
            <>
              <div className="lesson-label">{practiceView === "overview" ? "Practise" : practiceView === "discover" ? "Learn a useful word" : practiceView === "words" ? "Practise My Words" : "Expression Studio"}</div>
              <h2>{practiceView === "overview" ? "Choose one useful job." : practiceView === "discover" ? activeCommandCatalogEntry?.word : practiceView === "words" ? "Encountered. Active. Owned." : expressionStudy.title}</h2>
            </>
          )}
          <p>{experienceMode === "learning" ? "Everything you deliberately kept, with a clear way back." : experienceMode === "read" ? reading.whyItFits : practiceView === "overview" ? "Learn a word, practise one you saved, or compare how an intention lands." : practiceView === "discover" ? activeCommandWord?.whyWorthHaving || "One maintained word at a time, opened only when you choose it." : practiceView === "words" ? "Recognition, original use, later recall, and life beyond the exercise." : expressionStudy.skill}</p>
          {experienceMode === "learning" ? null : experienceMode === "read" ? <div className="progress-block" aria-label={`${progress}% read`}>
            <div className="progress-track"><span style={{ width: `${progress}%` }} /></div>
            <div><strong>{progress}%</strong><span>read</span></div>
          </div> : <div className="practice-count"><strong>{practiceView === "overview" ? 3 : practiceView === "discover" ? commandCatalogEntries.length : practiceView === "words" ? dueReviewCount : practiceAttempts.length}</strong><span>{practiceView === "overview" ? "ways to practise" : practiceView === "discover" ? "maintained words" : practiceView === "words" ? "reviews ready" : "attempts preserved"}</span></div>}
          {experienceMode === "read" && resumeParagraph && progress > 6 ? (
            <button className="resume-button" type="button" onClick={resumeReading}>
              Resume where I left off
            </button>
          ) : null}
          <nav className="rail-nav">
            <button className={experienceMode === "read" ? "active" : ""} type="button" onClick={() => experienceMode === "learning" ? returnFromLearning() : switchExperience("read")}>
              <span>01</span> Reading
            </button>
            <button className={experienceMode === "practice" && practiceView === "overview" ? "active" : ""} type="button" onClick={() => openPracticeView("overview")}>
              <span>02</span> Practice overview
            </button>
            <button className={experienceMode === "practice" && practiceView === "discover" ? "active" : ""} type="button" onClick={() => openPracticeView("discover")}>
              <span>03</span> Learn a useful word
            </button>
            <button className={experienceMode === "practice" && practiceView === "words" ? "active" : ""} type="button" onClick={() => openPracticeView("words")}>
              <span>04</span> Practise My Words
            </button>
            <button className={experienceMode === "practice" && practiceView === "expression" ? "active" : ""} type="button" onClick={() => openPracticeView("expression")}>
              <span>05</span> Expression Studio
            </button>
            <button className={experienceMode === "learning" ? "active" : ""} type="button" onClick={() => openLibrary()}>
              <span>◇</span> My learning
            </button>
            <button type="button" onClick={openLearningProfile}>
              <span>Aa</span> Learning profile
            </button>
          </nav>
          <div className="rail-note">
            <span aria-hidden="true">✦</span>
            <p>{experienceMode === "read" ? isPrivateReading ? <><strong>Private by design.</strong> This passage is not analysed or sent to an enrichment provider automatically.</> : <><strong>No detour is lost.</strong> Save a word now and return to its exact sentence later.</> : practiceView === "discover" ? <><strong>Breadth without a sack.</strong> One useful word at a time, with search only when you ask for it.</> : practiceView === "words" ? <><strong>No false mastery.</strong> A word changes state only when recall and use leave evidence.</> : <><strong>Your first wording matters.</strong> Compare it with the revision; do not erase the evidence of learning.</>}</p>
          </div>
        </aside>

        {experienceMode === "read" ? <section className="reading-stage" id="lesson-start">
          <div className="reading-tools">
            <span><i /> {isPrivateReading ? "Private passage" : "Enriched word"}</span>
            <span>{isPrivateReading ? "Nothing analysed automatically" : "Select any word or phrase"}</span>
            <details className="reading-size-settings" ref={readingSizeMenuRef}>
              <summary aria-label={`Reading preferences. Text size ${readingSize}; width ${readingMeasure}.`} title="Reading preferences">Aa</summary>
              <div className="reading-preferences-popover">
                <section role="group" aria-label="Choose reading text size">
                  <strong>Text size</strong>
                  <div>
                    {([
                      ["compact", "Compact", "A"],
                      ["balanced", "Balanced", "A"],
                      ["generous", "Generous", "A"],
                    ] as const).map(([value, label, glyph]) => (
                      <button
                        key={value}
                        type="button"
                        className={readingSize === value ? "active" : ""}
                        aria-pressed={readingSize === value}
                        onClick={() => chooseReadingSize(value)}
                      >
                        <span aria-hidden="true">{glyph}</span><small>{label}</small>
                      </button>
                    ))}
                  </div>
                </section>
                <section className="reading-measure-options" role="group" aria-label="Choose desktop reading width">
                  <strong>Reading width</strong>
                  <div>
                    {([
                      ["focused", "Focused", "50 ch"],
                      ["balanced", "Balanced", "62 ch"],
                      ["spacious", "Spacious", "72 ch"],
                    ] as const).map(([value, label, detail]) => (
                      <button
                        key={value}
                        type="button"
                        className={readingMeasure === value ? "active" : ""}
                        aria-pressed={readingMeasure === value}
                        onClick={() => chooseReadingMeasure(value)}
                      >
                        <span aria-hidden="true">{value === "focused" ? "▰" : value === "balanced" ? "▬" : "━"}</span><small>{label}<i>{detail}</i></small>
                      </button>
                    ))}
                  </div>
                  <p>Desktop only. At smaller widths, the page uses the largest safe measure.</p>
                </section>
              </div>
            </details>
            <button type="button" onClick={selectNativeRange}>Explore current selection</button>
          </div>

          <article
            ref={storyRef}
            className="story"
            data-reading-id={reading.id}
            aria-labelledby="story-title"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onKeyDown={handleStoryKeyDown}
          >
            <header className="story-header">
              <span className="eyebrow">{reading.subtitle}</span>
              <h1 id="story-title">{reading.title}</h1>
              <p className="story-deck">{reading.deck}</p>
              <p className="story-commitment">{computeReadingMetrics(reading).uninterruptedMinutes} min reading{computeReadingMetrics(reading).explorationMinutes ? ` · up to ${computeReadingMetrics(reading).explorationMinutes} min optional exploration` : ""}{reading.chapters?.length ? ` · ${reading.chapters.length} chapters · exact return preserved` : " · complete in one brief reading"}</p>
              {reading.chapters?.length ? (
                <nav className="chapter-index" aria-label="Chapters in this work">
                  {reading.chapters.map((chapter) => (
                    <button key={chapter.id} type="button" onClick={() => document.getElementById(chapter.id)?.scrollIntoView({ block: "start", behavior: "smooth" })}>
                      <span>{String(chapter.number).padStart(2, "0")}</span>{chapter.title}
                    </button>
                  ))}
                </nav>
              ) : null}
              <div className="ornament" aria-hidden="true"><span>✦</span></div>
            </header>

            <div className="story-body">
              {story.map((segment) => {
                const chapter = chaptersByOpening.get(segment.id);
                return (
                  <Fragment key={segment.id}>
                    {chapter ? (
                      <header className="chapter-opening" id={chapter.id}>
                        <span>Chapter {chapter.number}</span>
                        <h2>{chapter.title}</h2>
                        {chapter.deck ? <p>{chapter.deck}</p> : null}
                      </header>
                    ) : null}
                    <p
                      className={`story-paragraph ${segment.kind}`}
                      data-reading-paragraph
                      data-paragraph-id={segment.id}
                      data-sentence={segment.text}
                    >
                      <StoryText
                        segment={segment}
                        words={words}
                        glance={activeLessonLexicon?.glance}
                      />
                    </p>
                  </Fragment>
                );
              })}
            </div>

            <footer className="lesson-close">
              <span className="eyebrow">{reading.exercise.kicker}</span>
              <h2>{reading.exercise.title}</h2>
              <p>{reading.exercise.prompt}{reading.exercise.targetWords.length ? <> Use {reading.exercise.targetWords.map((word, index) => <span key={word}>{index ? ", " : ""}<em>{word}</em></span>)}.</> : null}</p>
              <div className="lesson-close-actions">
                {isPrivateReading ? (
                  <button type="button" onClick={() => openLibrary("words")}>Return to My Words <span>→</span></button>
                ) : (
                  <button type="button" onClick={() => openPracticeView("expression")}>Begin when ready <span>→</span></button>
                )}
                {!isPrivateReading ? (
                  <button type="button" onClick={() => void toggleReadingList(reading.id)}>
                    {readingList.some((entry) => entry.readingId === reading.id) ? "Remove from Reading list" : "Read later"} <span>◇</span>
                  </button>
                ) : null}
                <button className="next-reading-action" type="button" onClick={openDiscovery}>Choose another reading <span>↗</span></button>
              </div>
            </footer>
          </article>
        </section> : experienceMode === "learning" ? null : practiceView === "overview" ? (
          <section className="practice-overview-stage" id="practice-overview" aria-labelledby="practice-overview-title">
            {practiceMap}
            <header>
              <span className="section-kicker">Practise with a visible purpose</span>
              <h1 id="practice-overview-title">Three ways to make English available when you need it.</h1>
              <p>Choose one job. Verbalore shows what you will do, what it can prove, and what it will preserve before asking for effort.</p>
            </header>
            <div className="practice-job-grid">
              <article>
                <span>01</span><h2>Learn a useful word</h2>
                <p>Understand one maintained word, hear it, distinguish it from nearby words, and see how it changes a sentence.</p>
                <blockquote>Lucid: clear enough to understand without effort.</blockquote>
                <small>Opening a lesson is an encounter, not proof of learning.</small>
                <button type="button" onClick={() => openPracticeView("discover")}>Explore one word →</button>
              </article>
              <article>
                <span>02</span><h2>Practise My Words</h2>
                <p>Return to words you deliberately saved. Recall, distinguish, use, and revisit them without streak pressure.</p>
                <blockquote>A definition proves recognition. A true sentence tests availability.</blockquote>
                <small>Evidence changes only after an authored check or deliberate use.</small>
                <button type="button" onClick={() => openPracticeView("words")}>Open My Words practice →</button>
              </article>
              <article>
                <span>03</span><h2>Expression Studio</h2>
                <p>Hold one intention still. Compare how tact, warmth, restraint, force, or wit changes what the listener receives.</p>
                <blockquote>“Let me finish.” / “I’ll complete the thought, then I want your response.”</blockquote>
                <small>Comparison teaches range. Purposeful production is the evidence of command.</small>
                <button type="button" onClick={() => openPracticeView("expression")}>Compare how it lands →</button>
              </article>
            </div>
          </section>
        ) : practiceView === "discover" ? (
          <div className="practice-destination">
          {practiceMap}
          <WordDiscovery
            entries={commandCatalogEntries}
            activeId={activeCommandCatalogEntry?.id || ""}
            activeEntry={activeCommandWord}
            activeRelatedWord={commandRelatedWordId}
            query={commandWordQuery}
            handle={commandWordHandle}
            level={commandWordLevel}
            register={commandWordRegister}
            articulation={commandWordArticulation}
            loading={commandWordLoading}
            loadError={commandWordLoadError}
            plus={entitlements.plan === "plus"}
            saved={activeCommandWord ? savedKeys.has(activeCommandWord.word) : false}
            onSelect={selectCommandWord}
            onQueryChange={replaceCommandWordQuery}
            onCriteriaChange={replaceCommandDiscoveryCriteria}
            onSave={(entry) => { void saveCommandWord(entry); }}
            onPractice={(entry) => { void saveCommandWord(entry, true); }}
            onHear={hearCommandWord}
            onOpenPronunciationGuide={() => setPronunciationGuideOpen(true)}
            onShowPlus={() => setPlusOfferOpen(true)}
          />
          </div>
        ) : practiceView === "words" ? (
          <section className="word-command-stage" id="word-command-start" aria-labelledby="word-command-title">
            {practiceMap}
            <div className="practice-toolbar">
              <span><i /> Authored word practice</span>
              <span>{dueReviewCount} ready now</span>
              <strong>No streak · no AI judgment</strong>
            </div>

            <article className="word-command-workspace">
              <header className="command-header">
                <span className="eyebrow">Practise My Words</span>
                <h1 id="word-command-title">Recognition is a beginning.<br /><em>Use is the proof.</em></h1>
                <p>A saved word is Encountered. One authored check and an original sentence make it Active. A different check after at least 24 hours, followed by self-reported natural use, may make it Owned.</p>
              </header>

              {!currentUser ? (
                <section className="command-empty">
                  <span aria-hidden="true">◇</span>
                  <h2>Read before you register.</h2>
                  <p>Every lesson remains open. Sign in only when you want Verbalore to preserve a word and the evidence of learning it.</p>
                  <blockquote className="command-empty-demo">Lucid means clear and easy to understand. “Her lucid explanation settled the argument.”</blockquote>
                  <div className="command-empty-actions">
                    <button className="primary-action" type="button" onClick={() => openPracticeView("discover")}>Explore a useful word</button>
                    <button className="secondary-action" type="button" onClick={() => switchExperience("read")}>Find one in a reading</button>
                  </div>
                  <button className="text-action" type="button" onClick={() => requestSignIn()}>Sign in when you want to keep the learning →</button>
                </section>
              ) : !wordReviews.length ? (
                <section className="command-empty">
                  <span aria-hidden="true">Aa</span>
                  <h2>Practise begins with one word you chose to keep.</h2>
                  <p>A definition proves recognition. Writing one true sentence shows whether the word is available when you need it.</p>
                  <blockquote className="command-empty-demo">Lucid means clear and easy to understand. “Her lucid explanation settled the argument.”</blockquote>
                  <div className="command-empty-actions">
                    <button className="primary-action" type="button" onClick={() => openPracticeView("discover")}>Explore a useful word</button>
                    <button className="secondary-action" type="button" onClick={() => switchExperience("read")}>Find one in a reading</button>
                  </div>
                </section>
              ) : !currentReview || !currentReview.authored ? (
                <section className="command-empty">
                  <span aria-hidden="true">◇</span>
                  <h2>Context is preserved; practice is being authored.</h2>
                  <p>Your saved selections remain safe. Curated command practice is available for the enriched words in this lesson.</p>
                  <div className="command-empty-actions">
                    <button className="primary-action" type="button" onClick={() => openPracticeView("discover")}>Explore a useful word</button>
                    <button className="secondary-action" type="button" onClick={() => switchExperience("read")}>Find one in a reading</button>
                  </div>
                </section>
              ) : (
                <>
                  <nav className="word-queue" aria-label="Saved words ready for practice">
                    {actionableReviews.map((review) => (
                      <button
                        key={review.item.word}
                        type="button"
                        className={review.item.word === currentReview.item.word ? "active" : ""}
                        onClick={() => chooseReviewWord(review.item.word)}
                        aria-current={review.item.word === currentReview.item.word ? "true" : undefined}
                      >
                        <strong>{review.item.displayWord}</strong>
                        <span>{review.evidence.status}{review.evidence.due ? " · ready" : ""}</span>
                      </button>
                    ))}
                  </nav>

                  <section className="command-card" aria-labelledby="current-command-word">
                    <div className="command-card-heading">
                      <div>
                        <span className="section-kicker">{reviewStepName(currentReview.evidence.nextStep)}</span>
                        <h2 id="current-command-word">{currentReview.item.displayWord}</h2>
                      </div>
                      <div className={`status-seal status-${currentReview.evidence.status.toLowerCase()}`}>
                        <span>Current state</span>
                        <strong>{currentReview.evidence.status}</strong>
                      </div>
                    </div>

                    <blockquote className="command-context">“{currentReview.item.sourceSentence}”</blockquote>
                    {currentReview.item.sourceAnchor.startsWith("private:") ? (
                      <p className="private-review-boundary">Private-source boundary: this practice uses Verbalore’s maintained sense of the word. It does not verify that the private passage intended the same sense.</p>
                    ) : null}

                    <div className="command-ladder" aria-label={`Learning state: ${currentReview.evidence.status}`}>
                      {["Encountered", "Active", "Owned"].map((status, index) => (
                        <div
                          key={status}
                          className={index < vocabularyStatusRank(currentReview.evidence.status) ? "complete" : index === vocabularyStatusRank(currentReview.evidence.status) ? "current" : "future"}
                        >
                          <span aria-hidden="true">{index < vocabularyStatusRank(currentReview.evidence.status) ? "✓" : index + 1}</span>
                          <div><strong>{status}</strong><small>{status === "Encountered" ? "saved in context" : status === "Active" ? "recalled and used" : "recalled later, used naturally"}</small></div>
                        </div>
                      ))}
                    </div>

                    <div className="evidence-strip">
                      <div><strong>{currentReview.evidence.successfulChecks}</strong><span>accurate checks</span></div>
                      <div><strong>{currentReview.evidence.useAttempts}</strong><span>original uses</span></div>
                      <div><strong>{currentReview.evidence.naturalUses}</strong><span>natural uses</span></div>
                      <div><strong>{currentReview.evidence.state === "complete" ? "Complete" : currentReview.evidence.state === "content_gap" ? "Authored gap" : currentReview.evidence.state === "read_only" ? "Read only" : currentReview.evidence.due ? "Now" : calmReviewDate(currentReview.evidence.nextReviewAt)}</strong><span>next return</span></div>
                    </div>

                    <section className="review-prompt" aria-live="polite">
                      {currentReview.prompt?.type === "check" ? (
                        <>
                          <span className="section-kicker">{currentReview.prompt.assessmentForm === "context_cloze" ? "Restore the sentence" : "Choose the exact shade"}</span>
                          <h3>{currentReview.prompt.stem}</h3>
                          <div className={`choice-list ${currentReview.prompt.assessmentForm === "sense_discrimination" ? "prose-choices" : ""}`}>
                            {currentReview.prompt.options.map((option) => (
                              <button key={option} type="button" className={reviewChoice === option ? "selected" : ""} onClick={() => setReviewChoice(option)} aria-pressed={reviewChoice === option}>{option}</button>
                            ))}
                          </div>
                        </>
                      ) : currentReview.prompt?.type === "use" ? (
                        <>
                          <span className="section-kicker">An original use</span>
                          <h3>{currentReview.prompt.direction}</h3>
                          <label className="writing-field command-writing">
                            <span>Your sentence</span>
                            <textarea value={reviewDraft} onChange={(event) => setReviewDraft(event.target.value)} rows={4} placeholder={`Write with ${currentReview.item.displayWord}…`} />
                          </label>
                          <fieldset className="self-checks">
                            <legend>Check only what is genuinely true</legend>
                            {currentReview.prompt.criteria.map((criterion) => (
                              <label key={criterion.id}>
                                <input
                                  type="checkbox"
                                  checked={reviewSignals.includes(criterion.id)}
                                  onChange={(event) => setReviewSignals((current) => event.target.checked ? [...current, criterion.id] : current.filter((signal) => signal !== criterion.id))}
                                />
                                <span>{criterion.label}</span>
                              </label>
                            ))}
                          </fieldset>
                        </>
                      ) : currentReview.prompt?.type === "natural" ? (
                        <>
                          <span className="section-kicker">Life beyond the exercise</span>
                          <h3>{currentReview.prompt.cue}</h3>
                          <label className="writing-field command-writing">
                            <span>A brief context note</span>
                            <textarea value={reviewDraft} onChange={(event) => setReviewDraft(event.target.value)} rows={4} placeholder="I used it when… / I noticed it in…" />
                          </label>
                          <p className="natural-note">This is learner-reported evidence. Verbalore records it honestly; it does not pretend to have heard the conversation.</p>
                        </>
                      ) : currentReview.evidence.state === "wait" ? (
                        <div className="review-pause">
                          <span className="section-kicker">A real interval matters</span>
                          <h3>The next check opens {calmReviewDate(currentReview.evidence.nextReviewAt)}.</h3>
                          <p>Your first evidence is safe. Verbalore will use a different authored form when the full 24 hours have elapsed.</p>
                        </div>
                      ) : currentReview.evidence.state === "complete" ? (
                        <div className="review-pause">
                          <span className="section-kicker">Evidence receipt</span>
                          <h3>This learning cycle is complete.</h3>
                          <p>{currentReview.evidence.receiptSummary}</p>
                        </div>
                      ) : currentReview.evidence.state === "read_only" ? (
                        <div className="review-pause">
                          <span className="section-kicker">Receipts remain safe</span>
                          <h3>Review is temporarily read-only.</h3>
                          <p>The rollback service can show preserved history but cannot create evidence until the primary API returns.</p>
                        </div>
                      ) : (
                        <div className="review-pause">
                          <span className="section-kicker">No repeated score</span>
                          <h3>The current authored checks have been used.</h3>
                          <p>Your history remains intact. Verbalore will not repeat a revealed prompt and pretend it is new evidence.</p>
                        </div>
                      )}

                      <div className="review-actions">
                        {currentReview.prompt ? (
                          <button className="primary-action" type="button" onClick={submitWordReview} disabled={savingReview}>
                            {savingReview ? "Preserving…" : currentReview.prompt.type === "natural" ? "Record this use" : currentReview.prompt.type === "use" ? "Preserve this use" : "Check and continue"}
                          </button>
                        ) : null}
                        {currentReview.evidence.status !== "Encountered" ? (
                          <button className="secondary-action" type="button" onClick={demoteCurrentWord}>This word feels less secure</button>
                        ) : null}
                      </div>
                      {reviewFeedback ? <p className="review-feedback" role="status">{reviewFeedback}</p> : null}
                    </section>
                  </section>

                  <footer className="command-principle">
                    <strong>Ownership is deliberately difficult.</strong>
                    <p>Two accurate checks with different identities and forms, separated by at least 24 hours, plus one original use and one self-reported natural use are required. You may move a word backward whenever the state feels more confident than you do.</p>
                  </footer>
                </>
              )}
            </article>
          </section>
        ) : (
          <section className="practice-stage" id="practice-start" aria-labelledby="practice-title">
            {practiceMap}
            <div className="practice-toolbar">
              <span><i /> Authored guidance</span>
              <span>{profile.assistanceMode}</span>
              <strong>No AI · pattern check · nothing stored until you save</strong>
            </div>

            <article className="practice-workspace">
              <header className="practice-header">
                <span className="eyebrow">Expression Studio · Study {expressionStudy.number} · {expressionStudy.size} · {expressionStudy.minutes} min</span>
                <h1 id="practice-title">
                  {expressionTitleParts[0]}{expressionTitleParts.length > 1 ? <>,<br /><em>{expressionTitleParts.slice(1).join(", ")}.</em></> : "."}
                </h1>
                <p>{expressionStudy.concept}</p>
                <div className="expression-value-first">
                  <strong>Compare how it lands before you write.</strong>
                  <p>The intention stays fixed. Change only the verbal posture and watch what it protects, risks, and asks of the listener.</p>
                  <button type="button" onClick={() => document.getElementById("verbal-range-title")?.scrollIntoView({ block: "start", behavior: "smooth" })}>Compare the alternatives ↓</button>
                </div>
                <nav className="expression-study-nav" aria-label="Expression studies">
                  <button type="button" onClick={() => changeExpressionStudy(expressionStudyIndex - 1)} disabled={expressionStudyIndex === 0}>← Previous</button>
                  <strong>{expressionStudyIndex + 1} of {ALL_EXPRESSION_STUDIES.length}</strong>
                  <button type="button" onClick={() => changeExpressionStudy(expressionStudyIndex + 1)} disabled={expressionStudyIndex === ALL_EXPRESSION_STUDIES.length - 1}>Next →</button>
                </nav>
              </header>

              <section className="practice-brief" aria-labelledby="situation-title">
                <div className="step-number">01</div>
                <div>
                  <span className="section-kicker" id="situation-title">The situation</span>
                  <p>{expressionStudy.situation}</p>
                  <blockquote>“{expressionStudy.tangledDraft}”</blockquote>
                  <div className="diagnosis-grid">
                    <div><span>Intention</span><strong>{expressionStudy.skill}</strong></div>
                    <div><span>Invariant</span><strong>{expressionStudy.invariant}</strong></div>
                    <div><span>Craft</span><strong>{expressionStudy.craft.join(" · ")}</strong></div>
                  </div>
                </div>
              </section>

              <section className="verbal-range-study" aria-labelledby="verbal-range-title">
                <div className="step-number">02</div>
                <div>
                  <span className="section-kicker">Verbal Range</span>
                  <h2 id="verbal-range-title">Hold the meaning still. Change the landing.</h2>
                  <div className="range-contract">
                    <span>Meaning held constant</span>
                    <p>{expressionStudy.intention}</p>
                    <small>{expressionStudy.invariant}</small>
                  </div>

                  <div className="stance-tabs" aria-label="Choose an articulation stance">
                    {expressionStudy.stances.map((stance) => (
                      <button
                        key={stance.id}
                        type="button"
                        className={stance.id === activeStance.id ? "active" : ""}
                        aria-pressed={stance.id === activeStance.id}
                        onClick={() => setActiveStanceId(stance.id)}
                      >
                        {stance.label}
                      </button>
                    ))}
                  </div>

                  <article className="stance-card" key={activeStance.id} aria-live="polite">
                    <div className="stance-reading">
                      <span>{activeStance.label} · {activeStance.landing}</span>
                      <blockquote>“{activeStance.line}”</blockquote>
                    </div>
                    <div className="range-notes">
                      <div><span>What it protects</span><p>{activeStance.protects}</p></div>
                      <div><span>What it risks</span><p>{activeStance.tradeoff}</p></div>
                    </div>
                  </article>

                  <p className="range-principle">No version is ranked best. The situation decides which resource earns its place.</p>
                </div>
              </section>

              <section className="practice-editor" id="practice-editor" aria-labelledby="first-attempt-title">
                <div className="step-number">03</div>
                <div>
                  <span className="section-kicker" id="first-attempt-title">Try this situation</span>
                  <h2>Now make one purposeful line of your own.</h2>
                  <p className="editor-situation"><strong>Listener and stakes:</strong> {expressionStudy.context}. {expressionStudy.situation}</p>
                  <p className="editor-direction"><strong>Keep this intention:</strong> {expressionStudy.intention} <strong>Chosen landing:</strong> {activeStance.label}, {activeStance.landing.toLocaleLowerCase()}. {expressionStudy.practiceCue}</p>
                  <p className="production-proof">Comparison gives you range. Writing one line you would actually say shows whether that choice is available when you need it. Nothing is called command until you produce and deliberately save it.</p>
                  <label className="writing-field">
                    <span>What would you say?</span>
                    <textarea
                      value={practiceDraft}
                      onChange={(event) => setPracticeDraft(event.target.value)}
                      placeholder="Write the line you would actually say…"
                      rows={5}
                    />
                    <small>{practiceDraft.trim() ? practiceDraft.trim().split(/\s+/).length : 0} words · private until saved</small>
                  </label>
                  <button
                    className="check-expression"
                    type="button"
                    onClick={() => setPracticeChecked(true)}
                    disabled={!practiceDraft.trim() && profile.assistanceMode !== "Show me"}
                  >
                    {profile.assistanceMode === "Show me" ? "Show me how the sentence changes" : "Check its visible structure"}
                    <span>→</span>
                  </button>
                </div>
              </section>

              {practiceChecked ? (
                <div className="practice-reveal">
                  <section className="signal-section" aria-labelledby="signal-title">
                    <div className="step-number">04</div>
                    <div>
                      <span className="section-kicker" id="signal-title">Authored structure check · not AI</span>
                      <h2>{practiceAssessment.status === "assessed" ? "Four visible features of this articulation." : "This response was not assessed."}</h2>
                      <p className="pattern-check-note">{practiceAssessment.message}</p>
                      {practiceAssessment.status === "assessed" ? (
                        <div className="signal-list">
                          {practiceSignals.map((signal) => (
                            <div className={signal.met ? "met" : "open"} key={signal.id}>
                              <span aria-hidden="true">{signal.met ? "✓" : "○"}</span>
                              <div><strong>{signal.label}</strong><p>{signal.guidance}</p></div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="assessment-boundary" role="status">
                          <strong>Not assessed</strong>
                          <p>No ticks or scores are shown when the response is outside this exercise’s narrow pattern.</p>
                        </div>
                      )}
                    </div>
                  </section>

                  <section className="revision-section" aria-labelledby="revision-title">
                    <div className="step-number">05</div>
                    <div>
                      <span className="section-kicker" id="revision-title">Revision</span>
                      <h2>Keep your meaning. Change its posture.</h2>
                      <label className="writing-field revision-field">
                        <span>Your deliberate version</span>
                        <textarea
                          value={practiceRevision}
                          onChange={(event) => setPracticeRevision(event.target.value)}
                          placeholder={`Revise in a ${activeStance.label.toLocaleLowerCase()} stance, or choose another landing above.`}
                          rows={5}
                        />
                        <small>{practiceRevision.trim() ? practiceRevision.trim().split(/\s+/).length : 0} words</small>
                      </label>
                      <div className="practice-actions">
                        <button className="primary-action" type="button" onClick={savePractice} disabled={savingPractice}>
                          {savingPractice ? "Saving…" : "Save this attempt"}
                        </button>
                        <button className="secondary-action" type="button" onClick={() => { setPracticeDraft(""); setPracticeRevision(""); setPracticeChecked(false); }}>
                          Begin again
                        </button>
                      </div>
              <p className="storage-note">Saving preserves both versions in your private learning profile so you can see the change instead of remembering only the polished result.</p>
                    </div>
                  </section>
                </div>
              ) : null}

              <section className="attempt-history" aria-labelledby="history-title">
                <div className="history-heading">
                  <div><span className="section-kicker">Return without losing anything</span><h2 id="history-title">Earlier attempts</h2></div>
                  <strong>{String(practiceAttempts.length).padStart(2, "0")}</strong>
                </div>
                {practiceAttempts.length ? (
                  <ul>
                    {practiceAttempts.map((attempt) => (
                      <li key={attempt.id}>
                        <button type="button" onClick={() => reopenPractice(attempt)}>
                          <span>{EXPRESSION_STUDY_BY_ID.get(attempt.promptId)?.title || "Expression study"} · {attempt.status}</span>
                          <blockquote>“{attempt.revision || attempt.draft}”</blockquote>
                          <small>{new Date(attempt.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · reopen</small>
                        </button>
                        <button className="delete-attempt" type="button" onClick={() => removePractice(attempt.id)} aria-label="Delete this practice attempt">×</button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="empty-attempts">Your saved attempts will appear here. No streak, no pressure. Only evidence that your language is changing.</p>
                )}
              </section>
            </article>
          </section>
        )}

        {experienceMode === "read" && panelMode === "word" ? (
          <button
            className="word-sheet-scrim"
            type="button"
            onClick={closeWord}
            aria-label="Close word explanation and return to the reading"
          />
        ) : null}

        <section ref={inspectorRef} className={`inspector ${panelMode === "word" && deepOpen ? "deep" : ""}`} aria-label={experienceMode === "learning" ? "My learning" : "Contextual help"}>
          <p className="sr-only" role="status" aria-live="polite">
            {panelMode === "word" && selection && insight
              ? `Opened ${selection.word}. ${meaningLabel}: ${insight.meaning}`
              : ""}
          </p>
          {experienceMode === "practice" && panelMode === "empty" ? (
            <div className="practice-guide">
              <span className="inspector-number">02</span>
              {practiceView === "discover" ? (
                <>
                  <span className="section-kicker">The promise</span>
                  <h2>Useful words, not ornament.</h2>
                  <p>Every word should make a thought easier to name, a sentence easier to shape, or another person easier to understand.</p>
                  <ol>
                    <li><span>1</span><div><strong>Understand</strong><p>Begin with plain English and a sound you can follow.</p></div></li>
                    <li><span>2</span><div><strong>Distinguish</strong><p>See why a familiar nearby word is not quite the same.</p></div></li>
                    <li><span>3</span><div><strong>Use</strong><p>Meet the word in more than one kind of sentence.</p></div></li>
                    <li><span>4</span><div><strong>Choose</strong><p>Hold the intention still and notice how the wording changes its landing.</p></div></li>
                  </ol>
                  <blockquote>“The word is learned<br />when it becomes available.”</blockquote>
                  <p className="guide-footnote">Opening or saving is never called mastery. Evidence begins only when you recall and use the word.</p>
                </>
              ) : practiceView === "words" ? (
                <>
                  <span className="section-kicker">The evidence</span>
                  <h2>Command, not collection.</h2>
                  <p>A large library can disguise a small usable vocabulary. Verbalore advances one word only when its evidence changes.</p>
                  <ol>
                    <li><span>1</span><div><strong>Context</strong><p>Keep the sentence in which the word first mattered.</p></div></li>
                    <li><span>2</span><div><strong>Recall</strong><p>Restore the word and distinguish its exact shade.</p></div></li>
                    <li><span>3</span><div><strong>Use</strong><p>Write an original sentence against authored criteria.</p></div></li>
                    <li><span>4</span><div><strong>Return</strong><p>Recall it later, then notice it entering real language.</p></div></li>
                  </ol>
                  <blockquote>“Encountered is honest.<br />Owned must be earned.”</blockquote>
                  <p className="guide-footnote">No streak and no automatic AI judgment. The record shows what happened; it never pretends to know more than it observed.</p>
                </>
              ) : (
                <>
                  <span className="section-kicker">The method</span>
                  <h2>Conceptual distillation</h2>
                  <p>First name the thought beneath the tangle. Then rebuild the sentence around that name.</p>
                  <ol>
                    <li><span>1</span><div><strong>Intention</strong><p>What outcome do you actually want?</p></div></li>
                    <li><span>2</span><div><strong>Tension</strong><p>Which two values must the sentence hold together?</p></div></li>
                    <li><span>3</span><div><strong>Concept</strong><p>Give the central distinction a short, memorable name.</p></div></li>
                    <li><span>4</span><div><strong>Compression</strong><p>Remove every phrase that does not carry meaning, tone, or rhythm.</p></div></li>
                  </ol>
                  <blockquote>“A boundary,<br />not a favour.”</blockquote>
                  <p className="guide-footnote">This is authored instruction. An eventual AI coach may apply the method to personal situations, but the method must remain useful without it.</p>
                </>
              )}
            </div>
          ) : null}

          {experienceMode === "read" && panelMode === "empty" ? (
            <div className="empty-inspector">
              <span className="inspector-number">01</span>
              <div className="empty-glyph" aria-hidden="true">Aa</div>
              <h2>A word may be a doorway.</h2>
              <p>Tap any word for a maintained local meaning. Underlined words add Verbalore’s contextual teaching layer.</p>
              <ol>
                <li><span>1</span> Glance for enough meaning to continue.</li>
                <li><span>2</span> Explore when curiosity takes hold.</li>
                <li><span>3</span> Return to the exact sentence.</li>
              </ol>
            </div>
          ) : null}

          {experienceMode === "read" && panelMode === "word" && selection && insight ? (
            <div className="word-panel" key={selection.tokenId} data-selection-id={selection.tokenId} data-motion={wordMotion}>
              <div className="panel-header">
                <span>{deepOpen ? (dictionaryOnly ? "Dictionary view" : "Deep view") : "Glance"}</span>
                <button type="button" onClick={closeWord} aria-label="Close word explanation">×</button>
              </div>

              <blockquote className="context-anchor">
                <span>From the sentence</span>
                “{contextSentence}”
              </blockquote>

              {isPrivateReading ? (
                <div className="private-sense-boundary" role="note">
                  <strong>{dictionaryEntry || privateBundledInsight ? "Sense not verified for this private sentence" : "No local entry for this selection"}</strong>
                  <p>{dictionaryEntry
                    ? "This word was resolved against Verbalore’s on-device lesson lexicon. Your passage was not analysed or sent anywhere, and this may not be the sense intended here."
                    : privateBundledInsight
                      ? "This lexical material comes from a maintained Verbalore reading. Your passage was not analysed, and this sense may not be the one intended here."
                      : "Verbalore made no external lookup and sent no passage text to an AI, dictionary, translation, or pronunciation provider."}</p>
                </div>
              ) : null}

              <div className="selection-controls" aria-label="Adjust selected word">
                <button type="button" onClick={() => chooseAdjacent(-1)} disabled={!adjacentTokenIds.previous} aria-label="Select previous word">←</button>
                <span>Adjust the selection</span>
                <button type="button" onClick={() => chooseAdjacent(1)} disabled={!adjacentTokenIds.next} aria-label="Select next word">→</button>
              </div>

              <div className="word-heading">
                <div>
                  <h2>{selection.word}</h2>
                  <p>{insight.partOfSpeech} · {insight.phonetic}</p>
                  <button className="pronunciation-guide-link" type="button" onClick={() => setPronunciationGuideOpen(true)}>
                    How to read this pronunciation
                  </button>
                </div>
                {!isPrivateReading ? (
                  <button className="speak-button" type="button" onClick={hearWord} aria-label={`Hear ${selection.word} pronounced`}>
                    <span aria-hidden="true">▶</span> Hear
                  </button>
                ) : null}
              </div>

              <section className={`meaning-block ${activeMeaning?.authored ? "authored-meaning" : "lexical-meaning"}`} aria-labelledby="meaning-title">
                <div
                  className="meaning-copy"
                  onPointerDown={handleMeaningSwipeStart}
                  onPointerUp={handleMeaningSwipeEnd}
                  onPointerCancel={() => {
                    meaningSwipeStart.current = null;
                  }}
                >
                  {meaningViews.map((meaning, index) => (
                    <div
                      className={`meaning-copy-view ${index === activeMeaningIndex ? "is-active" : ""}`}
                      aria-hidden={index !== activeMeaningIndex}
                      key={`${meaning.label}-${meaning.text}`}
                    >
                      <h3 id={index === activeMeaningIndex ? "meaning-title" : undefined}>{meaning.label}</h3>
                      <p>{meaning.text}</p>
                    </div>
                  ))}
                </div>
                {meaningViews.length > 1 ? (
                  <nav className="meaning-navigation" aria-label={`Meanings for ${selection.word}`}>
                    <button
                      className="meaning-direction"
                      type="button"
                      onClick={() => setMeaningView((current) => moveMeaningIndex(current, meaningViews.length, -1))}
                      aria-disabled={activeMeaningIndex === 0}
                      aria-label="Previous meaning"
                    >
                      <span aria-hidden="true">←</span>
                      Previous meaning
                    </button>
                    <span className="meaning-position" aria-live="polite">
                      {activeMeaningIndex + 1} of {meaningViews.length}
                    </span>
                    <button
                      className="meaning-direction"
                      type="button"
                      onClick={() => setMeaningView((current) => moveMeaningIndex(current, meaningViews.length, 1))}
                      aria-disabled={activeMeaningIndex === meaningViews.length - 1}
                      aria-label="Next meaning"
                    >
                      Next meaning
                      <span aria-hidden="true">→</span>
                    </button>
                  </nav>
                ) : null}
              </section>

              {quickFacts.length ? (
                <div className={`quick-pair ${quickFacts.length === 1 ? "single" : ""}`}>
                  {quickFacts.map((fact) => (
                    <div key={`${fact.label}-${fact.value}`}>
                      <span>{fact.label}</span>
                      <strong>{fact.value}</strong>
                      {fact.note ? <small>{fact.note}</small> : null}
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="panel-actions">
                <button className="secondary-action" type="button" onClick={saveWord}>
                  {isSaved ? "Add this context" : "Save word"}
                </button>
                {!noLexicalEntry ? <button className="primary-action" type="button" onClick={() => setDeepOpen((open) => !open)}>
                  {deepOpen ? "Return to glance" : dictionaryOnly ? "Explore dictionary" : "Explore the word"}
                </button> : null}
              </div>

              {deepOpen ? (
                dictionaryOnly && dictionaryEntry ? (
                  <div className="deep-content dictionary-depth">
                    <section>
                      <h3 className="section-kicker">Available senses</h3>
                      <ol className="dictionary-senses">
                        {dictionaryEntry.senses.map((sense, index) => (
                          <li key={sense.id}>
                            <span>{index + 1} · {sense.partOfSpeech}</span>
                            <p>{completeLexicalSentence(sense.definition)}</p>
                            {sense.examples[0] ? <blockquote>“{displayLexicalText(sense.examples[0])}”</blockquote> : null}
                          </li>
                        ))}
                      </ol>
                    </section>
                    <section>
                      <h3 className="section-kicker">What this entry can tell you</h3>
                      <p>{dictionaryEntry.contextStatus === "reading-curated"
                        ? "Verbalore reviewed these central meanings for clarity and selected the relevant one for this reading. A sentence-specific note appears only when the central meaning would mislead."
                        : "This maintained entry defines the word without pretending to know which sense this sentence intends. Words taught in context by Verbalore carry that additional layer."}</p>
                    </section>
                    <section className="word-family">
                      <h3 className="section-kicker">Word family</h3>
                      <p>{insight.family}</p>
                    </section>
                    <section className="lexicon-source">
                      <h3 className="section-kicker">Source</h3>
                      <p>{dictionaryEntry.source}{dictionaryEntry.source.includes("Open English WordNet") ? " · CC BY 4.0" : ""}</p>
                    </section>
                  </div>
                ) : (
                <div className="deep-content">
                  <section className="authored-note">
                    <h3 className="section-kicker">Nuance and shade</h3>
                    <p>{insight.shade}</p>
                  </section>
                  {dictionaryEntry?.senseStatus === "dictionary" ? (
                    <section className="lexical-record">
                      <h3 className="section-kicker">Verbalore learner dictionary · central meanings</h3>
                      <ol className="dictionary-senses">
                        {dictionaryEntry.senses.map((sense, index) => (
                          <li key={sense.id}>
                            <span>{index + 1} · {sense.partOfSpeech}</span>
                            <p>{completeLexicalSentence(sense.definition)}</p>
                            {sense.examples[0] ? <blockquote>“{displayLexicalText(sense.examples[0])}”</blockquote> : null}
                          </li>
                        ))}
                      </ol>
                      <p className="lexicon-source">{dictionaryEntry.source}{dictionaryEntry.source.includes("Open English WordNet") ? " · CC BY 4.0" : ""}</p>
                    </section>
                  ) : null}
                  {isPrivateReading ? (
                    <section>
                      <h3 className="section-kicker">What remains private</h3>
                      <p>These details remain unavailable for private passages until you explicitly choose a trusted service.</p>
                    </section>
                  ) : (
                    <>
                      <section>
                        <h3 className="section-kicker">Why this word here?</h3>
                        <p>{insight.whyHere}</p>
                      </section>
                      <section>
                        <h3 className="section-kicker">Voice and intonation</h3>
                        <p>{insight.intonation}</p>
                        {pronunciationEvidence ? (
                          <p className="pronunciation-evidence">{pronunciationEvidence}</p>
                        ) : null}
                        <button className="text-action" type="button" onClick={hearWord}>Hear it once more →</button>
                        <details className="voice-settings">
                          <summary>Accent and device voice</summary>
                          <label>
                            <span>Pronunciation voice</span>
                            <select value={speechVoiceUri} onChange={(event) => chooseSpeechVoice(event.target.value)}>
                              <option value="default">Use device default · British English fallback</option>
                              {speechVoices.map((voice) => (
                                <option key={voice.voiceURI} value={voice.voiceURI}>{pronunciationLabel(voice)}</option>
                              ))}
                            </select>
                          </label>
                          <p>These voices are supplied by this device or browser. Availability can differ on another device.</p>
                        </details>
                      </section>
                    </>
                  )}
                  <section className="word-family">
                    <h3 className="section-kicker">Word family</h3>
                    <p>{insight.family}</p>
                    {insight.usageNote ? <small>{insight.usageNote}</small> : null}
                  </section>
                  <section className="example-card">
                    <h3 className="section-kicker">See it at work</h3>
                    <blockquote>“{insight.example}”</blockquote>
                  </section>
                </div>
                )
              ) : null}

              <button className="continue-button" type="button" onClick={closeWord}>
                Return to the marked sentence <span>↓</span>
              </button>
            </div>
          ) : null}

          {experienceMode === "learning" && panelMode === "library" ? (
            <div className="library-panel">
              <header className="learning-destination-heading">
                <button className="back-to-learning-origin" type="button" onClick={returnFromLearning}>← {learningOriginRef.current?.view === "home" ? "Back to Home" : learningOriginRef.current?.view === "practice" ? "Back to Practice" : `Back to ${reading.title}`}</button>
                <span className="section-kicker">Everything you deliberately kept</span>
                <h1 id="learning-destination-title" tabIndex={-1}>{learningSection === "overview" ? "My learning" : learningSection === "readings" ? "My readings" : "My Words"}</h1>
                <p>{learningSection === "overview" ? "Continue first. Inspect the record only when it helps." : learningSection === "readings" ? "Current, planned, completed, and private reading remain distinct." : "Each word remains attached to the contexts and evidence that made it matter."}</p>
              </header>
              {learningNavigation}
              {learningSection === "overview" ? <div className="library-intro">
                <span className="section-kicker">Return with the context intact</span>
                <h2>Where do I continue?</h2>
                <p>Your current reading comes first. Words, expression work, and evidence remain available without turning this into a dashboard.</p>
                <button className="library-continue" type="button" onClick={returnFromLearning}>
                  <span><small>Continue reading</small><strong>{reading.title}</strong></span>
                  <span>{progress}% <i aria-hidden="true">→</i></span>
                </button>
              </div> : null}

              {learningSection === "overview" ? (
                <nav className="learning-destinations" aria-label="My learning destinations">
                  <button type="button" onClick={() => openLearningSection("words")}>
                    <span aria-hidden="true">Aa</span><strong>My Words</strong><small>{savedWordTotal} contextual {savedWordTotal === 1 ? "thread" : "threads"}</small>
                  </button>
                  <button type="button" onClick={() => { setLibraryView("reading-list"); openLearningSection("readings"); }}>
                    <span aria-hidden="true">◇</span><strong>Reading list</strong><small>{readingList.length ? `${readingList.length} waiting for you` : "Nothing waiting"}</small>
                  </button>
                  <button type="button" onClick={() => { setLibraryView("private"); openLearningSection("readings"); }}>
                    <span aria-hidden="true">＋</span><strong>Private readings</strong><small>{privateReadings.length ? `${privateReadings.length} preserved` : "Bring your own passage"}</small>
                  </button>
                </nav>
              ) : null}

              {learningSection === "overview" && learningEvidence?.recent?.length ? (
                <section className="learning-recent-receipts" aria-labelledby="recent-learning-title">
                  <div className="library-section-heading"><span className="section-kicker" id="recent-learning-title">Recent evidence</span><button type="button" onClick={() => openLearningSection("progress")}>Inspect history →</button></div>
                  <LearningEvidenceHistory events={learningEvidence.recent.slice(0, 2)} />
                </section>
              ) : null}

              {learningSection === "overview" && warmWords.length ? (
                <section className="warm-words" aria-labelledby="warm-words-title">
                  <div className="library-section-heading"><span className="section-kicker" id="warm-words-title">Warm right now</span><small>At most seven, however large My Words becomes</small></div>
                  <div>{warmWords.map((item) => <button key={item.word} type="button" onClick={() => void openSavedWord(item)}><strong>{item.displayWord}</strong><span>{item.status}</span></button>)}</div>
                </section>
              ) : null}

              {learningSection === "readings" ? (
                <nav className="reading-record-tabs" aria-label="Choose a reading record">
                  <button type="button" className={libraryView === "reading-list" ? "active" : ""} onClick={() => setLibraryView("reading-list")}>Planned</button>
                  <button type="button" className={libraryView === "private" ? "active" : ""} onClick={() => setLibraryView("private")}>Private</button>
                </nav>
              ) : null}

              {learningSection === "readings" && libraryView === "private" ? (
                <section className="library-section private-reading-list" aria-labelledby="private-readings-title">
                  <div className="library-section-heading">
                    <span className="section-kicker" id="private-readings-title">Private readings</span>
                    <small>Stored only because you chose to save them</small>
                  </div>
                  <button className="bring-passage-action" type="button" onClick={openImportDialog}>
                    <span aria-hidden="true">＋</span><span><strong>Bring a passage</strong><small>Paste plain text · private to this account</small></span>
                  </button>
                  {privateReadings.length ? <div className="private-reading-cards">
                    {privateReadings.map((item) => (
                      <article key={item.id} className={currentReadingId === `private:${item.id}` ? "current" : ""}>
                        <button className="private-reading-open" type="button" onClick={() => void openPrivateReading(item.id)}>
                          <span>{currentReadingId === `private:${item.id}` ? "Open now" : `${item.wordCount} words`}</span>
                          <strong>{item.title}</strong>
                          <p>{item.preview}</p>
                          <small>{new Date(item.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · continue →</small>
                        </button>
                        {confirmDeleteImport === item.id ? (
                          <div className="private-delete-confirm" role="group" aria-label={`Delete ${item.title} permanently`}>
                            <p>Delete the source and its private saved context?</p>
                            <button type="button" onClick={() => void deletePrivateReading(item.id)}>Delete permanently</button>
                            <button type="button" onClick={() => setConfirmDeleteImport(null)}>Cancel</button>
                          </div>
                        ) : (
                          <button className="private-reading-delete" type="button" onClick={() => setConfirmDeleteImport(item.id)} aria-label={`Delete private reading ${item.title}`}>Delete…</button>
                        )}
                      </article>
                    ))}
                  </div> : <div className="empty-library"><span>＋</span><p>No private passage has been preserved.</p></div>}
                </section>
              ) : null}

              {learningSection === "readings" && libraryView === "reading-list" ? (
                <section className="library-section reading-list-view" aria-labelledby="reading-list-title">
                  <div className="library-section-heading">
                    <span className="section-kicker" id="reading-list-title">Planned reading</span>
                    <small>Read later is a plan, not a learning claim</small>
                  </div>
                  {readingList.length ? (
                    <div className="reading-list-cards">
                      {readingList.map((entry) => {
                        const planned = READING_BY_ID.get(entry.readingId);
                        if (!planned) return null;
                        return (
                          <article key={entry.readingId}>
                            <span>{computeReadingMetrics(planned).uninterruptedMinutes} min reading · {planned.taxonomy.form.replace("-", " ")}</span>
                            <h3>{planned.title}</h3>
                            <p>{planned.deck}</p>
                            <div>
                              <button type="button" onClick={() => void openReading(planned.id)}>Read this <span aria-hidden="true">→</span></button>
                              <button type="button" onClick={() => void toggleReadingList(planned.id)}>Remove</button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="empty-library"><span>◇</span><p>Use Read later in the chooser. This list stays separate from My Words.</p><button type="button" onClick={openDiscovery}>Choose a reading</button></div>
                  )}
                </section>
              ) : null}

              {learningSection === "words" ? <section className="library-section my-words-view" aria-labelledby="saved-words-title" aria-busy={wordPageLoading}>
                <div className="library-section-heading">
                  <div><span className="section-kicker" id="saved-words-title">My Words</span><p>Your learning has a history. Every saved word remains connected to the sentences in which you met it.</p></div>
                  <small>{wordAppliedSearch
                    ? `${wordResultTotal} matching ${wordResultTotal === 1 ? "thread" : "threads"}`
                    : savedWordTotal
                      ? `${savedWordTotal} learning ${savedWordTotal === 1 ? "thread" : "threads"}`
                      : "No thread begun yet"}</small>
                </div>
                <div className="my-words-controls">
                  <label><span>Find a word or meaning</span><input type="search" value={wordSearch} onChange={(event) => {
                    vocabularyPageRequest.current += 1;
                    setWordSearch(event.target.value);
                    setWordPageLoading(true);
                  }} placeholder="Search My Words" /></label>
                  <div role="group" aria-label="Group My Words">
                    <button type="button" className={wordGrouping === "state" ? "active" : ""} onClick={() => setWordGrouping("state")}>By state</button>
                    <button type="button" className={wordGrouping === "story" ? "active" : ""} onClick={() => setWordGrouping("story")}>By story</button>
                  </div>
                </div>
                {wordPageLoading && savedWords.length ? <p className="word-page-status" role="status">Updating this view…</p> : null}
                {wordPageLoading && !savedWords.length ? <p className="word-page-status" role="status">Opening My Words…</p> : null}
                {savedWords.length ? (
                  <div className="word-groups">
                    {groupedVisibleWords.map((group) => (
                      <section className="word-group" key={group.label} aria-label={`${group.label} words`}>
                        <header><strong>{group.label}</strong><span>{group.items.length}</span></header>
                        <div className="learning-threads">
                    {group.items.map((item) => {
                      const isPrivateContext = item.sourceAnchor.startsWith("private:");
                      const sourceParts = item.sourceAnchor.split(":");
                      const fallbackContext: VocabularyContext = {
                        word: item.word,
                        displayWord: item.displayWord,
                        meaning: item.meaning,
                        sourceSentence: item.sourceSentence,
                        sourceAnchor: item.sourceAnchor,
                        sourceType: isPrivateContext ? "private_import" : "authored",
                        sourceId: isPrivateContext ? sourceParts[1] || "" : sourceParts[0] || "",
                        createdAt: item.createdAt,
                        updatedAt: item.updatedAt,
                      };
                      const contexts = contextsByWord.get(item.word) || [fallbackContext];
                      const review = reviewsByWord.get(item.word);
                      return (
                        <article
                          className="learning-thread"
                          key={item.word}
                          id={`word-thread-${encodeURIComponent(item.word)}`}
                          data-word-anchor={item.anchor || `word:${item.word}`}
                        >
                          <button className="learning-thread-return" type="button" onClick={() => void openSavedWord(item)}>
                            <span className="learning-thread-title">
                              <strong>{item.displayWord}</strong>
                              <small>{item.status}</small>
                            </span>
                            <p>{item.meaning}</p>
                            <span className="learning-thread-source">
                              {item.contextCount || contexts.length} {(item.contextCount || contexts.length) === 1 ? "encounter" : "encounters"} · latest sentence preserved <i aria-hidden="true">→</i>
                            </span>
                          </button>
                          <details className="learning-thread-detail" onToggle={(event) => {
                            if (event.currentTarget.open) void loadWordThread(item.word);
                          }}>
                            <summary>Open the learning thread</summary>
                            <div className="evidence-receipt">
                              <span>Evidence receipt</span>
                              <strong>{item.displayWord} · {item.status}</strong>
                              <p>{evidenceReceipt(review)}</p>
                              {review ? <small>{reviewAvailabilityText(review)}</small> : null}
                            </div>
                            <ol className="encounter-timeline">
                              {contexts.map((context, index) => (
                                <li key={context.id || `${context.sourceAnchor}-${index}`}>
                                  <span aria-hidden="true" />
                                  <div>
                                    <small>{calmLearningDate(context.createdAt)} · {contextSourceName(context, privateReadings)}{context.sourceType === "private_import" ? " · sense unverified" : ""}</small>
                                    <blockquote>“{context.sourceSentence}”</blockquote>
                                    <button type="button" onClick={() => void openSavedWord({
                                      ...item,
                                      meaning: context.meaning,
                                      sourceSentence: context.sourceSentence,
                                      sourceAnchor: context.sourceAnchor,
                                    })}>Return to this sentence →</button>
                                  </div>
                                </li>
                              ))}
                            </ol>
                            {(item.contextCount || contexts.length) > contexts.length ? <p className="word-page-status" role="status">Opening the complete encounter history…</p> : null}
                            <button className="remove-thread" type="button" onClick={() => removeWord(item.word)}>Remove this word and its evidence…</button>
                          </details>
                        </article>
                      );
                    })}
                        </div>
                      </section>
                    ))}
                    {!groupedVisibleWords.length ? <div className="empty-library"><span>Aa</span><p>No word matches this search.</p></div> : null}
                    {wordNextCursor ? (
                      <button
                        className="show-more-words"
                        type="button"
                        disabled={wordPageLoading}
                        onClick={() => void loadVocabularyPage({ append: true, cursor: wordNextCursor, query: wordAppliedSearch })}
                      >
                        {wordPageLoading ? "Opening the next page…" : `Show the next ${Math.min(120, Math.max(0, wordResultTotal - savedWords.length))} words`}
                      </button>
                    ) : null}
                  </div>
                ) : !wordPageLoading ? wordAppliedSearch ? (
                  <div className="empty-library"><span>Aa</span><p>No word or meaning matches this search.</p></div>
                ) : (
                  <div className="empty-library"><span>◇</span><p>Your first saved word will remain attached to the sentence in which you met it.</p></div>
                ) : null}
              </section> : null}
            </div>
          ) : null}

          {experienceMode === "learning" && panelMode === "profile" && currentUser ? (
            <div className="profile-panel">
              <header className="learning-destination-heading">
                <button className="back-to-learning-origin" type="button" onClick={returnFromLearning}>← {learningOriginRef.current?.view === "home" ? "Back to Home" : learningOriginRef.current?.view === "practice" ? "Back to Practice" : `Back to ${reading.title}`}</button>
                <span className="section-kicker">My learning</span>
                <h1 id="learning-destination-title" tabIndex={-1}>{learningSection === "expression" ? "My expression" : learningSection === "progress" ? "Progress and history" : learningSection === "profile" ? "Profile and preferences" : learningSection === "membership" ? "Membership and billing" : "Data and privacy"}</h1>
                <p>{learningSection === "expression" ? "See the wording you deliberately saved, reopen it, and compare what changed." : learningSection === "progress" ? "Only authored checks, learner-recorded uses, corrections, and explicit states appear here." : learningSection === "profile" ? "Shape how Verbalore assists you without changing what the evidence can claim." : learningSection === "membership" ? "See your current access first. Billing appears only because you intentionally opened it." : "Export what belongs to you or remove it with an explicit, narrow decision."}</p>
              </header>
              {learningNavigation}
              <div className="profile-intro" hidden={learningSection !== "profile"}>
                <span className="avatar large">{profile.displayName.slice(0, 1).toUpperCase()}</span>
                <div>
                  <h2>{profile.displayName}</h2>
                  <p>{currentUser.email} · private learning profile</p>
                  {currentUser.id.startsWith("google:") ? (
                    <button className="profile-sign-out-action" type="button" onClick={signOut}>Sign out of this account</button>
                  ) : (
                    <p className="legacy-access-note">This temporary founder profile will be claimed only by the configured Google account during the backend migration.</p>
                  )}
                </div>
              </div>
              <section className="learning-expression-record" hidden={learningSection !== "expression"} aria-labelledby="learning-expression-title">
                <header><span className="section-kicker">Purposeful production</span><h2 id="learning-expression-title">Saved attempts and revisions</h2><p>A comparison alone is an encounter. These are the lines you chose to produce and preserve.</p></header>
                {practiceAttempts.length ? (
                  <ul>
                    {practiceAttempts.map((attempt) => (
                      <li key={attempt.id}>
                        <button type="button" onClick={() => { reopenPractice(attempt); openPracticeView("expression"); }}>
                          <span>{EXPRESSION_STUDY_BY_ID.get(attempt.promptId)?.title || "Expression study"}</span>
                          <blockquote>“{attempt.revision || attempt.draft}”</blockquote>
                          <small>{new Date(attempt.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · reopen in Expression Studio →</small>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="learning-record-empty"><p>No expression attempt has been deliberately saved.</p><button type="button" onClick={() => openPracticeView("expression")}>Compare how an intention lands →</button></div>
                )}
              </section>
              <section className="membership-summary" hidden={learningSection !== "membership"} aria-label="Membership">
                <span className="section-kicker">Membership</span>
                <div>
                  <strong>{entitlements.label}</strong>
                  <small>{membershipStateCopy(entitlements).heading}</small>
                </div>
                <p>{membershipStateCopy(entitlements).detail}</p>
                <p className="membership-value-note">{entitlements.plan === "plus"
                  ? `Plus is active. You are keeping ${privateReadings.length} of up to 50 private passages, and the maintained Plus vocabulary catalogue is available to this account.`
                  : "Free keeps the complete reading loop, your learning record, 50 command-vocabulary lessons, and one complete private passage. Plus expands the maintained catalogue and private shelf to fifty."}</p>
                {entitlements.plan === "plus" && entitlements.commerce.manageUrl ? (
                  <a className="membership-action" href={entitlements.commerce.manageUrl}>Manage membership on Gumroad</a>
                ) : entitlements.commerce.purchasesEnabled && entitlements.commerce.checkoutUrl ? (
                  <a className="membership-action" href={entitlements.commerce.checkoutUrl}>Explore Verbalore Plus</a>
                ) : (
                  <small className="membership-calm-state">Nothing in your current learning has been restricted while Plus is being prepared.</small>
                )}
                {entitlements.commerce.recoveryEnabled ? <details className="membership-recovery">
                  <summary>Already purchased or returning from Gumroad?</summary>
                  <p>Use the license key from your Verbalore receipt. Verification happens directly with Gumroad; the key is not stored.</p>
                  <label>
                    <span>License key</span>
                    <input type="password" value={membershipLicenseKey} onChange={(event) => setMembershipLicenseKey(event.target.value)} autoComplete="off" />
                  </label>
                  <button
                    className="membership-refresh-action"
                    type="button"
                    disabled={membershipRefreshing || !membershipLicenseKey.trim()}
                    onClick={() => void refreshMembership()}
                  >
                    {membershipRefreshing ? "Checking with Gumroad…" : "Refresh membership"}
                  </button>
                  {membershipMessage ? <p className="membership-message" role="status">{membershipMessage}</p> : null}
                </details> : null}
              </section>
              <section className="learning-evidence-summary" hidden={learningSection !== "progress"} aria-labelledby="learning-evidence-title" aria-busy={learningEvidenceLoading}>
                <header>
                  <div><span className="section-kicker">Evidence, not activity</span><h3 id="learning-evidence-title">Your learning curve</h3></div>
                  <small>{learningEvidence ? `${learningEvidence.window.weeks} weeks` : "Only when requested"}</small>
                </header>
                <p className="learning-evidence-explainer">This is not a score. It plots authored checks, learner-recorded use, and honest correction. Saving a word does not move the curve.</p>
                {learningEvidence ? (
                  <div className="learned-vocabulary-summary" aria-label={`${learningEvidence.states.active + learningEvidence.states.owned} words learned with evidence`}>
                    <span className="section-kicker">Vocabulary you have learned</span>
                    <strong>{learningEvidence.states.active + learningEvidence.states.owned}</strong>
                    <p>Active and Owned words only. Encountered words remain in your learning history without being counted as learned.</p>
                  </div>
                ) : null}
                {learningEvidenceLoading ? <p className="learning-evidence-state" role="status">Opening your evidence…</p> : null}
                {learningEvidenceError ? <div className="learning-evidence-state error" role="status"><p>{learningEvidenceError}</p><button type="button" onClick={() => void loadLearningEvidence()}>Try again</button></div> : null}
                {learningEvidence && !learningEvidenceLoading && !learningEvidenceError ? evidenceEventsInWindow ? (
                  <>
                    <div className="learning-curve" role="img" aria-label={`${evidenceChecks} authored checks, ${evidenceUses} learner-recorded uses, and ${evidenceCorrections} correction events across ${learningEvidence.window.weeks} weeks`}>
                      {learningEvidence.weeks.map((week) => {
                        const checks = week.firstChecks + week.delayedChecks;
                        const uses = week.originalUses + week.naturalUses;
                        const corrections = week.notYet + week.demotions;
                        return (
                          <div className="learning-curve-week" key={week.weekStart} aria-label={`Week of ${calmWeekDate(week.weekStart)}: ${checks} checks, ${uses} learner-recorded uses, ${corrections} corrections`}>
                            <div>
                              <i className="checks" style={{ height: `${Math.round((checks / evidenceWeekMax) * 100)}%` }} />
                              <i className="uses" style={{ height: `${Math.round((uses / evidenceWeekMax) * 100)}%` }} />
                              <i className="corrections" style={{ height: `${Math.round((corrections / evidenceWeekMax) * 100)}%` }} />
                            </div>
                            <span>{calmWeekDate(week.weekStart)}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="learning-curve-legend" aria-hidden="true"><span><i className="checks" />Checks</span><span><i className="uses" />Use</span><span><i className="corrections" />Correction</span></div>
                    <div className="learning-evidence-totals">
                      <div><strong>{evidenceChecks}</strong><span>authored checks</span></div>
                      <div><strong>{evidenceUses}</strong><span>uses recorded</span></div>
                      <div><strong>{evidenceCorrections}</strong><span>corrections</span></div>
                    </div>
                    <div className="evidence-state-summary" aria-label="Current word evidence states">
                      <span><strong>{learningEvidence.states.encountered}</strong> Encountered</span>
                      <span><strong>{learningEvidence.states.active}</strong> Active</span>
                      <span><strong>{learningEvidence.states.owned}</strong> Owned</span>
                    </div>
                    <LearningEvidenceHistory events={learningEvidence.recent} />
                  </>
                ) : (
                  <>
                    <div className="learning-evidence-empty"><span aria-hidden="true">○</span><p>No curve in this eight-week window.</p><small>A new check or recorded use must leave evidence. Quiet weeks do not erase your current states.</small></div>
                    <div className="evidence-state-summary" aria-label="Current word evidence states">
                      <span><strong>{learningEvidence.states.encountered}</strong> Encountered</span>
                      <span><strong>{learningEvidence.states.active}</strong> Active</span>
                      <span><strong>{learningEvidence.states.owned}</strong> Owned</span>
                    </div>
                    <LearningEvidenceHistory events={learningEvidence.recent} />
                  </>
                ) : null}
              </section>
              <div className="profile-preferences" hidden={learningSection !== "profile"}>
              <label>
                <span>Display name</span>
                <input value={profile.displayName} onChange={(event) => setProfile({ ...profile, displayName: event.target.value })} />
              </label>
              <label>
                <span>Explain difficult words in</span>
                <select value={profile.preferredLanguage} onChange={(event) => setProfile({ ...profile, preferredLanguage: event.target.value as Language })}>
                  <option>English</option><option>Hindi</option><option>Punjabi</option>
                </select>
              </label>
              <label>
                <span>Default depth</span>
                <select value={profile.explorationDepth} onChange={(event) => setProfile({ ...profile, explorationDepth: event.target.value })}>
                  <option>Glance first</option><option>Open deeply</option>
                </select>
              </label>
              <label>
                <span>Learning assistance</span>
                <select value={profile.assistanceMode} onChange={(event) => setProfile({ ...profile, assistanceMode: event.target.value })}>
                  <option>Show me</option><option>Build with me</option><option>Test me</option>
                </select>
              </label>
              <div className="profile-stats"><div><strong>{savedWordTotal}</strong><span>word threads</span></div><div><strong>{progress}%</strong><span>current reading</span></div><div><strong>{practiceAttempts.length}</strong><span>writing attempts</span></div></div>
              <button className="primary-action full" type="button" onClick={saveProfile}>Save profile</button>
              </div>
              <details className="profile-data-controls" hidden={learningSection !== "data"} open={learningSection === "data"}>
                <summary><span>Data and privacy</span><small>Export or delete</small></summary>
                <div>
                  <p>Take a complete JSON copy of your profile, words, contexts, progress, reading list, private passages, practice, evidence, and non-secret membership state. Sign-in and payment credentials are never included.</p>
                  <button className="account-export-action" type="button" disabled={accountAction !== "idle"} onClick={() => void exportLearningData()}>
                    {accountAction === "exporting" ? "Preparing your export…" : "Export my learning"}
                  </button>
                  {!accountDeleteOpen ? (
                    <button className="account-delete-reveal" type="button" disabled={accountAction !== "idle"} onClick={() => { setAccountDeleteOpen(true); setAccountActionError(""); }}>
                      Delete my Verbalore data
                    </button>
                  ) : (
                    <div className="account-delete-confirm" role="group" aria-labelledby="account-delete-title">
                      <strong id="account-delete-title">Delete everything Verbalore remembers?</strong>
                      <p>This permanently removes your words, contexts, progress, reading list, private passages, writing attempts, evidence, profile, purchase-to-profile link, and every Verbalore session. Provider transaction audit is retained without your account link; your Gumroad and Google accounts are untouched. This cannot be undone.</p>
                      <div>
                        <button type="button" disabled={accountAction === "deleting"} onClick={() => { setAccountDeleteOpen(false); setAccountActionError(""); }}>Keep my learning</button>
                        <button className="confirm-account-delete" type="button" disabled={accountAction === "deleting"} onClick={() => void deleteAccountData()}>
                          {accountAction === "deleting" ? "Deleting…" : "Delete everything"}
                        </button>
                      </div>
                    </div>
                  )}
                  {accountActionError ? <p className="account-action-error" role="status">{accountActionError}</p> : null}
                </div>
              </details>
              <p className="privacy-note" hidden={learningSection !== "data"}>Reading progress, saved words, private passages, and attempts are isolated to this Google account. Writing and pasted text are stored only after an explicit save, and private passages can be hard-deleted with their derived private context.</p>
            </div>
          ) : null}
        </section>
      </div>

      {experienceMode === "read" && showReturn && selection ? (
        <button className="return-chip" type="button" onClick={returnToSelection}>
          <span>↩</span> Return to “{selection.word}”
        </button>
      ) : null}

      {experienceMode === "read" && panelMode === "empty" && resumeCue ? (
        <div className="reading-resume-note" role="status">
          <span aria-hidden="true">●</span>
          Continue after “{resumeCue.word}”. Your place is highlighted.
        </div>
      ) : null}

      {discoveryOpen ? (
        <div className="discovery-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeDiscovery();
        }}>
          <section ref={discoveryDialogRef} className="discovery-dialog" role="dialog" aria-modal="true" aria-labelledby="discovery-title" tabIndex={-1}>
            <div className="discovery-mobile-bar">
              <span aria-hidden="true" />
              <button type="button" onClick={closeDiscovery} aria-label="Close reading chooser">×</button>
            </div>
            <header className="discovery-header">
              <div>
                <span className="section-kicker">Reading chooser</span>
                <h2 id="discovery-title">Choose your next reading</h2>
                <p>Tell Verbalore what fits this moment. The closest maintained readings stay visible even when no work matches every detail.</p>
              </div>
              <button type="button" onClick={closeDiscovery} aria-label="Close reading chooser">×</button>
            </header>

            <div className="discovery-layout">
              <div className="discovery-controls">
                <section className="discovery-question" aria-labelledby="discovery-time-title">
                  <div className="discovery-question-heading">
                    <span>01</span>
                    <div>
                      <h3 id="discovery-time-title">How much room do you have?</h3>
                      <p>A reading promise, never a deadline.</p>
                    </div>
                  </div>
                  <div className="choice-chip-grid descriptive" role="radiogroup" aria-labelledby="discovery-time-title">
                    {READING_COMMITMENTS.map((commitment) => {
                      const selected = readingCommitmentChoice === commitment.id;
                      return (
                        <button
                          key={commitment.id}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          tabIndex={selected ? 0 : -1}
                          data-choice-value={commitment.id}
                          className={`choice-chip ${selected ? "active" : ""}`}
                          onKeyDown={(event) => handleChoiceArrow(event, (value) => setReadingCommitmentChoice(value as ReadingCommitment))}
                          onClick={() => setReadingCommitmentChoice(commitment.id)}
                        >
                          <span className="choice-dot" aria-hidden="true" />
                          <span><strong>{commitment.label}</strong><small>{commitment.description}</small></span>
                        </button>
                      );
                    })}
                  </div>
                </section>

                <section className="discovery-question" aria-labelledby="discovery-appetite-title">
                  <div className="discovery-question-heading">
                    <span>02</span>
                    <div>
                      <h3 id="discovery-appetite-title">What are you in the mood for?</h3>
                      <p>Choose the kind of pull you want from the page.</p>
                    </div>
                  </div>
                  <div className="choice-chip-grid compact" role="radiogroup" aria-labelledby="discovery-appetite-title">
                    {READING_APPETITES.map((appetite) => {
                      const selected = readingAppetite === appetite.id;
                      return (
                        <button
                          key={appetite.id}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          tabIndex={selected ? 0 : -1}
                          data-choice-value={appetite.id}
                          className={`choice-chip ${selected ? "active" : ""}`}
                          onKeyDown={(event) => handleChoiceArrow(event, (value) => setReadingAppetite(value as ReadingAppetite))}
                          onClick={() => setReadingAppetite(appetite.id)}
                        >
                          <span className="choice-dot" aria-hidden="true" />
                          <strong>{appetite.label}</strong>
                        </button>
                      );
                    })}
                  </div>
                </section>

                <button
                  className="particular-toggle"
                  type="button"
                  aria-expanded={discoveryParticular}
                  aria-controls="particular-reading-ask"
                  onClick={() => setDiscoveryParticular((current) => !current)}
                >
                  <span>
                    <strong>{discoveryParticular ? "Return to the simple choice" : "Be more particular"}</strong>
                    <small>{discoveryParticular ? "Your detailed choices will be kept." : "Choose landing, language purpose, difficulty, and form."}</small>
                  </span>
                  <span aria-hidden="true">{discoveryParticular ? "−" : "+"}</span>
                </button>

                {discoveryParticular ? (
                  <div className="particular-reading-ask" id="particular-reading-ask">
                    <section className="discovery-question" aria-labelledby="discovery-landing-title">
                      <div className="discovery-question-heading compact-heading">
                        <span>03</span>
                        <div><h3 id="discovery-landing-title">How should the language land?</h3></div>
                      </div>
                      <div className="choice-chip-grid compact" role="radiogroup" aria-labelledby="discovery-landing-title">
                        {[
                          { id: "any", label: "Any landing" },
                          ...READING_ARTICULATIONS,
                        ].map((articulation) => {
                          const selected = articulation.id === "any"
                            ? readingArticulation === null
                            : readingArticulation === articulation.id;
                          return (
                            <button
                              key={articulation.id}
                              type="button"
                              role="radio"
                              aria-checked={selected}
                              tabIndex={selected ? 0 : -1}
                              data-choice-value={articulation.id}
                              className={`choice-chip ${selected ? "active" : ""}`}
                              onKeyDown={(event) => handleChoiceArrow(event, (value) => setReadingArticulation(value === "any" ? null : value as ReadingArticulation))}
                              onClick={() => setReadingArticulation(articulation.id === "any" ? null : articulation.id as ReadingArticulation)}
                            >
                              <span className="choice-dot" aria-hidden="true" />
                              <strong>{articulation.label}</strong>
                            </button>
                          );
                        })}
                      </div>
                    </section>

                    <section className="discovery-question" aria-labelledby="discovery-intent-title">
                      <div className="discovery-question-heading compact-heading">
                        <span>04</span>
                        <div><h3 id="discovery-intent-title">What should the language help you do?</h3></div>
                      </div>
                      <div className="choice-chip-grid descriptive two-column" role="radiogroup" aria-labelledby="discovery-intent-title">
                        <button
                          type="button"
                          role="radio"
                          aria-checked={readingIntent === null}
                          tabIndex={readingIntent === null ? 0 : -1}
                          data-choice-value="any"
                          className={`choice-chip ${readingIntent === null ? "active" : ""}`}
                          onKeyDown={(event) => handleChoiceArrow(event, (value) => setReadingIntent(value === "any" ? null : value as ReadingIntent))}
                          onClick={() => setReadingIntent(null)}
                        >
                          <span className="choice-dot" aria-hidden="true" />
                          <span><strong>Any purpose</strong><small>Let the reading lead.</small></span>
                        </button>
                        {READING_INTENTS.map((intent) => {
                          const selected = readingIntent === intent.id;
                          return (
                            <button
                              key={intent.id}
                              type="button"
                              role="radio"
                              aria-checked={selected}
                              tabIndex={selected ? 0 : -1}
                              data-choice-value={intent.id}
                              className={`choice-chip ${selected ? "active" : ""}`}
                              onKeyDown={(event) => handleChoiceArrow(event, (value) => setReadingIntent(value === "any" ? null : value as ReadingIntent))}
                              onClick={() => setReadingIntent(intent.id)}
                            >
                              <span className="choice-dot" aria-hidden="true" />
                              <span><strong>{intent.label}</strong><small>{intent.description}</small></span>
                            </button>
                          );
                        })}
                      </div>
                    </section>

                    <section className="discovery-question" aria-labelledby="discovery-difficulty-title">
                      <div className="discovery-question-heading compact-heading">
                        <span>05</span>
                        <div><h3 id="discovery-difficulty-title">How difficult should the vocabulary feel?</h3></div>
                      </div>
                      <div className="choice-chip-grid compact" role="radiogroup" aria-labelledby="discovery-difficulty-title">
                        {[
                          { id: "any", label: "Any level" },
                          ...READING_DIFFICULTIES,
                        ].map((difficulty) => {
                          const selected = difficulty.id === "any"
                            ? readingDifficulty === null
                            : readingDifficulty === difficulty.id;
                          return (
                            <button
                              key={difficulty.id}
                              type="button"
                              role="radio"
                              aria-checked={selected}
                              tabIndex={selected ? 0 : -1}
                              data-choice-value={difficulty.id}
                              className={`choice-chip ${selected ? "active" : ""}`}
                              onKeyDown={(event) => handleChoiceArrow(event, (value) => setReadingDifficulty(value === "any" ? null : value as ReadingLexicalDifficulty))}
                              onClick={() => setReadingDifficulty(difficulty.id === "any" ? null : difficulty.id as ReadingLexicalDifficulty)}
                            >
                              <span className="choice-dot" aria-hidden="true" />
                              <strong>{difficulty.label}</strong>
                            </button>
                          );
                        })}
                      </div>
                    </section>

                    <section className="discovery-question" aria-labelledby="discovery-form-title">
                      <div className="discovery-question-heading compact-heading">
                        <span>06</span>
                        <div><h3 id="discovery-form-title">Which form fits today?</h3></div>
                      </div>
                      <div className="choice-chip-grid compact" role="radiogroup" aria-labelledby="discovery-form-title">
                        {[
                          { id: "any", label: "Any form" },
                          ...AVAILABLE_READING_FORMS.map((form) => ({ id: form, label: READING_FORM_LABELS[form] })),
                        ].map((form) => {
                          const selected = form.id === "any"
                            ? readingForm === null
                            : readingForm === form.id;
                          return (
                            <button
                              key={form.id}
                              type="button"
                              role="radio"
                              aria-checked={selected}
                              tabIndex={selected ? 0 : -1}
                              data-choice-value={form.id}
                              className={`choice-chip ${selected ? "active" : ""}`}
                              onKeyDown={(event) => handleChoiceArrow(event, (value) => setReadingForm(value === "any" ? null : value as ReadingForm))}
                              onClick={() => setReadingForm(form.id === "any" ? null : form.id as ReadingForm)}
                            >
                              <span className="choice-dot" aria-hidden="true" />
                              <strong>{form.label}</strong>
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  </div>
                ) : null}
              </div>

              <aside className="discovery-outcomes">
                <div className="discovery-ask" aria-live="polite">
                  <span className="section-kicker">Your ask</span>
                  <p>{discoveryAsk}</p>
                </div>

                <div className="reading-results" aria-live="polite">
                  <span className="section-kicker">Closest maintained readings</span>
                  {readingRecommendations.map(({
                    reading: candidate,
                    reason,
                    exactMatch,
                    tradeoffs,
                    landingEvidence,
                  }) => (
                    <article key={candidate.id}>
                      <div>
                        <span className="result-fit">
                          {exactMatch ? "Strong maintained fit" : `Closest fit · ${tradeoffs.length} ${tradeoffs.length === 1 ? "trade-off" : "trade-offs"}`}
                        </span>
                        <span className="result-evidence">
                          {computeReadingMetrics(candidate).uninterruptedMinutes} min · {READING_FORM_LABELS[candidate.taxonomy.form]}
                          {landingEvidence.length ? ` · ${landingEvidence.join(" / ")}` : ""}
                        </span>
                        <h3>{candidate.title}</h3>
                        <p>{reason}</p>
                      </div>
                      <div className="reading-result-actions">
                        <button type="button" onClick={() => void openReading(candidate.id)}>Read this <span aria-hidden="true">→</span></button>
                        <button type="button" onClick={() => void toggleReadingList(candidate.id)}>
                          {readingList.some((entry) => entry.readingId === candidate.id) ? "In Reading list" : "Read later"}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>

                <details className="maintained-shelf">
                  <summary>Browse the maintained shelf</summary>
                  <div>
                    {READINGS.map((candidate) => (
                      <button key={candidate.id} type="button" className={candidate.id === reading.id ? "current" : ""} onClick={() => void openReading(candidate.id)}>
                        <span>{candidate.id === reading.id ? "Current reading" : READING_FORM_LABELS[candidate.taxonomy.form]}</span>
                        <strong>{candidate.title}</strong>
                        <small>
                          {computeReadingMetrics(candidate).uninterruptedMinutes} min · {
                            READING_DIFFICULTIES.find(({ id }) => id === candidate.taxonomy.lexicalChallenge)?.label
                            || "Maintained level"
                          }
                        </small>
                      </button>
                    ))}
                  </div>
                </details>
              </aside>
            </div>
          </section>
        </div>
      ) : null}

      {importOpen ? (
        <div className="import-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !savingImport) setImportOpen(false);
        }}>
          <section ref={importDialogRef} className="import-dialog" role="dialog" aria-modal="true" aria-labelledby="import-title" tabIndex={-1}>
            <header>
              <div>
                <span className="section-kicker">Private pasted reading</span>
                <h2 id="import-title">Bring language that matters to you.</h2>
                <p>Paste plain text. Verbalore will preserve it for reading and exact return; it will not analyse the passage automatically.</p>
              </div>
              <button type="button" onClick={() => setImportOpen(false)} disabled={savingImport} aria-label="Close private passage form">×</button>
            </header>

            <form onSubmit={(event) => { event.preventDefault(); void savePrivateReading(); }}>
              <label className="import-field">
                <span>Title</span>
                <input value={importTitle} onChange={(event) => setImportTitle(event.target.value)} maxLength={120} placeholder="A title only you need to recognise" />
              </label>
              <label className="import-field passage-field">
                <span>Passage</span>
                <textarea value={importSource} onChange={(event) => setImportSource(event.target.value)} maxLength={40000} rows={13} placeholder="Paste the passage here…" />
                <small>{importSource.trim().length.toLocaleString("en")} / 40,000 characters · minimum 80</small>
              </label>

              <div className="import-privacy-boundary">
                <strong>What saving does and does not do</strong>
                <p>The text is stored in your private Verbalore profile. It is not published or sent automatically to an AI model, dictionary, translation service, or pronunciation provider.</p>
                <p>Paste only material you have the right to use. You can later delete the source and its derived private context together.</p>
              </div>

              <label className="import-consent">
                <input type="checkbox" checked={importAccepted} onChange={(event) => setImportAccepted(event.target.checked)} />
                <span>I understand this private-storage boundary and choose to save this passage.</span>
              </label>

              {importError ? <p className="import-error" role="alert">{importError}</p> : null}
              {importNeedsPlus ? (
                <aside className="contextual-plus-boundary" aria-label="Verbalore Plus private shelf">
                  <span className="section-kicker">For this private shelf</span>
                  <strong>Keep another passage with Plus.</strong>
                  <p>Free includes one complete private passage. Plus raises this shelf to fifty. Your existing passage remains readable, exportable, and yours.</p>
                  {entitlements.commerce.purchasesEnabled && entitlements.commerce.checkoutUrl ? (
                    <a className="membership-action import-membership-action" href={entitlements.commerce.checkoutUrl}>Proceed to buy</a>
                  ) : (
                    <button className="membership-action import-membership-action" type="button" onClick={() => { setImportOpen(false); setPlusOfferOpen(true); }}>See Plus availability</button>
                  )}
                </aside>
              ) : null}
              <div className="import-actions">
                <button className="primary-action" type="submit" disabled={savingImport || !importAccepted || importSource.trim().length < 80}>
                  {savingImport ? "Saving privately…" : "Save and begin reading"}
                </button>
                <button className="secondary-action" type="button" onClick={() => setImportOpen(false)} disabled={savingImport}>Keep it off Verbalore</button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {accountMenu}

      {authDialog}

      {plusOfferDialog}

      <PronunciationGuide
        open={pronunciationGuideOpen}
        onClose={() => setPronunciationGuideOpen(false)}
        dialogRef={pronunciationGuideDialogRef}
        word={experienceMode === "practice" && practiceView === "discover"
          ? activeCommandWord?.word || "this word"
          : selection?.word || "this word"}
        pronunciation={experienceMode === "practice" && practiceView === "discover"
          ? activeCommandWord?.phonetic || ""
          : insight?.phonetic || ""}
      />

      {toast ? <div className="toast" role="status">{toast}</div> : null}
    </main>
  );
}
